package org.multiplatform.kotfolio.pages.compose

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText

import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/animation")
@Composable
fun AnimationComposePage() {
    BlogLayout(
        PageProperties(
            "Jetpack Compose Animation",
            "Animations are essential in a modern mobile app in order to realize a smooth and understandable user experience."
        ),
        BlogProperties("Jetpack Compose", "Animation")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Animating Positions (Layout Phase)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    // BAD: Recomposes the Box and its parents on every single frame
                    @Composable
                    fun SlidingCard(targetOffset: Dp) {
                        val translationX by animateDpAsState(targetValue = targetOffset)

                        Box(
                            modifier = Modifier
                                .size(100.dp)
                                .offset(x = translationX, y = 0.dp) // State read occurs in composition
                                .background(Color.Blue)
                        )
                    }
                    
                    // GOOD: Zero recompositions. Updates coordinates directly in the Layout phase
                    @Composable
                    fun SlidingCard(targetOffset: Dp) {
                        val translationX by animateDpAsState(targetValue = targetOffset)

                        Box(
                            modifier = Modifier
                                .size(100.dp)
                                .offset { IntOffset(x = translationX.roundToPx(), y = 0) } // Lambda defers state read
                                .background(Color.Blue)
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Animating Alpha and Rotations (Draw Phase)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    // BAD: Triggers composition and layout passes on every frame change
                    @Composable
                    fun FadingCard(targetAlpha: Float) {
                        val alphaState by animateFloatAsState(targetValue = targetAlpha)

                        Box(
                            modifier = Modifier
                                .size(100.dp)
                                .alpha(alphaState) // State read occurs in composition
                                .background(Color.Blue)
                        )
                    }
                    
                    // GOOD: Zero recompositions. Modifies drawing properties directly on the GPU
                    @Composable
                    fun FadingCard(targetAlpha: Float) {
                        val alphaState by animateFloatAsState(targetValue = targetAlpha)

                        Box(
                            modifier = Modifier
                                .size(100.dp)
                                .graphicsLayer { alpha = alphaState } // Direct draw-phase read
                                .background(Color.Blue)
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Types of Animations in Compose",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Animatable - Holds and animates a value over time. Color transitions, loading effects",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "animate*AsState - Simplifies property animations (float, color, size). Expanding cards, scaling buttons.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "updateTransition - Animates multiple properties together. Complex state-based animations. Coordinate multiple animations between different states simultaneously.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "AnimatedVisibility - Handles enter/exit animations for composables. When visible is false and the exit animation completes, the content is removed from the composition tree. Fading in/out elements.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "AnimatedContent - AnimatedContent is a container that automatically animates its content when targetState changes. Its content for different target states is defined in a mapping between a target state and a composable function.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How Do Animations Work Internally in Jetpack Compose?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose animations are built using State-Driven Animation. Instead of manually updating values frame by frame, Compose provides observable state-driven animation APIs that automatically update UI properties over time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Here’s how it works:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "State Change Detection → Compose listens for changes in state variables.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Animation Interpolation → The animation engine smoothly transitions between values.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Recomposition & Frame Updates → The UI automatically updates based on the interpolated values.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            //https://github.com/Shahidzbi4213/WallpaperApp-Cmp.git
            KotlinCode(
                code = """
                    //Pagination Animation
                    
                    @Immutable
                    data class PageState(
                        val items: List<Wallpaper> = emptyList(),
                        val page: Int = 1,
                        val totalPages: Int = 1,
                        val isLoading: Boolean = false,
                        val error: StringResource? = null
                    ) {
                        val isEmpty: Boolean get() = !isLoading && error == null && items.isEmpty()
                    }
                    
                    AnimatedContent(
                        targetState = state.page,
                        transitionSpec = {
                            if (targetState > initialState) {
                                (slideInHorizontally(tween(240)) { width -> width / 4 } + fadeIn(tween(200)))
                                    .togetherWith(slideOutHorizontally(tween(200)) { width -> -width / 4 } + fadeOut(tween(160)))
                            } else {
                                (slideInHorizontally(tween(240)) { width -> -width / 4 } + fadeIn(tween(200)))
                                    .togetherWith(slideOutHorizontally(tween(200)) { width -> width / 4 } + fadeOut(tween(160)))
                            }.using(SizeTransform(clip = false))
                        },
                        label = "pageTransition",
                        modifier = Modifier.fillMaxSize()
                    ) { _ ->
                        ScrollableGrid(state = gridState) {
                        }
                     }
                    """.trimIndent()
            )

            //https://github.com/HachimiWorld/hachimi-world-client.git
            KotlinCode(
                code = """
                    enum class InitializeStatus {
                        INIT, LOADED, FAILED
                    }
                    
                    @Composable
                    private fun LoadableContent(
                        loading: Boolean,
                        initializeStatus: InitializeStatus,
                        onRefresh: () -> Unit,
                        onRetryClick: () -> Unit,
                        modifier: Modifier = Modifier,
                        onLoad: () -> Unit,
                        onDispose: () -> Unit = {},
                        content: @Composable () -> Unit,
                    ) {
                        val slideOffset = with(LocalDensity.current) {
                            32.dp.roundToPx()
                        }
                        AnimatedContent(
                            initializeStatus,
                            modifier = modifier.onVisibilityChanged { visible ->
                                if (visible) onLoad()
                            },
                            transitionSpec = {
                                if (targetState == InitializeStatus.LOADED) {
                                    (fadeIn(animationSpec = tween(220, delayMillis = 90)) +
                                            slideInVertically(
                                                initialOffsetY = { slideOffset },
                                                animationSpec = tween(220, delayMillis = 90)
                                            ))
                                        .togetherWith(fadeOut(animationSpec = tween(90)))
                                } else {
                                    (fadeIn(animationSpec = tween(220, delayMillis = 90)) +
                                            scaleIn(initialScale = 0.92f, animationSpec = tween(220, delayMillis = 90)))
                                        .togetherWith(fadeOut(animationSpec = tween(90)))
                                }
                            }) {
                            when (it) {
                                InitializeStatus.INIT -> LoadingPage()
                                InitializeStatus.FAILED -> ReloadPage(onRetryClick)
                                InitializeStatus.LOADED -> content()
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/HachimiWorld/hachimi-world-client.git
            KotlinCode(
                code = """
                    AnimatedContent(
                        targetState = ,
                        transitionSpec = { materialFadeThrough() }
                    ) { loading ->
                       
                    }
                    
                    @file:Suppress("unused")
                    import androidx.compose.animation.*
                    import androidx.compose.animation.core.FastOutLinearInEasing
                    import androidx.compose.animation.core.LinearOutSlowInEasing
                    import androidx.compose.animation.core.tween

                    private const val ProgressThreshold = 0.35f

                    private val Int.ForOutgoing: Int
                        get() = (this * ProgressThreshold).toInt()

                    private val Int.ForIncoming: Int
                        get() = this - this.ForOutgoing

                    /**
                     * [materialFadeThrough] allows to switch a layout with a fade through animation.
                     *
                     * @param durationMillis the duration of transition.
                     */
                    public fun materialFadeThrough(
                        durationMillis: Int = MotionConstants.DefaultMotionDuration,
                    ): ContentTransform = materialFadeThroughIn(
                        durationMillis = durationMillis,
                    ) togetherWith materialFadeThroughOut(
                        durationMillis = durationMillis,
                    )

                    /**
                     * [materialFadeThroughIn] allows to switch a layout with fade through enter transition.
                     *
                     * @param initialScale the starting scale of the enter transition.
                     * @param durationMillis the duration of the enter transition.
                     */
                    public fun materialFadeThroughIn(
                        initialScale: Float = 0.92f,
                        durationMillis: Int = MotionConstants.DefaultMotionDuration,
                    ): EnterTransition = fadeIn(
                        animationSpec = tween(
                            durationMillis = durationMillis.ForIncoming,
                            delayMillis = durationMillis.ForOutgoing,
                            easing = LinearOutSlowInEasing,
                        ),
                    ) + scaleIn(
                        animationSpec = tween(
                            durationMillis = durationMillis.ForIncoming,
                            delayMillis = durationMillis.ForOutgoing,
                            easing = LinearOutSlowInEasing,
                        ),
                        initialScale = initialScale,
                    )

                    /**
                     * [materialFadeThroughOut] allows to switch a layout with fade through exit transition.
                     *
                     * @param durationMillis the duration of the exit transition.
                     */
                    public fun materialFadeThroughOut(
                        durationMillis: Int = MotionConstants.DefaultMotionDuration,
                    ): ExitTransition = fadeOut(
                        animationSpec = tween(
                            durationMillis = durationMillis.ForOutgoing,
                            delayMillis = 0,
                            easing = FastOutLinearInEasing,
                        ),
                    )
                    
                    public object MotionConstants {
                        public const val DefaultMotionDuration: Int = 300
                        public const val DefaultFadeInDuration: Int = 150
                        public const val DefaultFadeOutDuration: Int = 75
                        public val DefaultSlideDistance: Dp = 30.dp
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "MutableTransitionState, SeekableTransitionState - 1.6",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/thejohnsondev/PassGuard-Multiplatform
            KotlinCode(
                code = """
                    @Composable
                    fun VaultOption(
                        icon: ImageVector,
                        title: String,
                        titleComment: String? = null,
                        description: String,
                        isSelected: Boolean,
                        onClick: (Boolean) -> Unit,
                        content: @Composable () -> Unit = {},
                    ) {
                        val itemTransitionState = remember {
                            MutableTransitionState(isSelected).apply {
                                targetState = !isSelected
                            }
                        }
                        val itemTransition = rememberTransition(itemTransitionState, label = "")
                        val cardBgColor by itemTransition.animateColor({
                            tween(durationMillis = EXPAND_ANIM_DURATION)
                        }, label = "") {
                            if (isSelected) MaterialTheme.colorScheme.secondaryContainer else MaterialTheme.colorScheme.surfaceContainerLow
                        }
                        Column(
                            modifier = Modifier
                                .padding(horizontal = Size16, vertical = Size8)
                                .clip(RoundedCornerShape(Size16))
                                .background(cardBgColor)
                                .clickable {
                                    onClick(!isSelected)
                                },
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.Center
                        ) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(Size16),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Icon(
                                    modifier = Modifier.size(Size32),
                                    imageVector = icon,
                                    contentDescription = null,
                                    tint = MaterialTheme.colorScheme.onSurface
                                )
                                Spacer(modifier = Modifier.width(Size16))
                                VaultTypeDescriptions(title = title, titleComment = titleComment, description = description)
                            }
                            ExpandableContent(isSelected) {
                                content()
                            }
                        }
                    }
                    
                    @Composable
                    fun ExpandableContent(
                        visible: Boolean = true,
                        content: @Composable ColumnScope.() -> Unit
                    ) {
                        val enterTransition = remember {
                            expandVertically(
                                expandFrom = Alignment.Top,
                                animationSpec = tween(EXPAND_ANIM_DURATION)
                            ) + fadeIn(
                                initialAlpha = 0.3f,
                                animationSpec = tween(EXPAND_ANIM_DURATION)
                            )
                        }
                        val exitTransition = remember {
                            shrinkVertically(
                                shrinkTowards = Alignment.Top,
                                animationSpec = tween(EXPAND_ANIM_DURATION)
                            ) + fadeOut(
                                animationSpec = tween(EXPAND_ANIM_DURATION)
                            )
                        }
                        AnimatedVisibility(
                            visible = visible,
                            enter = enterTransition,
                            exit = exitTransition
                        ) {
                            Column {
                                content(this)
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        LazyColumn(
                            state = lazyListState,
                        ) {
                             items(listItem, key = { item -> item.id }) { item ->
                                val visibleState = remember {
                                    MutableTransitionState(false).apply {
                                        targetState = true
                                    }
                                }

                                AnimatedVisibility(
                                    visibleState = visibleState,
                                    enter = fadeIn(animationSpec = tween(durationMillis = 300)) +
                                            slideInVertically(
                                                animationSpec = tween(durationMillis = 300),
                                                initialOffsetY = { it / 2 }
                                            )
                                ) {

                                }
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @OptIn(ExperimentalAnimationApi::class)
                    @Composable
                    fun HeroesList(
                        heroes: List<Hero>,
                        modifier: Modifier = Modifier,
                        contentPadding: PaddingValues = PaddingValues(0.dp)
                    ) {
                        val visibleState = remember {
                            MutableTransitionState(false).apply { targetState = true }
                        }

                        AnimatedVisibility(
                            visibleState = visibleState,
                            enter = fadeIn(animationSpec = spring(dampingRatio = DampingRatioLowBouncy)),
                            exit = fadeOut(),
                            modifier = modifier
                        ) {
                            LazyColumn(contentPadding = contentPadding) {
                                itemsIndexed(heroes) { index, item ->
                                    HeroesItem(
                                        hero = item,
                                        modifier = Modifier
                                            .padding(vertical = 8.dp, horizontal = 16.dp)
                                            .animateEnterExit(
                                                enter = slideInVertically(
                                                    animationSpec = spring(
                                                        stiffness = StiffnessVeryLow,
                                                        dampingRatio = DampingRatioLowBouncy
                                                    ),
                                                    initialOffsetY = { it * (index + 1) }
                                                )
                                            )
                                    )
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    enum class RotationState {
                        BEFORE,
                        AFTER
                    }

                    @Composable
                    fun RotatingText(
                        text: String,
                        textColor: Int
                    ) {
                        val textRotationState = remember { MutableTransitionState(RotationState.BEFORE) }
                        LaunchedEffect(Unit) {
                            textRotationState.targetState = RotationState.AFTER
                        }

                        val rotationTransition = updateTransition(
                            transitionState = textRotationState,
                            label = "rotate_text"
                        )
                        val rotation = rotationTransition.animateFloat(
                            label = "rotate_text",
                            transitionSpec = {
                                tween(3000)
                            },
                            targetValueByState = { state ->
                                when (state) {
                                    RotationState.BEFORE -> 360F
                                    RotationState.AFTER -> 0F
                                }
                            }
                        )

                        Text(
                            text = text,
                            color = Color(textColor),
                            modifier = Modifier
                                .graphicsLayer(
                                    rotationX = rotation.value,
                                    rotationY = rotation.value
                                )
                                .clickable {
                                    with(textRotationState) {
                                        targetState =
                                            if (currentState == RotationState.AFTER) RotationState.BEFORE
                                            else RotationState.AFTER
                                    }
                                }
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Expanded Animation
                    
                    VaultOption(
                        icon = Icons.Filled.Cloud,
                        title = stringResource(ResString.cloud_vault),
                        titleComment = stringResource(ResString.cloud_vault_in_development),
                        description = stringResource(ResString.cloud_vault_description),
                        onClick = { isSelected ->
                            
                        },
                        isSelected = state.selectedVaultType == VaultType.CLOUD
                    ) {
                        //Expand Compose
                    }
                
                    @Composable
                    fun VaultOption(
                        icon: ImageVector,
                        title: String,
                        titleComment: String? = null,
                        description: String,
                        isSelected: Boolean,
                        onClick: (Boolean) -> Unit,
                        content: @Composable () -> Unit = {},
                    ) {
                        val itemTransitionState = remember {
                            MutableTransitionState(isSelected).apply {
                                targetState = !isSelected
                            }
                        }
                        val itemTransition = rememberTransition(itemTransitionState, label = "")
                        val cardBgColor by itemTransition.animateColor({
                            tween(durationMillis = EXPAND_ANIM_DURATION)
                        }, label = "") {
                            if (isSelected) MaterialTheme.colorScheme.secondaryContainer else MaterialTheme.colorScheme.surfaceContainerLow
                        }
                        Column(
                            modifier = Modifier
                                .padding(horizontal = Size16, vertical = Size8)
                                .clip(RoundedCornerShape(Size16))
                                .background(cardBgColor)
                                .clickable {
                                    onClick(!isSelected)
                                },
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.Center
                        ) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(Size16),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Icon(
                                    modifier = Modifier.size(Size32),
                                    imageVector = icon,
                                    contentDescription = null,
                                    tint = MaterialTheme.colorScheme.onSurface
                                )
                                Spacer(modifier = Modifier.width(Size16))
                                VaultTypeDescriptions(title = title, titleComment = titleComment, description = description)
                            }
                            ExpandableContent(isSelected) {
                                content()
                            }
                        }
                    }
                    
                    @Composable
                    private fun VaultTypeDescriptions(
                        title: String,
                        titleComment: String?,
                        description: String
                    ) {
                        Column {
                            Row {
                                Text(
                                    text = title,
                                    fontWeight = FontWeight.Bold,
                                    style = MaterialTheme.typography.bodyLarge,
                                    color = MaterialTheme.colorScheme.onSurface
                                )
                                titleComment?.let {
                                    Text(
                                        modifier = Modifier.padding(start = Size4),
                                        text = titleComment,
                                        style = MaterialTheme.typography.bodyMedium,
                                        color = MaterialTheme.colorScheme.onPrimaryContainer
                                    )
                                }
                            }
                            Text(
                                modifier = Modifier.padding(top = Size8),
                                text = description,
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurface.darken(darkenBy = Percent70)
                            )
                        }
                    }
                    
                    
                    @Composable
                    fun ExpandableContent(
                        visible: Boolean = true,
                        content: @Composable ColumnScope.() -> Unit
                    ) {
                        val enterTransition = remember {
                            expandVertically(
                                expandFrom = Alignment.Top,
                                animationSpec = tween(EXPAND_ANIM_DURATION)
                            ) + fadeIn(
                                initialAlpha = 0.3f,
                                animationSpec = tween(EXPAND_ANIM_DURATION)
                            )
                        }
                        val exitTransition = remember {
                            shrinkVertically(
                                shrinkTowards = Alignment.Top,
                                animationSpec = tween(EXPAND_ANIM_DURATION)
                            ) + fadeOut(
                                animationSpec = tween(EXPAND_ANIM_DURATION)
                            )
                        }
                        AnimatedVisibility(
                            visible = visible,
                            enter = enterTransition,
                            exit = exitTransition
                        ) {
                            Column {
                                content(this)
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Animatable()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Animatable provides fine-grained control over animations. It allows you to animate properties manually over time. Animatable holds a mutable value that can be animated over time. ses coroutines to smoothly update the value between two states. Can be paused, resumed, or reversed dynamically.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/90fermat/budget-master.git
            KotlinCode(
                code = """
                    /**
                     * Counts a monetary value up to [targetValue].
                     *
                     * Animates from the previous value on later changes, not from zero, so a balance update reads
                     * as a change rather than a re-count. Under reduced motion the value snaps immediately.
                     */
                    @Composable
                    fun animateCounter(targetValue: Double): State<Double> {
                        val reducedMotion = isReducedMotionEnabled()
                        val animatable = remember { Animatable(if (reducedMotion) targetValue.toFloat() else 0f) }

                        LaunchedEffect(targetValue, reducedMotion) {
                            if (reducedMotion) {
                                animatable.snapTo(targetValue.toFloat())
                            } else {
                                animatable.animateTo(
                                    targetValue = targetValue.toFloat(),
                                    animationSpec = tween(Motion.DurationSlow, easing = Motion.EasingExpressive),
                                )
                            }
                        }
                        return remember { derivedStateOf { animatable.value.toDouble() } }
                    }
                    
                    //UI
                    val animatedBalance by animateCounter(100.0)
                    
                    Text(
                        text = formatCurrency(animatedBalance, currencyCode)
                    )
                    """.trimIndent()
            )

            //https://github.com/Chouten-App/ChoutenKMP.git
            KotlinCode(
                code = """
                    @Composable
                    fun PosterCard(angle: Float) {
                        val scale = remember { Animatable(0.0f) }

                        // Launch the scale animation when this item enters composition
                        LaunchedEffect(Unit) {
                            scale.animateTo(
                                targetValue = 1f,
                                animationSpec = spring(
                                    dampingRatio = Spring.DampingRatioMediumBouncy,
                                    stiffness = Spring.StiffnessMedium
                                )
                            )
                        }

                        Column(
                            verticalArrangement = Arrangement.spacedBy((-10).dp),
                            modifier = Modifier
                                .graphicsLayer { // apply the scale
                                    scaleX = scale.value
                                    scaleY = scale.value
                                    alpha = scale.value
                                }
                        ) {
                            Box(
                                modifier = Modifier
                                    .padding(bottom = 8.dp)
                                    .size(AppTheme.layout.posterSize)
                                    .shiningBorder(angle, 12.dp)
                            ) {

                            }
                        }
                    }

                    @Composable
                    fun Modifier.shiningBorder(
                        angle: Float,
                        radius: Dp,
                        backgroundColor: Color? = null,
                        borderColor: Color? = null
                    ): Modifier {
                        val colors = AppTheme.colors
                        val background = backgroundColor ?: colors.container
                        val border = borderColor ?: colors.border
                        val sweepBrush = remember(colors) {
                            Brush.sweepGradient(
                                colors = listOf(
                                    border,
                                    background,
                                    background,
                                    background,
                                    border,
                                    border,
                                    background,
                                    background,
                                    background,
                                    background,
                                    background,
                                    border
                                )
                            )
                        }

                        return this
                            .clip(RoundedCornerShape(radius))
                            .drawWithContent {
                                rotate(angle) {
                                    drawCircle(
                                        brush = sweepBrush,
                                        radius = size.width
                                    )
                                }
                                drawContent()
                            }
                            .padding(0.5.dp)
                    }
                    """.trimIndent()
            )

            //https://github.com/rainxchzed/NovemberMiniChallenges.git
            KotlinCode(
                code = """
                    //Swipeable 
                    
                    @Composable
                    fun SwipeableCartItem(
                        isRevealed: Boolean,
                        discountContainer: @Composable () -> Unit,
                        modifier: Modifier = Modifier,
                        onExpanded: () -> Unit = {},
                        onCollapsed: () -> Unit = {},
                        content: @Composable () -> Unit
                    ) {
                        var contextMenuWidth by remember {
                            mutableFloatStateOf(0f)
                        }
                        val offset = remember {
                            Animatable(initialValue = 0f)
                        }
                        val scope = rememberCoroutineScope()

                        LaunchedEffect(key1 = isRevealed, contextMenuWidth) {
                            if(isRevealed) {
                                offset.animateTo(contextMenuWidth)
                                // Auto-collapse after 3 seconds
                                delay(3000)
                                offset.animateTo(0f)
                                onCollapsed()
                            } else {
                                offset.animateTo(0f)
                            }
                        }

                        Box(
                            modifier = modifier
                                .fillMaxWidth()
                                .height(IntrinsicSize.Min)
                                .pointerInput(contextMenuWidth) {
                                    detectHorizontalDragGestures(
                                        onHorizontalDrag = { _, dragAmount ->
                                            scope.launch {
                                                val newOffset = (offset.value + dragAmount)
                                                    .coerceIn(0f, contextMenuWidth)
                                                offset.snapTo(newOffset)
                                            }
                                        },
                                        onDragEnd = {
                                            when {
                                                offset.value >= contextMenuWidth / 2f -> {
                                                    scope.launch {
                                                        offset.animateTo(contextMenuWidth)
                                                        onExpanded()
                                                    }
                                                }

                                                else -> {
                                                    scope.launch {
                                                        offset.animateTo(0f)
                                                        onCollapsed()
                                                    }
                                                }
                                            }
                                        }
                                    )
                                }
                        ) {
                            Row(
                                modifier = Modifier
                                    .onSizeChanged {
                                        contextMenuWidth = it.width.toFloat()
                                    },
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                discountContainer()
                            }
                            Surface(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .offset { IntOffset(offset.value.roundToInt(), 0) }
                            ) {
                                content()
                            }
                        }
                    }
                    
                    @Composable
                    fun CartPromoCode(
                        onCopyClick: () -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        Box(
                            modifier = modifier.fillMaxWidth()
                        ) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .background(
                                        Brush.linearGradient(
                                            listOf(
                                                Color(0xffD33F3F),
                                                Color(0xff7C1414),
                                            )
                                        )
                                    )
                                    .drawWithContent {
                                        drawContent()

                                        drawCircle(
                                            color = HiddenDiscountColors.bg,
                                            radius = 50f,
                                            center = Offset(y = -20f, x = size.width / 2 - 20f)
                                        )

                                        drawCircle(
                                            color = HiddenDiscountColors.bg,
                                            radius = 50f,
                                            center = Offset(y = size.height + 20f, x = size.width / 2 - 20f)
                                        )

                                        (0..12).forEach { index ->
                                            drawRect(
                                                color = HiddenDiscountColors.outlineAlt,
                                                size = Size(
                                                    width = 4f,
                                                    height = 36f
                                                ),
                                                topLeft = Offset(
                                                    x = size.width / 2 - 20f,
                                                    y = index * 50f
                                                )
                                            )
                                        }
                                    }
                                    .padding(12.dp),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Column(
                                    modifier = Modifier.weight(.5f)
                                ) {
                                    Text(
                                        text = "BLACK FRIDAY SALE",
                                        fontFamily = hostGroteskFont(),
                                        fontWeight = FontWeight.Normal,
                                        fontSize = 12.sp,
                                        color = HiddenDiscountColors.textOnDiscount
                                    )

                                    Spacer(Modifier.height(20.dp))

                                    Text(
                                        text = "25%",
                                        fontFamily = hostGroteskFont(),
                                        fontWeight = FontWeight.Bold,
                                        fontSize = 44.sp,
                                        color = HiddenDiscountColors.textAlt,
                                        lineHeight = 44.sp
                                    )
                                    Text(
                                        text = "OFF",
                                        fontFamily = hostGroteskFont(),
                                        fontWeight = FontWeight.Normal,
                                        fontSize = 16.sp,
                                        color = HiddenDiscountColors.textAlt
                                    )
                                }

                                Column(
                                    modifier = Modifier.weight(.5f)
                                ) {
                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        verticalAlignment = Alignment.CenterVertically,
                                        horizontalArrangement = Arrangement.Center
                                    ) {
                                        Text(
                                            text = HiddenDiscountViewModel.VALID_PROMO_CODE,
                                            fontFamily = hostGroteskFont(),
                                            fontWeight = FontWeight.Medium,
                                            fontSize = 24.sp,
                                            color = HiddenDiscountColors.textAlt,
                                            textAlign = TextAlign.Center,
                                            modifier = Modifier
                                                .height(40.dp)
                                                .border(1.dp, HiddenDiscountColors.outlineAlt)
                                                .padding(vertical = 6.dp, horizontal = 16.dp)
                                                .align(Alignment.CenterVertically)
                                        )

                                        Box(
                                            modifier = Modifier
                                                .size(40.dp)
                                                .background(HiddenDiscountColors.surface)
                                                .clickable {
                                                    onCopyClick()
                                                },
                                            contentAlignment = Alignment.Center
                                        ) {
                                            Icon(
                                                imageVector = Icons.Default.ContentCopy,
                                                contentDescription = null,
                                                tint = HiddenDiscountColors.discount,
                                                modifier = Modifier.size(16.dp)
                                            )
                                        }
                                    }
                                }
                            }

                            VerticalDivider()
                        }
                    }
                    """.trimIndent()
            )

            //Button Scale Animation
            KotlinCode(
                code = """
                    //Button Spring Animation
                    
                    val claimButtonScale = remember { Animatable(1.0f) }
                    
                    LaunchedEffect(keyName) {
                        val animationSpec = spring<Float>(
                            dampingRatio = DampingRatioLowBouncy,
                            stiffness = (0.9f * Spring.StiffnessMedium + 0.1f * Spring.StiffnessHigh)
                        )
                        delay(500)
                        claimButtonScale.animateTo(1.25f, animationSpec = animationSpec)
                        claimButtonScale.animateTo(1.0f, animationSpec = animationSpec)
                    }
                    
                    // Modifier.scale(claimButtonScale.value)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                     //Login/SignUp From Animation
                     val animationState = remember { Animatable(0f) }
                     
                     LaunchedEffect(Unit) {
                        animationState.animateTo(1f, animationSpec = tween(1000))
                    }
                    
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(24.dp)
                            .scale(animationState.value)
                            .alpha(animationState.value),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.Center
                    ) { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun ColorAnimation() {
                        val color = remember { Animatable(Color.Red) }

                        LaunchedEffect(Unit) {
                            color.animateTo(Color.Green, animationSpec = tween(2000)) // Animates over 2 seconds
                        }

                        Box(modifier = Modifier.size(100.dp).background(color.value))
                    }
                    """.trimIndent()
            )


            KotlinCode(
                code = """
                    //Button Click Animation
                    
                    val scale = remember { Animatable(1f) }
                    val scope = rememberCoroutineScope()
                    var isAnimating = false

                    val onClick = {
                        scope.launch {
                            scale.animateTo(0.96f, animationSpec = tween(250))
                            scale.animateTo(1f, animationSpec = tween(150))
                            isAnimating = false
                        }
                    }
                    
                    //Button 
                    Modifier.fillMaxWidth()
                        .graphicsLayer {
                            scaleX = scale.value
                            scaleY = scale.value
                    }
                    
                    onClick = {
                        if(!isAnimating) {
                            isAnimating = true
                            onClick()
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Scale Animation
                    
                    @Composable
                    fun SplashScreen(navController: NavHostController) {
                        val sessionManager: SessionManager = koinInject()
                        val scale = remember { Animatable(0f) }

                        LaunchedEffect(key1 = true) {
                            scale.animateTo(
                                targetValue = 0.9f,
                                animationSpec = tween(
                                    durationMillis = 800,
                                    easing = FastOutSlowInEasing 
                                )
                            )

                            delay(2000L)
                            
                            val destination = if (sessionManager.isLoggedIn()) {
                                Screen.Calendar.route
                            } else {
                                Screen.Login.route
                            }

                            navController.navigate(destination) {
                                popUpTo(Screen.Splash.route) { inclusive = true }
                            }
                        }

                        Box(
                            contentAlignment = Alignment.Center,
                            modifier = Modifier
                                .fillMaxSize()
                                .background(Color(0xFF4675A6))
                        ) {
                            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                Image(
                                    painter = painterResource(),
                                    contentDescription = "Logo",
                                    modifier = Modifier
                                        .size(300.dp)
                                        .scale(scale.value)
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "animateItem()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The animateItem() modifier allows you to seamlessly animate individual list items.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It provides smooth transitions when items are added, removed, or reordered.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This modifier is compatible with list views like LazyColumn, LazyRow, and LazyVerticalGrid.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To enable these animations, ensure you provide a unique key for each item using the key parameter in LazyListScope.item / LazyListScope.items",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://www.linkedin.com/in/anshsachdevawork/
            KotlinCode(
                code = """
                    //LazyColumn Animation (LazyVerticalGrid)
                    
                    Modifier
                    .animateItem(
                        fadeInSpec = spring(stiffness = Spring.StiffnessMediumLow),
                        placementSpec = spring(
                            stiffness = Spring.StiffnessLow,
                            visibilityThreshold = IntOffset.VisibilityThreshold
                        ),
                        fadeOutSpec = spring(stiffness = Spring.StiffnessMediumLow)
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "AnimateValueAsState",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "animateValueAsState (and its type-safe variants like animateFloatAsState, animateDpAsState, etc.) is a Compose state utility that smoothly animates a value from its current state to a target whenever the target changes. Instead of instantly snapping, it produces an animated value you can read in Composables. You can configure the animation with AnimationSpec (tween, spring, keyframes), provide a label for inspection, and use finishedListener to react when the animation completes. Prefer the typed variants when possible; animateValueAsState is for custom types with a TwoWayConverter.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "animate*AsState",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "animate*AsState is a group of composable functions that smoothly animate a value whenever its target changes. Feed it a target, and it produces a frame-by-frame animated value you can plug directly into your UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "animateFloatAsState, animateDpAsState (value-based animations (like padding, size, alpha, color)), animateColorAsState, animateIntAsState, animateSizeAsState,  animateRectAsstate, animateIntOffsetAsState, animateIntSizeAsState, animateValueAsState and animateOffsetAsState provide easy state-driven animations. Automatically animates property changes when a state variable updates. When a state changes, Compose detects the change and starts an animation. The animation engine interpolates values over time to transition smoothly. Works with default animation specs like tween(), spring(), etc.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "animateOffsetAsState",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/Joker-x-dev/CoolMallKotlin.git
            KotlinCode(
                code = """
                    //Bottom to Top Animation
                    val bottomBarOffset by animateOffsetAsState(
                        targetValue = if (hasAnimated) Offset(0f, 0f) else Offset(0f, 200f),
                        animationSpec = tween(durationMillis = 800),
                        label = "bottom_bar_offset"
                    )
  
                    //offset { IntOffset(bottomBarOffset.x.toInt(), bottomBarOffset.y.toInt()) },
                    """.trimIndent()
            )

            SpanText(
                text = "animateIntAsState",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/codewithdipesh/Habitized.git
            KotlinCode(
                code = """
                    //Image Animation Bottom to Top with diagonal 
                    
                    @Composable
                    fun ThirdScreen(
                        onContinue : () -> Unit = {},
                        onNavigateBack : () -> Unit = {}
                    ) {

                        var startPhoneAnimation by rememberSaveable { mutableStateOf(false) }
                        var startImageLogAnimation by rememberSaveable { mutableStateOf(false) }

                        val phoneYOffset by animateIntAsState(
                            targetValue = if (startPhoneAnimation) -200 else 0,
                            animationSpec = tween(durationMillis = 700)
                        )

                        val imageLogXOffset by animateIntAsState(
                            targetValue = if (startImageLogAnimation) -90 else 0,
                            animationSpec = tween(durationMillis = 700)
                        )
                        val imageLogYOffset by animateIntAsState(
                            targetValue = if (startImageLogAnimation) -200 else 0,
                            animationSpec = tween(durationMillis = 700)
                        )

                        LaunchedEffect(Unit) {
                            startPhoneAnimation = true
                            delay(300)
                            startImageLogAnimation = true
                        }

                       Box(modifier = Modifier.fillMaxSize()
                            .background(Color(0xFFECA856))
                       ){
                           //background
                           Image(
                               painter = painterResource(id = R.drawable.bg_grid),
                               contentDescription = null,
                               modifier = Modifier
                                   .align(Alignment.TopCenter)
                                   .fillMaxWidth()
                                   .offset(y=60.dp)
                           )
                           
                            //image logging
                            if(startImageLogAnimation) {
                                Image(
                                    painter = painterResource(id = R.drawable.image_loging),
                                    contentDescription = "Onboarding Image",
                                    modifier = Modifier.align(Alignment.Center)
                                        .width(180.dp)
                                        .offset(x= imageLogXOffset.dp , y=imageLogYOffset.dp)
                                )
                            }
                            
                            //main image
                            Image(
                                painter = painterResource(id = R.drawable.phone_photo),
                                contentDescription = "Onboarding Image",
                                modifier = Modifier.align(Alignment.BottomCenter)
                                    .width(220.dp)
                                    .offset(y=phoneYOffset.dp)
                            )

                            //Card
                            Box(
                                modifier = Modifier
                                    .align(Alignment.BottomCenter)
                                    .fillMaxWidth()
                                    .fillMaxHeight(0.4f)
                                    .clip(RoundedCornerShape(topStart = 27.dp, topEnd = 27.dp))
                                    .background(Color.White)
                            )

                            //Title and heading
                            Column(
                                modifier = Modifier
                                    .align(Alignment.BottomCenter)
                                    .fillMaxWidth()
                                    .padding(horizontal = 24.dp)
                                    .padding(bottom = 150.dp),
                                horizontalAlignment = Alignment.Start,
                            ){
                                Text(
                                    text = "Dopamine Boost",
                                    style = TextStyle(
                                        color = Color.Black,
                                        fontFamily = instrumentSerif,
                                        fontWeight = FontWeight.Normal,
                                        fontSize = 44.sp
                                    )
                                )
                                Text(
                                    text = "Discipline Over Motivation",
                                    style = TextStyle(
                                        color = Color.Black,
                                        fontFamily = instrumentSerif,
                                        fontWeight = FontWeight.Normal,
                                        fontSize = 32.sp
                                    )
                                )
                                Spacer(Modifier.height(16.dp))

                                Text(
                                    text = "Streaks , Stats and Image Logging help you stay stick to your habit",
                                    style = TextStyle(
                                        color = Color.Black,
                                        fontFamily = instrumentSerif,
                                        fontWeight = FontWeight.Normal,
                                        fontSize = 20.sp
                                    )
                                )
                            }
                           
                            //IconButton
                            IconButton(
                                onClick = onNavigateBack,
                                modifier = Modifier
                                    .align(Alignment.TopStart)
                                    .padding(top = 30.dp, start = 16.dp)
                                    .clip(CircleShape)
                                    .background(Color.White.copy(0.2f))
                            ) {
                                Icon(
                                    imageVector = Icons.Filled.KeyboardArrowLeft,
                                    contentDescription = "Back",
                                    tint = Color.White,
                                    modifier = Modifier.size(30.dp)
                                )
                            }

                            //Continue Button
                            Box(
                                modifier = Modifier
                                    .align(Alignment.BottomCenter)
                                    .padding(bottom = 50.dp)
                                    .fillMaxWidth()
                                    .padding(horizontal = 24.dp)
                                    .clip(RoundedCornerShape(50.dp))
                                    .background(Color.Black)
                                    .clickable{
                                        onContinue()
                                    },
                                contentAlignment = Alignment.Center
                            ){
                                Text(
                                    text = "Continue",
                                    style = TextStyle(
                                        color = Color.White,
                                        fontFamily = regular,
                                        fontWeight = FontWeight.Normal,
                                        fontSize = 16.sp
                                    ),
                                    modifier = Modifier.padding(16.dp)
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "animateFloatAsState",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/odaridavid/Smoovie-KMP.git
            KotlinCode(
                code = """
                     //LazyColumn Animation 
                     
                     private val IMAGE_HEIGHT = 140.dp
                     private const val CARD_ANIM_DURATION = 350
                     private const val CARD_STAGGER_MS = 60
                     private const val CARD_STAGGER_LIMIT = 5
                     
                     @Composable
                     internal fun AnimatedMovieCard(
                         movie: MovieUiModel,
                         index: Int,
                         skipAnimation: Boolean,
                         onAnimationEnd: () -> Unit,
                         onClick: () -> Unit,
                     ) {
                         if (index >= CARD_STAGGER_LIMIT || skipAnimation) {
                             //Card
                             return
                         }
                         var entered by remember { mutableStateOf(false) }
                         val staggerDelay = index * CARD_STAGGER_MS
                         LaunchedEffect(Unit) {
                             entered = true
                             delay((staggerDelay + CARD_ANIM_DURATION).toLong().milliseconds)
                             onAnimationEnd()
                         }
                         val spec = tween<Float>(CARD_ANIM_DURATION, delayMillis = staggerDelay)
                         val scaleX by animateFloatAsState(
                             targetValue = if (entered) 1f else 0f,
                             animationSpec = spec,
                             label = "scaleX",
                         )
                         val alpha by animateFloatAsState(
                             targetValue = if (entered) 1f else 0f,
                             animationSpec = spec,
                             label = "alpha",
                         )
                         Box(
                             modifier =
                                 Modifier
                                     .fillMaxWidth()
                                     .graphicsLayer {
                                         this.scaleX = scaleX
                                         this.alpha = alpha
                                     },
                         ) {
                             //Card
                         }
                     }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun ExpandIcon(expanded: Boolean) {
                        val rotation by animateFloatAsState(
                            targetValue = if (expanded) 180f else 0f,
                            animationSpec = tween(durationMillis = 250)
                        )
                        Icon(
                            imageVector = Icons.Default.KeyboardArrowDown,
                            contentDescription = if (expanded) "Collapse" else "Expand",
                            modifier = Modifier.rotate(rotation)
                        )
                    }
                    The rotation duration should roughly match the card’s resize duration so the two read as one motion. 250ms is a safe default. If you tuned animateContentSize to be slower, slow the chevron too.

                    Putting It Together: An FAQ Card
                    Here’s the whole thing as a reusable composable you can drop into an FAQ screen:

                    @Composable
                    fun FaqCard(
                        question: String,
                        answer: String,
                        modifier: Modifier = Modifier
                    ) {
                        var expanded by remember { mutableStateOf(false) }
                        val rotation by animateFloatAsState(
                            targetValue = if (expanded) 180f else 0f,
                            animationSpec = tween(durationMillis = 250)
                        )

                        Card(
                            modifier = modifier
                                .fillMaxWidth()
                                .clickable { expanded = !expanded }
                        ) {
                            Column(
                                modifier = Modifier
                                    .padding(16.dp)
                                    .animateContentSize(
                                        animationSpec = tween(
                                            durationMillis = 250,
                                            easing = FastOutSlowInEasing
                                        )
                                    )
                            ) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.SpaceBetween
                                ) {
                                    Text(
                                        text = question,
                                        style = MaterialTheme.typography.titleMedium,
                                        modifier = Modifier.weight(1f)
                                    )
                                    Icon(
                                        imageVector = Icons.Default.KeyboardArrowDown,
                                        contentDescription = if (expanded) "Collapse" else "Expand",
                                        modifier = Modifier.rotate(rotation)
                                    )
                                }
                                
                                AnimatedVisibility(visible = expanded) {
                                    Column {
                                        Spacer(modifier = Modifier.height(12.dp))
                                        Text(
                                            text = answer,
                                            style = MaterialTheme.typography.bodyMedium
                                        )
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/ProjektMagma/MagmaQuiz.git
            KotlinCode(
                code = """
                    //stickyHeader
                    
                    @Composable
                    actual fun <T> AutoScalableLazyColumn(
                        itemList: List<T>,
                        key: ((T) -> Any)?,
                        contentEmptyMessage: String,
                        uiState: UiState,
                        isLoadingMore: Boolean,
                        onLoadMore: () -> Unit,
                        stickyHeader: @Composable (modifier: Modifier) -> Unit,
                        content: @Composable ((T) -> Unit)
                    ) {
                        val keyboardController = LocalSoftwareKeyboardController.current
                        val state = rememberLazyListState()
                        val yHeaderOffset by animateFloatAsState(
                            targetValue = if (state.lastScrolledBackward || state.firstVisibleItemIndex == 0)
                                0f
                            else -150f,
                            animationSpec = tween(durationMillis = 300, easing = EaseOut)
                        )

                        LaunchedEffect(state.isScrollInProgress) {
                            keyboardController?.hide()
                        }

                        LaunchedEffect(itemList) {
                            snapshotFlow { state.layoutInfo.visibleItemsInfo.lastOrNull()?.index }
                                .distinctUntilChanged()
                                .collect { lastVisibleIndex ->
                                    if (lastVisibleIndex == itemList.lastIndex + 1) {
                                        onLoadMore()
                                    }
                                }
                        }

                        LazyColumn(
                            modifier = Modifier
                                .clip(MaterialTheme.shapes.large)
                                .fillMaxWidth(),
                            state = state,
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(8.dp, Alignment.Top)
                        ) {
                            stickyHeader {
                                stickyHeader(Modifier.offset(y = yHeaderOffset.dp))
                            }

                            if (uiState == UiState.Success) {
                                items(items = itemList, key = key) { item ->
                                    content(item)
                                }
                                if (isLoadingMore) {
                                    item {
                                        Box(modifier = Modifier.fillMaxWidth().padding(16.dp)) {
                                            CircularProgressIndicator(modifier = Modifier.align(Alignment.Center))
                                        }
                                    }
                                }
                            }
                        }

                        when (uiState) {
                            UiState.Success -> {
                                if (itemList.isEmpty() && !isLoadingMore) {
                                    Column(
                                        modifier = Modifier
                                            .fillMaxSize()
                                            .zIndex(1f),
                                        horizontalAlignment = Alignment.CenterHorizontally,
                                        verticalArrangement = Arrangement.Center
                                    ) {
                                        Text(
                                            text = contentEmptyMessage,
                                            style = MaterialTheme.typography.titleLarge
                                        )
                                    }
                                }
                            }

                            is UiState.Error -> FullSizeErrorIndicator(message = uiState.errorMessage)
                            UiState.Loading -> FullSizeCircularProgressIndicator()
                        }
                    }
                    
                    @Composable
                    expect fun <T> AutoScalableLazyColumn(
                        itemList: List<T>,
                        key: ((T) -> Any)? = null,
                        contentEmptyMessage: String = stringResource(Res.string.empty_content),
                        uiState: UiState,
                        isLoadingMore: Boolean = false,
                        onLoadMore: () -> Unit = {},
                        stickyHeader: @Composable (modifier: Modifier) -> Unit,
                        content: @Composable (T) -> Unit
                    )
                    """.trimIndent()
            )

            //https://github.com/julesmeister/deutschcraft-compose-multiplatform.git
            KotlinCode(
                code = """
                    //Error Animations
                    //Typewriter text effect that reveals text character by character.
                    
                    @Composable
                    fun TypewriterText(
                        text: String,
                        modifier: Modifier = Modifier,
                        style: TextStyle = MaterialTheme.typography.bodyMedium,
                        typingDelayMs: Long = 15,
                        onComplete: () -> Unit = {}
                    ) {
                        var visibleChars by remember { mutableStateOf(0) }

                        LaunchedEffect(text) {
                            visibleChars = 0
                            text.forEachIndexed { index, _ ->
                                delay(typingDelayMs)
                                visibleChars = index + 1
                            }
                            onComplete()
                        }

                        Text(
                            text = text.take(visibleChars),
                            style = style,
                            modifier = modifier
                        )
                    }
                    """.trimIndent()
            )

            //https://github.com/julesmeister/deutschcraft-compose-multiplatform.git
            KotlinCode(
                code = """
                    //Button Scale Animation 
                    
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        var applyButtonVisible by remember { mutableStateOf(false) }

                        LaunchedEffect(currentSuggestion) {
                            applyButtonVisible = false
                            kotlinx.coroutines.delay(400)
                            applyButtonVisible = true
                        }

                        val applyButtonScale by animateFloatAsState(
                            targetValue = if (applyButtonVisible) 1f else 0.8f,
                            animationSpec = spring(
                                dampingRatio = Spring.DampingRatioHighBouncy,
                                stiffness = Spring.StiffnessMedium
                            ),
                            label = "apply_bounce"
                        )

                        Button(
                            onClick = onApply,
                            modifier = Modifier
                                .weight(1f)
                                .graphicsLayer { scaleX = applyButtonScale; scaleY = applyButtonScale },
                            colors = ButtonDefaults.buttonColors(
                                containerColor = Indigo
                            )
                        ) {
                            Icon(Icons.Default.Check, contentDescription = null, modifier = Modifier.size(16.dp))
                            Spacer(modifier = Modifier.width(4.dp))
                            Text("Apply")
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun CategoryChip(
                        title: String,
                        onClick: () -> Unit
                    ) {
                        var pressed by remember { mutableStateOf(false) }

                        val scale by animateFloatAsState(
                            targetValue = if (pressed) 0.72f else 1f,
                            animationSpec = spring(
                                dampingRatio = Spring.DampingRatioMediumBouncy,
                                stiffness = Spring.StiffnessLow
                            )
                        )

                        Box(
                            modifier = Modifier
                                .width(140.dp)
                                .height(90.dp)
                                .graphicsLayer {
                                    scaleX = scale
                                    scaleY = scale
                                },
                            contentAlignment = Alignment.TopCenter
                        ) {
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/ResulSilay/Sentinel.git
            KotlinCode(
                code = """
                    //Scroll to Hide Header
                    
                    @Composable
                    internal fun SentinelHeader(
                        scrollState: ScrollState
                    ) {
                        val collapseRange = 350f

                        @Suppress("FrequentlyChangingValue")
                        val collapseFraction = (scrollState.value / collapseRange).coerceIn(0f, 1f)

                        val scale by animateFloatAsState(
                            targetValue = 1f - (collapseFraction * 0.3f),
                            label = ""
                        )

                        val alpha by animateFloatAsState(
                            targetValue = 1f - collapseFraction,
                            label = ""
                        )

                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .graphicsLayer {
                                    scaleX = scale
                                    scaleY = scale
                                    this.alpha = alpha
                                }
                        ) {
                            //Header UI

                            Spacer(modifier = Modifier.height(height = 16.dp))
                        }
                    }
                    
                    @Composable
                    private fun SentinelDashboardContent() 
                    {
                        val scrollState = rememberScrollState()

                        Scaffold(
                            modifier = Modifier.fillMaxSize(),
                            containerColor = Color.Transparent,
                        ) { _ ->
                            Column(
                                modifier = Modifier.verticalScroll(state = scrollState)
                            ) {
                                SentinelHeader(
                                    scrollState = scrollState
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Welcome screen animation

                    @Composable
                    fun WelcomeScreen(onNavigate: (String) -> Unit) {
                    	var animationPlayed by remember { mutableStateOf(false) }

                    	val textAlpha by animateFloatAsState(
                    		targetValue = if (animationPlayed) 1f else 0f,
                    		label = "",
                    		animationSpec = tween(delayMillis = 200, durationMillis = 1000)
                    	)

                    	val imgSize by animateDpAsState(
                    		targetValue = if (animationPlayed) 300.dp else 100.dp, animationSpec = spring(
                    			dampingRatio = Spring.DampingRatioMediumBouncy, stiffness = Spring.StiffnessLow
                    		), label = ""
                    	)

                    	LaunchedEffect(Unit) {
                    		delay(100)
                    		animationPlayed = true
                    	}

                    	Scaffold(floatingActionButton = {
                    		FloatingActionButton(
                    			onClick = {
                    				onNavigate(Screen.PermissionScreen.name)
                    			},
                    			shape = CircleShape,
                    			containerColor = blue,
                    			contentColor = MaterialTheme.colorScheme.onPrimary
                    		) {
                    			Icon(imageVector = Icons.AutoMirrored.Filled.ArrowForward, contentDescription = null)
                    		}
                    	}) { innerPadding ->

                    		val blackGradient = Brush.verticalGradient(
                    			listOf(
                    				MaterialTheme.colorScheme.primary,
                    				MaterialTheme.colorScheme.secondary
                    			)
                    		)

                    		Column(
                    			Modifier
                    				.fillMaxSize()
                    				.padding(innerPadding)
                    				.background(blackGradient),
                    			horizontalAlignment = Alignment.CenterHorizontally,
                    			verticalArrangement = Arrangement.Center
                    		) {
                    			Text(text = "Want to reduce", style = descriptionStyle)
                    			Spacer(modifier = Modifier.height(8.dp))
                    			Text(text = "Scroll Addiction ?", style = titleStyle, color = orange)

                    			Spacer(modifier = Modifier.height(40.dp))

                    			Image(
                    				modifier = Modifier.size(imgSize),
                    				painter = painterResource(id = R.drawable.onboarding),
                    				contentDescription = null
                    			)
                    			Spacer(modifier = Modifier.height(40.dp))

                    			Column(
                    				modifier = Modifier
                    					.fillMaxWidth()
                    					.graphicsLayer { alpha = textAlpha },
                    				horizontalAlignment = Alignment.CenterHorizontally
                    			) {
                    				Text(text = "Welcome to", style = h2style)
                    				Spacer(modifier = Modifier.height(24.dp))
                    				Text(text = "ScrollBlock", style = titleStyle, color = blue)
                    				Spacer(modifier = Modifier.height(8.dp))
                    				Text(text = "Regain Control  Boost Productivity", style = descriptionStyle)
                    			}
                    		}
                    	}
                    }

                    @Preview
                    @Composable
                    private fun WelcomeScreenPreview() {
                    	ScrollBlockTheme {
                    		WelcomeScreen({})
                    	}
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //RotationEffect
                    
                    var isRotated by remember { mutableStateOf(false) }
                    val rotateAngle by animateFloatAsState( targetValue = if (isRotated) 360f else 0f, label = "", animationSpec = tween(1000))
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //ItemView inside LinearWavyProgressIndicator
                    
                    data class AttendanceEntity(
                        val subjectCode: String,
                        val subjectName: String,
                        val attendedClasses: Int,
                        val totalClasses: Int,
                        val percentage: Double,
                        val facultyName: String,
                        val year: String,
                        val term: String
                    )
                    
                    @OptIn(ExperimentalMaterial3ExpressiveApi::class)
                    @Composable
                    fun AttendanceCard(item: AttendanceEntity) {
                        val uiColors = UIColors()
                        var targetProgress by remember { mutableFloatStateOf(0f) }

                        val progress by animateFloatAsState(
                            targetValue = targetProgress,
                            animationSpec = tween(
                                durationMillis = 1000,
                                easing = FastOutSlowInEasing
                            ),
                            label = "attendance"
                        )

                        // 👇 This triggers the animation ONCE
                        LaunchedEffect(Unit) {
                            targetProgress = (item.percentage.toFloat() / 100f).coerceIn(0f, 1f)
                        }

                        Column(modifier = Modifier.padding(16.dp)) {
                            // Title
                            Row(
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Text(
                                    text = item.subjectName,
                                    fontFamily = FontFamily.Monospace,
                                    fontWeight = FontWeight.SemiBold,
                                    color = uiColors.textPrimary,
                                    fontSize = 16.sp,
                                    modifier = Modifier.weight(1f)
                                )
                                Icon(
                                    imageVector = Icons.AutoMirrored.Filled.ArrowForwardIos,
                                    contentDescription = "Tap",
                                    modifier = Modifier.size(16.dp)
                                )
                            }

                            Spacer(modifier = Modifier.height(8.dp))

                            // Attendance row
                            Text(
                                text = "Attendance: ${item.attendedClasses}/${item.totalClasses}",
                                fontFamily = FontFamily.Monospace,
                                color = uiColors.textSecondary,
                                fontSize = 14.sp
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            LinearWavyProgressIndicator(
                                progress = {
                                    progress
                                },
                                color = uiColors.accentOrangeStart,
                                trackColor = uiColors.progressAccent,
                                modifier = Modifier.fillMaxWidth(),
                                amplitude = {
                                    0.8f
                                },
                                waveSpeed = 20.dp,
                                wavelength = 50.dp
                            )

                            Spacer(modifier = Modifier.height(10.dp))

                            // Percentage and faculty
                            Text(
                                text = "Percentage: ${((item.percentage * 100).toInt() / 100.0)}%",
                                fontFamily = FontFamily.Monospace,
                                color = uiColors.textSecondary,
                                fontSize = 14.sp
                            )

                            Spacer(modifier = Modifier.height(6.dp))

                            Text(
                                text = "Faculty: ${item.facultyName}",
                                fontFamily = FontFamily.Monospace,
                                color = uiColors.textSecondary,
                                fontSize = 14.sp
                            )
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/mrAyush1804/PoultryMandi.git
            KotlinCode(
                code = $$"""
                    //Select Date Flip
                    
                    data class DataItem(
                        val day: Int,
                        val dayLabel: String,
                        val month: String,
                        val year: Int,
                        val isAvailable1: String,
                        val isAvailable: Boolean = true
                    )
                    
                    @Composable
                    fun DateSelector(
                        dates: List<DataItem>,
                        selectedDate: DataItem?,
                        onDateSelected: (DataItem) -> Unit,
                        modifier: Modifier = Modifier,
                        monthYear: String = "September 2023"  
                    ) {
                        val listState = rememberLazyListState()
                        
                        LaunchedEffect(selectedDate) {
                            selectedDate?.let { selected ->
                                val index = dates.indexOfFirst { it.day == selected.day }
                                if (index >= 0) listState.animateScrollToItem(index)
                            }
                        }

                        Column(modifier = modifier) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(horizontal = 10.dp),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Text(
                                    text = monthYear,
                                    fontSize = 14.sp,
                                    fontWeight = FontWeight.SemiBold,
                                    color = Color(0xFF333333)
                                )

                                AnimatedContent(
                                    targetState = selectedDate,
                                    label = "selectedBadge"
                                ) { date ->
                                    if (date != null) {
                                        Text(
                                            text = "${date.dayLabel}, ${date.day}",
                                            color = brown,
                                            fontSize = 12.sp,
                                            fontWeight = FontWeight.SemiBold,
                                            modifier = Modifier
                                                .clip(RoundedCornerShape(20.dp))
                                                .padding(horizontal = 10.dp, vertical = 4.dp)
                                        )
                                    } else {
                                        // Nothing — placeholder space
                                        Spacer(Modifier.width(80.dp))
                                    }
                                }
                            }

                            Spacer(Modifier.height(10.dp))
                            
                            LazyRow(
                                state = listState,
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(8.dp),
                                contentPadding = PaddingValues(horizontal = 4.dp)
                            ) {
                                items(
                                    items = dates,
                                    key = { it.day }
                                ) { dateItem ->
                                    FlipDateCard(
                                        dateItem = dateItem,
                                        isSelected = dateItem.day == selectedDate?.day,
                                        onClick = { onDateSelected(dateItem) }
                                    )
                                }
                            }
                        }
                    }
                    
                    @Composable
                    fun FlipDateCard(
                        dateItem: DataItem,
                        isSelected: Boolean,
                        onClick: () -> Unit
                    ) {
                        val rotation by animateFloatAsState(
                            targetValue = if (isSelected) 180f else 0f,
                            animationSpec = tween(
                                durationMillis = 500,
                                easing = FastOutSlowInEasing
                            ),
                            label = "flipRotation"
                        )

                        Box(
                            modifier = Modifier
                                .width(58.dp)
                                .height(72.dp)
                                .graphicsLayer {
                                    rotationY = rotation        // ✅ Y axis flip
                                    cameraDistance = 12f * density
                                }
                                .clip(RoundedCornerShape(14.dp))
                                .background(
                                    when {
                                        isSelected -> brown // Selected = brown
                                        !dateItem.isAvailable -> Color(0xFFF0F0F0) // Unavailable = light grey
                                        else -> Color.White // Available = white
                                    }
                                )
                                .border(
                                    width = 1.dp,
                                    color = when {
                                        isSelected -> brown.copy(alpha = 0.7f)
                                        !dateItem.isAvailable -> Color(0xFFE0E0E0)
                                        else -> Color(0xFFD0D0D0)
                                    },
                                    shape = RoundedCornerShape(14.dp)
                                )
                                .clickable(
                                    enabled = dateItem.isAvailable  // ✅ Unavailable = click nahi hoga
                                ) { onClick() }
                                .alpha(if (dateItem.isAvailable) 1f else 0.4f), // ✅ Unavailable = faded
                            contentAlignment = Alignment.Center
                        ) {
                            if (rotation <= 90f) {
                                Column(
                                    horizontalAlignment = Alignment.CenterHorizontally,
                                    verticalArrangement = Arrangement.Center
                                ) {
                                    Text(
                                        text = dateItem.dayLabel,
                                        color = if (isSelected) Color.White else Color.Gray,
                                        fontSize = 11.sp,
                                        fontWeight = FontWeight.Medium
                                    )
                                    Spacer(Modifier.height(4.dp))
                                    Text(
                                        text = "${dateItem.day}",
                                        color = if (isSelected) Color.White else Color.Black,
                                        fontSize = 20.sp,
                                        fontWeight = FontWeight.Bold
                                    )

                                    if (dateItem.isAvailable && !isSelected) {
                                        Spacer(Modifier.height(4.dp))
                                        Box(
                                            modifier = Modifier
                                                .size(5.dp)
                                                .background(brown, CircleShape)
                                        )
                                    }
                                }
                            }

                            else {
                                Box(
                                    modifier = Modifier.graphicsLayer { rotationY = 180f },
                                    contentAlignment = Alignment.Center
                                ) {
                                    Column(
                                        horizontalAlignment = Alignment.CenterHorizontally,
                                        verticalArrangement = Arrangement.Center
                                    ) {
                                        Icon(
                                            imageVector = Icons.Default.Check,
                                            contentDescription = null,
                                            tint = Color.White,
                                            modifier = Modifier.size(22.dp)
                                        )
                                        Spacer(Modifier.height(2.dp))
                                        Text(
                                            text = "${dateItem.day}",
                                            color = Color.White,
                                            fontSize = 14.sp,
                                            fontWeight = FontWeight.Bold
                                        )
                                    }
                                }
                            }
                        }
                    }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    //ItemView Animation
                    
                    @Composable
                    fun AnimatedProductCard(
                        onClick: () -> Unit,
                        modifier: Modifier = Modifier,
                        animationDelay: Int = 0
                    ) {
                        var isPressed by remember { mutableStateOf(false) }
                        var isVisible by remember { mutableStateOf(false) }

                        // Entry animation
                        LaunchedEffect(Unit) {
                            kotlinx.coroutines.delay(animationDelay.toLong())
                            isVisible = true
                        }

                        // Press animation
                        val scale by animateFloatAsState(
                            targetValue = when {
                                isPressed -> 0.92f
                                else -> 1f
                            },
                            animationSpec = spring(
                                dampingRatio = Spring.DampingRatioMediumBouncy,
                                stiffness = Spring.StiffnessHigh
                            )
                        )

                        // Entry animation values
                        val alpha by animateFloatAsState(
                            targetValue = if (isVisible) 1f else 0f,
                            animationSpec = tween(400, easing = FastOutSlowInEasing)
                        )

                        val offsetY by animateFloatAsState(
                            targetValue = if (isVisible) 0f else 30f,
                            animationSpec = tween(400, easing = FastOutSlowInEasing)
                        )

                        Card(
                            modifier = modifier
                                .width(160.dp)
                                .graphicsLayer {
                                    this.alpha = alpha
                                    translationY = offsetY
                                }
                                .scale(scale)
                                .shadow(
                                    elevation = if (isPressed) 2.dp else 6.dp,
                                    shape = ProductCardShape,
                                    spotColor = CardShadowColor
                                ),
                            shape = ProductCardShape,
                            colors = CardDefaults.cardColors(containerColor = White),
                            onClick = {
                                isPressed = true
                                onClick()
                            }
                        ) {
                        
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/MustafaKoceerr/JustRelax.git
            KotlinCode(
                code = """
                    //Space for chat UI
                    
                    val spacerWeight by animateFloatAsState(
                        targetValue = if (hasResults) 0.001f else 1f,
                        animationSpec = tween(durationMillis = 600),
                        label = "LayoutMorphing"
                    )
                    """.trimIndent()
            )

            //https://github.com/Quartel-Enterprise/bible-planner-mobile-client.git
            KotlinCode(
                code = """
                    private const val EXPANDED_ROTATION_VALUE = 180f
                    private const val COLLAPSED_ROTATION_VALUE = 0f
                    private const val ROTATION_LABEL = "WeekArrowRotation"

                    @Composable
                    fun ArrowRotationIcon(
                        modifier: Modifier = Modifier,
                        isUp: Boolean,
                    ) {
                        val arrowRotation by getAnimationState(isUp)
                        Icon(
                            imageVector = Icons.Default.KeyboardArrowDown,
                            contentDescription = stringResource(
                                resource = if (isUp) {
                                    Res.string.collapse
                                } else {
                                    Res.string.expand
                                },
                            ),
                            modifier = modifier
                                .rotate(arrowRotation),
                        )
                    }

                    @Composable
                    private fun getAnimationState(isUp: Boolean): State<Float> = animateFloatAsState(
                        targetValue = if (isUp) {
                            EXPANDED_ROTATION_VALUE
                        } else {
                            COLLAPSED_ROTATION_VALUE
                        },
                        label = ROTATION_LABEL,
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Animated Tag Scale
                    
                    @Composable
                    fun AnimatedTag(
                        text: String,
                        color: Color,
                        isPressed: Boolean
                    ) {
                        val tagScale by animateFloatAsState(
                            targetValue = if (isPressed) 0.95f else 1f,
                            animationSpec = spring(
                                dampingRatio = Spring.DampingRatioMediumBouncy,
                                stiffness = Spring.StiffnessHigh
                            ),
                            label = "tagScale"
                        )

                        Surface(
                            shape = RoundedCornerShape(12.dp),
                            color = color.copy(alpha = 0.1f),
                            modifier = Modifier
                                .scale(tagScale)
                        ) {
                            Text(
                                text = text,
                                style = TextStyle(
                                    color = color,
                                    fontSize = 12.sp,
                                    fontWeight = FontWeight.SemiBold
                                ),
                                modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp)
                            )
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/1006245347/zhheweijie-testai1.git
            KotlinCode(
                code = """
                    //Vertical Scrollbar Indicator
                    
                    @Composable
                    fun Modifier.VerticalScrollbar(
                        state: LazyListState,
                        width: Dp = 8.dp,
                        color: Color,
                        isReverse: Boolean = false,
                    ): Modifier {
                        val targetAlpha = if (state.isScrollInProgress) 1f else 0f
                        val duration = if (state.isScrollInProgress) 150 else 500
                        
                        val alpha by animateFloatAsState(
                            targetValue = targetAlpha,
                            animationSpec = tween(durationMillis = duration)
                        )
                        
                        return drawWithContent {
                            drawContent()

                            val firstVisibleElementIndex = state.layoutInfo.visibleItemsInfo.firstOrNull()?.index
                            val needDrawScrollbar = state.isScrollInProgress || alpha > 0.0f

                            // Draw scrollbar if scrolling or if the animation is still running and lazy column has content
                            if (needDrawScrollbar && firstVisibleElementIndex != null) {
                                val elementHeight = this.size.height / state.layoutInfo.totalItemsCount
                                val scrollbarOffsetY =
                                    firstVisibleElementIndex * elementHeight + state.firstVisibleItemScrollOffset / 4
                                val scrollbarHeight = elementHeight * 4

                                drawRect(
                                    color = color,
                                    topLeft = Offset(
                                        this.size.width - width.toPx(),
                                        this.center.y * 2 - scrollbarOffsetY
                                    ),
                                    size = Size(width.toPx(), scrollbarHeight),
                                    alpha = alpha
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                   // Animation for TopBar

                   val topBarElevation by animateFloatAsState(
                       targetValue = if (lazyListState.firstVisibleItemIndex > 0) 8f else 0f,
                       animationSpec = tween(300),
                       label = "topBar elevation"
                   ) {
                        //Modifier.shadow(topBarElevation.dp)
                   }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Setting option select onClick animation border background icon iconBackground
                    
                    @Composable
                    fun PreferenceOption(
                        modifier: Modifier = Modifier,
                        name: String,
                        description: String,
                        imageRes: Painter,
                        isSelected: Boolean,
                        onClick: () -> Unit
                    ) {
                        var isPressed by remember { mutableStateOf(false) }

                        val scale by animateFloatAsState(
                            targetValue = if (isPressed) 1.05f else 1f,
                            animationSpec = spring(
                                dampingRatio = Spring.DampingRatioMediumBouncy,
                                stiffness = Spring.StiffnessLow
                            )
                        )

                        val borderColor by animateColorAsState(
                            targetValue = if (isSelected) Theme.colors.brand.secondary else Color.Transparent
                        )

                        val backgroundColor by animateColorAsState(
                            targetValue = if (isSelected) Theme.colors.brand.tertiary else Theme.colors.background.bottomSheetCard
                        )
                        val iconColor by animateColorAsState(
                            targetValue = if (isSelected) Theme.colors.brand.primary else Theme.colors.shade.secondary
                        )

                        val iconBackground by animateColorAsState(
                            targetValue = if (isSelected) Theme.colors.brand.secondary else Theme.colors.shade.quaternary
                        )

                        LaunchedEffect(isPressed) {
                            if (isPressed) {
                                delay(100)
                                isPressed = false
                            }
                        }

                        Row(
                            modifier = modifier
                                .scale(scale)
                                .fillMaxWidth()
                                .clip(shape = RoundedCornerShape(Theme.radius.large))
                                .background(color = backgroundColor)
                                .border(
                                    width = 1.dp, color = borderColor, shape = RoundedCornerShape(Theme.radius.large)
                                )
                                .clickable {
                                    isPressed = true
                                    onClick()
                                }
                                .padding(12.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                            Box(
                                modifier = Modifier
                                    .size(32.dp)
                                    .background(
                                        color = iconBackground, shape = RoundedCornerShape(Theme.radius.medium)
                                    )
                                    .padding(8.dp), contentAlignment = Alignment.Center
                            ) {
                                Icon(
                                    painter = imageRes,
                                    contentDescription = "preference icon",
                                    tint = iconColor,
                                    modifier = Modifier.size(16.dp)

                                )
                            }
                            Column(
                                verticalArrangement = Arrangement.Center
                            ) {
                                Text(
                                    text = name,
                                    color = if (isSelected) Theme.colors.brand.primary else Theme.colors.shade.primary,
                                    style = Theme.textStyle.body.medium.medium
                                )
                                Spacer(modifier = Modifier.height(4.dp))
                                Text(
                                    text = description,
                                    color = Theme.colors.shade.secondary,
                                    style = Theme.textStyle.body.small.regular
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //OnboardingNavigationBar Button Left and Right Animation
                    
                    @Composable
                    fun BoxScope.OnboardingNavigationBar(
                        currentPageIndex: Int,
                        totalPages: Int,
                        pagerState: PagerState,
                        interactionListener: OnboardingInteractionListener,
                        coroutineScope: CoroutineScope
                    ) {
                        Column(
                            modifier = Modifier
                                .align(Alignment.BottomCenter)
                                .fillMaxWidth()
                                .navigationBarsPadding()
                                .padding(start = 24.dp,end = 24.dp, bottom = 16.dp,top = 24.dp),
                            horizontalAlignment = Alignment.CenterHorizontally,
                        ) {
                            Box(
                                modifier = Modifier.fillMaxWidth(),
                                contentAlignment = Alignment.Center
                            ) {
                                val nextButtonTargetOffsetX by animateDpAsState(
                                    targetValue = if (currentPageIndex > 0) 38.dp else 0.dp,
                                    animationSpec = tween(600, easing = FastOutSlowInEasing),
                                    label = ""
                                )
                                val backButtonTargetOffsetX by animateDpAsState(
                                    targetValue = if (currentPageIndex > 0) -38.dp else 72.dp,
                                    animationSpec = tween(600, easing = FastOutSlowInEasing),
                                    label = ""
                                )
                                val backButtonAlpha by animateFloatAsState(
                                    targetValue = if (currentPageIndex > 0) 1f else 0f,
                                    animationSpec = tween(600, easing = FastOutSlowInEasing),
                                    label = ""
                                )

                                NavigationButton(
                                    icon = painterResource(id = R.drawable.ic__back_arrow),
                                    onClick = {
                                        if (currentPageIndex == 0) return@NavigationButton
                                        coroutineScope.launch {
                                            pagerState.animateScrollToPage(currentPageIndex - 1)
                                        }
                                        interactionListener.onPreviousPageClicked()
                                    },
                                    modifier = Modifier
                                        .offset(x = backButtonTargetOffsetX)
                                        .graphicsLayer {
                                            alpha = backButtonAlpha
                                        }
                                )

                                NavigationButton(
                                    icon = painterResource(id = R.drawable.ic_arrow_right),
                                    onClick = {
                                        if (currentPageIndex == totalPages - 1) {
                                            interactionListener.onGetStartedClicked()
                                        } else {
                                            coroutineScope.launch {
                                                pagerState.animateScrollToPage(currentPageIndex + 1)
                                            }
                                            interactionListener.onNextPageClicked()
                                        }
                                    },
                                    modifier = Modifier.offset(x = nextButtonTargetOffsetX)
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Toggle Button Grid and List Animation Border 
                    
                    enum class ViewMode {
                        GRID, LIST
                    }
                    
                    @Composable
                    fun ViewModeToggleButton(
                        selectedMode: ViewMode,
                        onModeSelected: (ViewMode) -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        val indicatorOffsetX by animateDpAsState(
                            targetValue = when (selectedMode) {
                                ViewMode.GRID -> 0.dp
                                ViewMode.LIST -> 40.dp
                            },
                            animationSpec = spring(
                                dampingRatio = Spring.DampingRatioMediumBouncy,
                                stiffness = Spring.StiffnessLow
                            ),
                        )

                        Box(
                            modifier = modifier
                                .width(80.dp)
                                .height(40.dp)
                        ) {
                            Box(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .background(
                                        color = Theme.colors.background.card,
                                        shape = RoundedCornerShape(Theme.radius.small)
                                    )
                                    .border(
                                        width = 1.dp,
                                        color = Theme.colors.stroke.primary,
                                        shape = RoundedCornerShape(Theme.radius.small)
                                    )
                            )

                            Box(
                                modifier = Modifier
                                    .offset(x = indicatorOffsetX)
                                    .width(40.dp)
                                    .height(40.dp)
                                    .background(
                                        color = Theme.colors.brand.tertiary,
                                        shape = RoundedCornerShape(Theme.radius.small)
                                    )
                                    .border(
                                        width = 1.dp,
                                        color = Theme.colors.brand.secondary,
                                        shape = RoundedCornerShape(Theme.radius.small)
                                    )
                            )
                            Row(
                                modifier = Modifier.fillMaxSize(),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                IconButton(
                                    modifier = Modifier.weight(1f),
                                    onClick = { onModeSelected(ViewMode.GRID) }
                                ) {
                                    Image(
                                        painter = painterResource(
                                            if (selectedMode == ViewMode.GRID) R.drawable.grid_view_active
                                            else R.drawable.grid_view_not_active
                                        ),
                                        contentDescription = stringResource(R.string.grid_view_selected)
                                    )
                                }

                                IconButton(
                                    modifier = Modifier.weight(1f),
                                    onClick = { onModeSelected(ViewMode.LIST) }
                                ) {
                                    Image(
                                        painter = painterResource(
                                            if (selectedMode == ViewMode.LIST) R.drawable.list_view_active
                                            else R.drawable.list_view_not_active
                                        ),
                                        contentDescription = stringResource(R.string.list_view_active)
                                    )
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Hide and Show Code
                    
                    AnimatedVisibility(
                        visible = ,
                        enter = fadeIn(tween(400)) + expandVertically(tween(400)) + scaleIn(tween(400)),
                        exit = fadeOut(tween(200)) + shrinkVertically(tween(200)) + scaleOut(tween(200))
                    ) {
                        val codeAlpha by animateFloatAsState(
                            targetValue = if (timeRemaining < 10) 0.8f else 1f,
                            animationSpec = tween(300)
                        )

                        Text(
                            text = currentCode,
                            style = MaterialTheme.typography.headlineMedium,
                            fontWeight = FontWeight.Bold,
                            color = theme.primary.copy(alpha = codeAlpha),
                            fontFamily = FontFamily.Monospace,
                            modifier = Modifier.animateContentSize()
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val animatedAlpha by animateFloatAsState(
                        targetValue = 1f,
                        animationSpec = tween(durationMillis = 500), label = ""
                    )

                    AnimatedVisibility(
                        visible = animatedAlpha == 1f,
                        enter = fadeIn() + slideInHorizontally(),
                        exit = fadeOut() + slideOutVertically()
                    ) { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                     //Slide in Horizontally and out Horizontally
                     AnimatedVisibility(
                        visible = isOpen,
                        enter = slideInHorizontally() + fadeIn(),
                        exit = slideOutHorizontally() + fadeOut()
                    ) {
                        Box(
                            Modifier
                                .size(150.dp)
                                .background(Color.Blue)
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val expanded = rememberSaveable { mutableStateOf(false) }
                    val arrowRotation by animateFloatAsState(
                        targetValue = if (expanded.value) 180f else 0f,
                        label = "dropdown-arrow"
                    )

                    Icon(
                        modifier = Modifier
                            .rotate(arrowRotation)
                            .padding(horizontal = 20.dp),
                        imageVector = Icons.Filled.KeyboardArrowDown,
                        //contentDescription = if() "Collapse" else "Expand",
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "updateTransition - (For Complex, Multi-Property Animations)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = ": Used when you need to coordinate multiple different animation properties across a single state change simultaneously. ",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A powerful API for animating multiple properties together. Defines a single state variable that affects multiple animated properties. updateTransition creates an internal animation controller. Monitors state changes and applies animations simultaneously. Can animate multiple UI properties in sync (size, color, rotation, etc.). transitionSpec is used with AnimatedContent to define how the animation should happen when content changes. It lets you specify enter + exit transitions together based on the initialState and targetState.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Used when you have a complex component that has multiple distinct visual states (e.g., a \"Download Button\" that transforms from a wide rectangle to a small circle, while simultaneously changing color and swapping its internal icon).",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    enum class CardState { Collapsed, Expanded }
                    
                    @Composable
                    fun ToggleCard(expanded: Boolean) {
                        val state = if (expanded) CardState.Expanded else CardState.Collapsed
                        val transition = updateTransition(targetState = state, label = "card")
                        val size by transition.animateDp(label = "size") { s ->
                            if (s == CardState.Expanded) 200.dp else 100.dp
                        }
                        val color by transition.animateColor(label = "color") { s ->
                            if (s == CardState.Expanded) MaterialTheme.colorScheme.primary
                            else MaterialTheme.colorScheme.surfaceVariant
                        }
                        val corner by transition.animateDp(label = "corner") { s ->
                            if (s == CardState.Expanded) 16.dp else 32.dp
                        }

                        Box(
                            modifier = Modifier
                                .size(size)
                                .clip(RoundedCornerShape(corner))
                                .background(color)
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Like/Unlike (Follow/UnFollow)
                    
                    enum class LikeState { Idle, Liked }

                    @Composable
                    fun LikeButton(liked: Boolean, onClick: () -> Unit) {
                        val state = if (liked) LikeState.Liked else LikeState.Idle
                        val transition = updateTransition(targetState = state, label = "like")

                        val background by transition.animateColor(label = "bg") { s ->
                            if (s == LikeState.Liked) Color(0xFFE53935) else Color(0xFFF0F0F0)
                        }
                        val iconTint by transition.animateColor(label = "tint") { s ->
                            if (s == LikeState.Liked) Color.White else Color(0xFF555555)
                        }
                        val scale by transition.animateFloat(
                            label = "scale",
                            transitionSpec = {
                                if (LikeState.Idle isTransitioningTo LikeState.Liked)
                                    spring(dampingRatio = Spring.DampingRatioMediumBouncy)
                                else
                                    tween(durationMillis = 200)
                            }
                        ) { s ->
                            if (s == LikeState.Liked) 1.15f else 1f
                        }

                        Box(
                            modifier = Modifier
                                .size(56.dp)
                                .scale(scale)
                                .clip(CircleShape)
                                .background(background)
                                .clickable { onClick() },
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(
                                imageVector = Icons.Default.Favorite,
                                contentDescription = if (liked) "Unlike" else "Like",
                                tint = iconTint
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun TwoWaySwitch(
                        isProductSummary: Boolean,
                        onToggle: (Boolean) -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        val transition = updateTransition(isProductSummary, label = "SwitchTransition")

                        BoxWithConstraints(
                            modifier = modifier
                                .fillMaxWidth()
                                .padding(horizontal = 16.dp)
                                .height(48.dp)
                                .clip(RoundedCornerShape(24.dp))
                                .background(colorScheme.surface)
                                .border(1.dp, colorScheme.outline, RoundedCornerShape(24.dp))
                        ) {
                            val fullWidth = maxWidth
                            val halfWidth = fullWidth / 2

                            val indicatorOffset by transition.animateDp(label = "IndicatorOffset") { state ->
                                if (state) halfWidth else 0.dp
                            }

                            // Sliding indicator background
                            Box(
                                modifier = Modifier
                                    .offset(x = indicatorOffset)
                                    .width(halfWidth)
                                    .fillMaxHeight()
                                    .background(
                                        MaterialTheme.colorScheme.primary,
                                        shape = RoundedCornerShape(24.dp)
                                    )
                            )

                            Row(
                                modifier = Modifier.fillMaxSize(),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                // Demand button (left)
                                Box(
                                    modifier = Modifier
                                        .weight(1f)
                                        .clickable(enabled = isProductSummary) { onToggle(false) }
                                        .zIndex(if (!isProductSummary) 2f else 1f), // ensure text is above background
                                    contentAlignment = Alignment.Center
                                ) {
                                    Text(
                                        text = "📝",
                                        color = if (!isProductSummary) MaterialTheme.colorScheme.onPrimary
                                        else colorScheme.onSurface,
                                        fontWeight = FontWeight.Medium,
                                        maxLines = 1
                                    )
                                }

                                // roduct button (right)
                                Box(
                                    modifier = Modifier
                                        .weight(1f)
                                        .clickable(enabled = !isProductSummary) { onToggle(true) }
                                        .zIndex(if (isProductSummary) 2f else 1f),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Text(
                                        text = "🛍️",
                                        color = if (isProductSummary) colorScheme.onPrimary
                                        else colorScheme.onSurface,
                                        fontWeight = FontWeight.Medium,
                                        maxLines = 1
                                    )
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val isFavorite = remember { mutableStateOf(false) }
                    
                    val transition = updateTransition(
                        targetState = isFavorite.value,
                        label = "favoriteTransition"
                    )
                    
                    val scale by transition.animateFloat(label = "scale") {
                        if (it) 1.2f else 1f
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Add To Cart Animation

                    var addedToCart by remember { mutableStateOf(initialQuantity > 0) }
                    var quantity by remember { mutableStateOf(initialQuantity) }

                    val transition = updateTransition(targetState = addedToCart, label = "CartTransition")

                    val buttonWidth by transition.animateDp(label = "WidthAnim") { added ->
                        if (added) 160.dp else 40.dp
                    }
                    val buttonHeight by transition.animateDp(label = "HeightAnim") { 38.dp }

                    Surface(
                        elevation = 0.dp,
                        shape = if (addedToCart) RoundedCornerShape(12.dp) else RoundedCornerShape(24.dp),
                        color = AppColors.primaryMuted,
                        modifier = Modifier
                            .width(buttonWidth)
                            .height(buttonHeight)
                            .clickable() {

                            }
                    ) { }
                    """.trimIndent()
            )

            SpanText(
                text = "animateColorAsState",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    val backgroundColor by animateColorAsState(
                        if (selected) primaryBackground
                        else tileBackground,
                        ColorSpringSpec,
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                     val borderColor by animateColorAsState(
                        if (bookmarked) strokeHalf
                            else strokePale,
                            animationSpec = tween(1000),
                     )
                    """.trimIndent()
            )

            //https://github.com/FilipeGuerreiro/app-controle-de-caixa.git
            KotlinCode(
                code = """
                    //Tab Button 
                    
                    @Composable
                    fun TransactionTypeSelector(
                        selectedType: TransactionType,
                        onTypeSelected: (TransactionType) -> Unit
                    ) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(56.dp)
                                .clip(RoundedCornerShape(16.dp))
                                .background(MaterialTheme.colorScheme.surfaceVariant)
                                .padding(4.dp),
                            horizontalArrangement = Arrangement.spacedBy(4.dp)
                        ) {
                            TypeButton(
                                text = "Entrada",
                                isSelected = selectedType == TransactionType.INCOME,
                                selectedColor = MaterialTheme.financial.profitContainer,
                                selectedContentColor = MaterialTheme.financial.onProfitContainer,
                                onClick = { onTypeSelected(TransactionType.INCOME) },
                                modifier = Modifier.weight(1f)
                            )
                            TypeButton(
                                text = "Saída",
                                isSelected = selectedType == TransactionType.EXPENSE,
                                selectedColor = MaterialTheme.colorScheme.errorContainer,
                                selectedContentColor = MaterialTheme.colorScheme.onErrorContainer,
                                onClick = { onTypeSelected(TransactionType.EXPENSE) },
                                modifier = Modifier.weight(1f)
                            )
                        }
                    }
                    
                    @Composable
                    fun TypeButton(
                        text: String,
                        isSelected: Boolean,
                        selectedColor: Color,
                        selectedContentColor: Color,
                        onClick: () -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        val backgroundColor by animateColorAsState(
                            if (isSelected) selectedColor else Color.Transparent
                        )
                        val contentColor by animateColorAsState(
                            if (isSelected) selectedContentColor else MaterialTheme.colorScheme.onSurfaceVariant
                        )

                        Box(
                            modifier = modifier
                                .fillMaxSize()
                                .clip(RoundedCornerShape(12.dp))
                                .background(backgroundColor)
                                .clickable(onClick = onClick),
                            contentAlignment = Alignment.Center
                        ) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.Center
                            ) {
                                AnimatedVisibility(visible = isSelected) {
                                    Icon(
                                        imageVector = if (text == "Entrada") Icons.Default.ArrowUpward else Icons.Default.ArrowDownward,
                                        contentDescription = null,
                                        tint = contentColor,
                                        modifier = Modifier.padding(end = 8.dp).size(18.dp)
                                    )
                                }
                                Text(
                                    text = text,
                                    style = MaterialTheme.typography.labelLarge.copy(fontWeight = FontWeight.SemiBold),
                                    color = contentColor
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                     val interactionSource = remember { MutableInteractionSource() }
                     val isFocused by interactionSource.collectIsFocusedAsState()
    
                     val borderColor by animateColorAsState(
                        targetValue = if (isError) theme.borderError
                        else if (isFocused) theme.borderFocused
                        else Color.Transparent,
                        animationSpec = tween(durationMillis = 200)
                    )
                """.trimIndent()
            )

            KotlinCode(
                code = """
                     val backgroundColor by animateColorAsState(
                        if (primary) primaryBackground
                        else Color.Transparent
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "AnimatedVisibility",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A wrapper composable that animates visibility changes. Uses enter and exit transitions for fading, sliding, or expanding UI elements. When visibility changes, AnimatedVisibility triggers enter/exit transitions. Uses default animations like fadeIn(), slideInVertically(), etc. Handles composable lifecycle efficiently by removing UI when not visible.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/youndie/mani.git
            KotlinCode(
                code = """
                    //FloattingButton Animation
                    
                    @Composable
                    fun AddRuleFab(onClick: () -> Unit, modifier: Modifier = Modifier) {
                        FloatingActionButton(
                            onClick = onClick,
                            shape = RoundedCornerShape(16.dp),
                            containerColor = MaterialTheme.colorScheme.primaryContainer,
                            contentColor = MaterialTheme.colorScheme.onPrimaryContainer,
                            modifier = modifier.testTag("fab"),
                        ) {
                            Icon(
                                imageVector = Icons.Filled.Add,
                                contentDescription = "Add",
                            )
                        }
                    }
                    
                    @Composable
                    fun ManiApp(
                        modifier: Modifier = Modifier,
                      
                    ) {
                        Scaffold(
                            modifier = modifier.fillMaxSize(),
                            topBar = {
                               
                            },
                           
                            floatingActionButton = {
                                AnimatedVisibility(
                                    true,
                                    exit = fadeOut() + slideOut(targetOffset = {
                                        IntOffset(0, (it.height / 2f).roundToInt())
                                    }),
                                    enter = fadeIn() + slideIn(initialOffset = {
                                        IntOffset(0, (it.height / 2f).roundToInt())
                                    })
                                ) {
                                    AddRuleFab(
                                        onClick = { navController.navigate(ManiScreen.Add.name) },
                                        modifier = Modifier.navigationBarsPadding()
                                    )
                                }
                            },
                        ) { padding ->
                            
                           
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Right to Left Animation
                    
                    AnimatedVisibility(
                        visible = visible,
                        enter = fadeIn() + slideInHorizontally { it },
                        exit = fadeOut() + slideOutHorizontally { -it },
                    ) { }
                    """.trimIndent()
            )
            
            KotlinCode(
                code = """
                    // Bottom Sheet Overlay (Bottom Animation)
                    
                    AnimatedVisibility(
                        visible = state.selectedBody != null,
                        enter = slideInVertically(initialOffsetY = { it }) + fadeIn(),
                        exit = slideOutVertically(targetOffsetY = { it }) + fadeOut(),
                        modifier = Modifier
                            .align(Alignment.BottomCenter)
                            .padding(16.dp)
                    ) { }
                    """.trimIndent()
            )

            //https://github.com/Turskyi/MalaKnyzhka.git
            KotlinCode(
                code = """
                    //Drawer Animation 
                    
                    @Composable
                    fun DrawerPanel(
                        visible: Boolean
                    ) {
                        // Desired duration. 700 milliseconds.
                        val customAnimationDurationMillis = 700

                        // 📂 Drawer Panel.
                        AnimatedVisibility(
                            visible = visible,
                            enter = slideInHorizontally(
                                initialOffsetX = { -it },
                                animationSpec = tween(durationMillis = customAnimationDurationMillis)
                            ) + fadeIn(
                                animationSpec = tween(customAnimationDurationMillis),
                            ),
                            exit = slideOutHorizontally(
                                targetOffsetX = { -it },
                                animationSpec = tween(durationMillis = customAnimationDurationMillis)
                            ) + fadeOut(
                                animationSpec = tween(customAnimationDurationMillis),
                            )
                        ) {
                            val width: Dp = 312.dp

                            Box(
                                modifier = Modifier
                                    .width(width)
                                    .fillMaxHeight()
                            ) {
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/odaridavid/Smoovie-KMP.git
            KotlinCode(
                code = """
                     //Collapsed SearchBar/ToolBar Animation
                     
                     @OptIn(ExperimentalMaterial3Api::class)
                     @Composable
                     private fun MoviesContent(
                         state: MoviesScreenState,
                         actions: MovieActions,
                     ) {
                         var isSearchActive by rememberSaveable { mutableStateOf(false) }
                         var isFilterSheetVisible by remember { mutableStateOf(false) }
                         SearchBackHandler(enabled = isSearchActive) {
                             isSearchActive = false
                             actions.onSearchQueryChanged("")
                         }

                         val heroVisible =
                             !isSearchActive &&
                                 state.uiState !is MoviesUiState.Error &&
                                 (state.featuredMovies.isNotEmpty() || state.uiState is MoviesUiState.Loading)
                         SetStatusBarIcons(useDarkIcons = !isSystemInDarkTheme() && !heroVisible)

                         val listState = rememberLazyListState()
                         val animatedIds = remember { mutableSetOf<Int>() }

                         val successState = state.uiState as? MoviesUiState.Success
                         val firstMovieId = successState?.movies?.firstOrNull()?.id
                         LaunchedEffect(firstMovieId) { animatedIds.clear() }

                         val shouldLoadMore by remember {
                             derivedStateOf {
                                 val lastVisible =
                                     listState.layoutInfo.visibleItemsInfo
                                         .lastOrNull()
                                         ?.index
                                         ?: return@derivedStateOf false
                                 lastVisible >= listState.layoutInfo.totalItemsCount - 3
                             }
                         }
                         LaunchedEffect(shouldLoadMore) {
                             if (shouldLoadMore && successState?.hasMorePages == true && successState.isLoadingMore.not()) {
                                 actions.onLoadMore()
                             }
                         }

                         val background = MaterialTheme.colorScheme.background

                         Scaffold(
                             contentWindowInsets = WindowInsets(0),
                             topBar = {
                                 AnimatedContent(
                                     targetState = isSearchActive,
                                     transitionSpec = { 
                                         (fadeIn(tween(350)) togetherWith fadeOut(tween(300))).using(SizeTransform(clip = true))
                                     },
                                     label = "topbar",
                                 ) { searchActive ->
                                     if (searchActive) {
                                         SearchToolbar(
                                             query = state.searchQuery,
                                             onQueryChanged = actions.onSearchQueryChanged,
                                             onClose = {
                                                 isSearchActive = false
                                                 actions.onSearchQueryChanged("")
                                             },
                                         )
                                     } else if (state.uiState is MoviesUiState.Error ||
                                         (state.featuredMovies.isEmpty() && state.uiState !is MoviesUiState.Loading)
                                     ) {
                                         CollapsedToolbar(
                                             visible = true,
                                             isFilterActive = state.filterPreferences.isActive,
                                             onSearchClick = { isSearchActive = true },
                                             onFilterClick = { isFilterSheetVisible = true },
                                         )
                                     }
                                 }
                             },
                         ) { padding ->
                             Column(modifier = Modifier.padding(padding).fillMaxSize()) {
                                 AnimatedVisibility(
                                     visible = heroVisible,
                                     enter = expandVertically(tween(400)) + fadeIn(tween(400)),
                                     exit = shrinkVertically(tween(350)) + fadeOut(tween(300)),
                                     modifier =
                                         Modifier
                                             .fillMaxWidth()
                                             .layout { measurable, constraints ->
                                                 val placeable = measurable.measure(constraints)
                                                 val overlapPx = SHEET_OVERLAP.roundToPx()
                                                 val reportedHeight = (placeable.height - overlapPx).coerceAtLeast(0)
                                                 layout(placeable.width, reportedHeight) {
                                                     placeable.place(0, 0)
                                                 }
                                             },
                                 ) {
                                     AnimatedContent(
                                         targetState = state.featuredMovies.isNotEmpty(),
                                         transitionSpec = {
                                             fadeIn(tween(SLOW_ANIM_DURATION)) togetherWith fadeOut(tween(SLOW_ANIM_DURATION))
                                         },
                                         label = "featured",
                                     ) { hasFeatured ->
                                        
                                     }
                                 }
                                 Column(
                                     modifier =
                                         Modifier
                                             .fillMaxWidth()
                                             .weight(1f)
                                             .background(background, SheetShape)
                                             .padding(top = 12.dp),
                                 ) {
                                     AnimatedContent(
                                         targetState = state.uiState,
                                         transitionSpec = {
                                             fadeIn(tween(SLOW_ANIM_DURATION)) togetherWith fadeOut(tween(SLOW_ANIM_DURATION))
                                         },
                                         contentKey = { it::class },
                                         modifier = Modifier.weight(1f),
                                     ) { uiState ->
                                       
                                     }
                                 }
                             }
                         }
                     }
                     
                     @OptIn(ExperimentalMaterial3Api::class)
                     @Composable
                     internal fun CollapsedToolbar(
                         visible: Boolean,
                         isFilterActive: Boolean = false,
                         onSearchClick: () -> Unit,
                         onFilterClick: () -> Unit = {},
                     ) {
                         CenterAlignedTopAppBar(
                             title = {
                                 AnimatedVisibility(
                                     visible = visible,
                                     enter = fadeIn(tween(ANIMATION_DURATION_MS)) + slideInHorizontally(tween(ANIMATION_DURATION_MS)) { -it },
                                     exit = fadeOut(tween(ANIMATION_DURATION_MS)) + slideOutHorizontally(tween(ANIMATION_DURATION_MS)) { -it },
                                 ) {
                                     Text(
                                         text = stringResource(Res.string.app_name),
                                         style =
                                             TextStyle(
                                                 fontFamily = LobsterFontFamily,
                                                 fontSize = 28.sp,
                                             )
                                     )
                                 }
                             },
                             actions = {
                                 AnimatedVisibility(
                                     visible = visible,
                                     enter = fadeIn(tween(ANIMATION_DURATION_MS)) + slideInHorizontally(tween(ANIMATION_DURATION_MS)) { it },
                                     exit = fadeOut(tween(ANIMATION_DURATION_MS)) + slideOutHorizontally(tween(ANIMATION_DURATION_MS)) { it },
                                 ) {
                                     Row {
                                         IconButton(onClick = onSearchClick) {
                                             Icon(
                                                 imageVector = Icons.Default.Search,
                                                 contentDescription = stringResource(Res.string.search_movies_hint),
                                             )
                                         }
                                         IconButton(onClick = onFilterClick) {
                                             BadgedBox(
                                                 badge = { if (isFilterActive) Badge() },
                                             ) {
                                                 Icon(
                                                     imageVector = Icons.Default.Tune,
                                                     contentDescription = stringResource(Res.string.filter_button_description),
                                                 )
                                             }
                                         }
                                     }
                                 }
                             }
                         )
                     }
                     
                     @OptIn(ExperimentalMaterial3Api::class)
                     @Composable
                     internal fun SearchToolbar(
                         query: String,
                         onQueryChanged: (String) -> Unit,
                         onClose: () -> Unit,
                         placeholder: String = stringResource(Res.string.search_movies_hint),
                     ) {
                         val focusRequester = remember { FocusRequester() }
                         val keyboardController = LocalSoftwareKeyboardController.current

                         LaunchedEffect(Unit) {
                             focusRequester.requestFocus()
                         }

                         TopAppBar(
                             navigationIcon = {
                                 IconButton(onClick = onClose) {
                                     Icon(
                                         imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                                         contentDescription = null,
                                     )
                                 }
                             },
                             title = {
                                 TextField(
                                     value = query,
                                     onValueChange = onQueryChanged,
                                     placeholder = { Text(placeholder) },
                                     singleLine = true,
                                     keyboardOptions = KeyboardOptions(imeAction = ImeAction.Search),
                                     keyboardActions =
                                         KeyboardActions(
                                             onSearch = { keyboardController?.hide() },
                                         ),
                                     colors =
                                         TextFieldDefaults.colors(
                                             focusedContainerColor = Color.Transparent,
                                             unfocusedContainerColor = Color.Transparent,
                                             focusedIndicatorColor = Color.Transparent,
                                             unfocusedIndicatorColor = Color.Transparent,
                                         ),
                                     modifier =
                                         Modifier
                                             .fillMaxWidth()
                                             .focusRequester(focusRequester),
                                 )
                             },
                             actions = {
                                 if (query.isNotEmpty()) {
                                     IconButton(onClick = { onQueryChanged("") }) {
                                         Icon(
                                             imageVector = Icons.Default.Close,
                                             contentDescription = null,
                                         )
                                     }
                                 }
                             }
                         )
                     }

                     @PreviewLightDark
                     @Composable
                     private fun SearchToolbarPreview() {
                         SmoovieTheme {
                             SearchToolbar(query = "", onQueryChanged = {}, onClose = {})
                         }
                     }
                """.trimIndent()
            )

            //https://github.com/Dev-Aditya-More/PokeVerse.git
            KotlinCode(
                code = """
                    //Search Suggestion
                    
                    AnimatedVisibility(
                        visible = ,
                        enter = fadeIn() + expandVertically(),
                        exit = fadeOut() + shrinkVertically()
                    ) { }
                    """.trimIndent()
            )

            //https://github.com/Dev-Aditya-More/PokeVerse.git
            KotlinCode(
                code = """
                    //Filter Animation Top to Bottom
                    
                    var showFilters by rememberSaveable { mutableStateOf(false) }
                    
                         AnimatedVisibility(
                             visible = showFilters,
                             enter = slideInVertically(
                                 initialOffsetY = { -it / 2 }
                             ) + fadeIn(
                                 animationSpec = tween(
                                     durationMillis = 250,
                                     easing = FastOutSlowInEasing
                                 )
                             ),
                             exit = slideOutVertically(
                                 targetOffsetY = { -it / 2 }
                             ) + fadeOut(
                                 animationSpec = tween(
                                     durationMillis = 200,
                                     easing = FastOutSlowInEasing
                                 )
                             )
                         ) {
                         }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun AnimatedAppear(
                        modifier: Modifier = Modifier,
                        delayMillis: Int = 0,
                        content: @Composable () -> Unit,
                    ) {
                        val state = remember {
                            MutableTransitionState(initialState = false).apply { targetState = true }
                        }
                        AnimatedVisibility(
                            visibleState = state,
                            enter = fadeIn(tween(durationMillis = 250, delayMillis = delayMillis)) +
                                slideInVertically(tween(durationMillis = 250, delayMillis = delayMillis)) { it / 8 },
                            modifier = modifier,
                        ) {
                            content()
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                     AnimatedVisibility(
                        visible = isVisible, 
                        enter = fadeIn(spring(stiffness = Spring.StiffnessVeryLow)) + slideInVertically { 50 }) 
                        {  }
                     )
                    """.trimIndent()
            )

            //https://github.com/mofeejegi/compose-alert-banner.git
            KotlinCode(
                code = """
                    //Top to Bottom Animation
                    
                    fun <T> animationSpec(): FiniteAnimationSpec<T> = spring(
                        dampingRatio = Spring.DampingRatioNoBouncy,
                        stiffness = Spring.StiffnessLow,
                    )
                    
                    AnimatedVisibility(
                        visible = ,
                        enter = expandVertically(animationSpec = animationSpec())
                            + slideInVertically(animationSpec = animationSpec()) { -it },
                        exit = shrinkVertically(shrinkTowards = Alignment.CenterVertically, animationSpec = animationSpec())
                            + fadeOut(animationSpec = tween(durationMillis = 200, easing = EaseInOut))
                            + scaleOut(animationSpec = tween(easing = EaseInOut)),
                    ) {}
                    """.trimIndent()
            )

            //https://github.com/julesmeister/deutschcraft-compose-multiplatform.git
            KotlinCode(
                code = """
                    fun <T> fadeTween(durationMillis: Int = AnimationSpecs.DEFAULT_DURATION) = tween<T>(
                        durationMillis = durationMillis,
                        easing = FastOutSlowInEasing
                    )

                    /**
                     * Returns a tween spec with easing for slide animations.
                     */
                    fun <T> slideTween(durationMillis: Int = AnimationSpecs.DEFAULT_DURATION) = tween<T>(
                        durationMillis = durationMillis,
                        easing = EaseOutQuart
                    )

                    AnimatedVisibility(
                        visible = isGenerating || currentSuggestion.isNotEmpty() || suggestions.isNotEmpty(),
                        enter = expandVertically(
                            animationSpec = spring(stiffness = Spring.StiffnessMedium),
                            expandFrom = Alignment.Top
                        ) + fadeIn(animationSpec = fadeTween()),
                        exit = shrinkVertically(
                            animationSpec = tween(200),
                            shrinkTowards = Alignment.Top
                        ) + fadeOut(animationSpec = tween(200))
                    ) { }
                    """.trimIndent()
            )

            //https://github.com/nana-adrien/Portfolio.git
            KotlinCode(
                code = """
                    //Slide Animation Color 
                    
                    @OptIn(ExperimentalComposeUiApi::class)
                    @Composable
                     fun ProjectItem(
                        isPrivate: Boolean = false,
                        title: String,
                        description: String,
                        image: String?,
                        demoLink: String? = null,
                        githubLink: String? = null,
                        previewLink: String? = null,
                        modifier: Modifier = Modifier,
                        onLinkClick: (String) -> Unit,
                    ) {
                        val isMobileDevice = currentDeviceConfigure().isMobileDevice()
                        val interactionSource = remember { MutableInteractionSource() }
                        val enabledLinkBox2 by interactionSource.collectIsHoveredAsState()

                        Column(
                            modifier = modifier
                                .widthIn(min = if (isMobileDevice) 300.dp else 200.dp, max = if (isMobileDevice) 350.dp else 300.dp)
                                .height(if (isMobileDevice) 270.dp else 270.dp)
                                .clip(RoundedCornerShape(8.dp))
                                .border(1.dp, MaterialTheme.colorScheme.background.copy(alpha = 0.3f), RoundedCornerShape(8.dp))
                                .background(MaterialTheme.colorScheme.background)
                                .padding(15.dp),
                            verticalArrangement = Arrangement.spacedBy(10.dp),
                        )
                        {
                            Box(
                                modifier = Modifier
                                    .fillMaxHeight(0.7f)
                                    .fillMaxWidth()
                                    .hoverable(
                                        interactionSource = interactionSource,
                                    )
                                    .clip(RoundedCornerShape(8.dp)),
                            ) {

                                image?.let {
                                    AsyncImage(
                                        modifier = Modifier.fillMaxSize(),
                                        model = image,
                                        contentDescription = null,
                                        contentScale = ContentScale.Inside,
                                    )
                                }
                                this@Column.AnimatedVisibility(
                                    visible = enabledLinkBox2,
                                    enter = fadeIn() + expandHorizontally(),
                                    exit = fadeOut() + shrinkHorizontally(),
                                ) {
                                    Box(
                                        modifier = Modifier.fillMaxSize().background(Color.Black.copy(alpha = 0.7f)),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Row(
                                            modifier = Modifier.fillMaxWidth(),
                                            horizontalArrangement = Arrangement.spacedBy(10.dp, alignment = Alignment.CenterHorizontally),
                                            verticalAlignment = Alignment.CenterVertically,
                                        ) {
                                            if (isPrivate) {
                                                ProjectLinkButton(
                                                    model = Icons.Default.Security,
                                                ) {

                                                }
                                            } else {
                                                previewLink?.let {
                                                    ProjectLinkButton(
                                                        model = Icons.Default.Preview,
                                                    ) {
                                                        onLinkClick(it)
                                                    }
                                                }
                                                githubLink?.let {
                                                    ProjectLinkButton(
                                                        model = Res.getUri("drawable/github.png")
                                                    ) {
                                                        onLinkClick(it)
                                                    }
                                                }
                                                demoLink?.let {
                                                    ProjectLinkButton(
                                                        model = Icons.Default.Videocam,
                                                    ) {
                                                        onLinkClick(it)
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            Text(
                                modifier = Modifier.fillMaxWidth(),
                                text = title,
                                style = MaterialTheme.typography.titleSmall.let { it.copy(fontWeight = FontWeight.Bold) },
                                color = MaterialTheme.colorScheme.onBackground,
                                overflow = TextOverflow.Ellipsis,
                                maxLines = 1,
                            )
                            Text(
                                modifier = Modifier.fillMaxWidth(),
                                text = description,
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.onBackground.copy(0.7f),
                                maxLines = 2,
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Animation slide left - right 
                    
                    AnimatedVisibility(
                        visible = isVisible,
                        enter = slideInHorizontally(
                            initialOffsetX = { -it }, // Slide in from left
                            animationSpec = tween(durationMillis = 1500)
                        )
                    ) {

                    }

                    AnimatedVisibility(
                        visible = isVisible,
                        enter = slideInHorizontally(
                            initialOffsetX = { it }, // Slide in from right
                            animationSpec = tween(durationMillis = 1500)
                        )
                    ) {

                    }
                    """.trimIndent()
            )
            //https://github.com/stoyan-vuchev/magic-message.git
            KotlinCode(
                code = """
                    // Button Animation 
                    
                    AnimatedVisibility(
                        visible = ,
                        enter = fadeIn() + scaleIn(initialScale = .8f),
                        exit = fadeOut() + scaleOut(targetScale = .8f)
                    ) { }
                    """.trimIndent()
            )

            //https://github.com/surajpsp/CustomList.git
            KotlinCode(
                code = """
                    AnimatedVisibility(
                        visible = visible,
                        enter = slideInVertically(
                            initialOffsetY = { it / 2 }, //
                                animationSpec = spring(
                                    dampingRatio = Spring.DampingRatioMediumBouncy,
                                    stiffness = Spring.StiffnessLow
                                )
                            ) + fadeIn(
                                animationSpec = tween(
                                    durationMillis = 600,
                                    easing = LinearOutSlowInEasing
                                )
                            ),
                            exit = fadeOut(
                                animationSpec = tween(
                                    durationMillis = 400,
                                    easing = FastOutLinearInEasing
                                )
                            )
                        ) {
                           
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @OptIn(ExperimentalMaterial3ExpressiveApi::class)
                    @Composable
                    fun AppCheckBox(
                        modifier: Modifier = Modifier,
                        checked: Boolean,
                        size : Dp = 26.dp,
                        selectedCheckColor: Color = MaterialTheme.colorScheme.primary,
                        onCheckChange: (Boolean) -> Unit,
                    ) {
                        val containerColor by animateColorAsState(
                            targetValue = if (checked) selectedCheckColor else MaterialTheme.colorScheme.surfaceContainer,
                            animationSpec = MaterialTheme.motionScheme.fastEffectsSpec(),
                            label = "containerColor"
                        )
                        val borderColor by animateColorAsState(
                            targetValue = if (checked) selectedCheckColor else MaterialTheme.colorScheme.surfaceVariant,
                            animationSpec = MaterialTheme.motionScheme.fastEffectsSpec(),
                            label = "containerColor"
                        )
                        Box(
                            modifier = modifier
                                .size(size)
                                .clip(CircleShape)
                                .border(width = 1.dp, color = borderColor, shape = CircleShape)
                                .background(color = containerColor, shape = CircleShape)
                                .clickable {
                                    onCheckChange(!checked)
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            AnimatedVisibility(
                                visible = checked,
                                enter = scaleIn(animationSpec = MaterialTheme.motionScheme.fastEffectsSpec()) + fadeIn(
                                    animationSpec = MaterialTheme.motionScheme.fastEffectsSpec()
                                ),
                                exit = scaleOut(animationSpec = MaterialTheme.motionScheme.fastEffectsSpec()) + fadeOut(
                                    animationSpec = MaterialTheme.motionScheme.fastEffectsSpec()
                                ), modifier = Modifier.align(Alignment.Center)
                            ) {
                                Icon(
                                    imageVector = ImageVector.vectorResource(R.drawable.ic_check),
                                    contentDescription = null,
                                    modifier = Modifier.size(22.dp),
                                    tint = MaterialTheme.colorScheme.onPrimary
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/vahitkeskin/Equatix.git
            KotlinCode(
                code = """
                     //Right to Left Dialog Animation
                     
                     @Composable
                     fun GlassBox(
                         modifier: Modifier = Modifier,
                         cornerRadius: Dp = 24.dp,
                         content: @Composable BoxScope.() -> Unit
                     ) {
                         Box(
                             modifier = modifier
                                 .clip(RoundedCornerShape(cornerRadius))
                                 .background(
                                     Brush.linearGradient(
                                         colors = listOf(
                                             Color.White.copy(alpha = 0.15f),
                                             Color.White.copy(alpha = 0.05f)
                                         )
                                     )
                                 )
                                 .border(
                                     1.dp,
                                     Brush.linearGradient(
                                         colors = listOf(
                                             Color.White.copy(alpha = 0.3f),
                                             Color.Transparent
                                         )
                                     ),
                                     RoundedCornerShape(cornerRadius)
                                 ),
                             content = content
                         )
                     }
                     
                     @Composable
                     fun HomeOverlayPanel(
                         onDismiss: () -> Unit,
                     ) {
                           val overlayDimColor = if (isDark) Color.Black.copy(0.85f) else Color.Black.copy(0.4f)
                           val strings by viewModel.strings.collectAsState()

                           var lastActiveOverlay by remember { mutableStateOf(overlayType) }

                           if (overlayType != null) {
                               lastActiveOverlay = overlayType
                           }

                           val isSettings = lastActiveOverlay == HomeScreen.OverlayType.SETTINGS
                           
                           val enterTransition = if (isSettings) {
                               slideInHorizontally(initialOffsetX = { it }) + fadeIn()
                           } else {
                               slideInHorizontally(initialOffsetX = { -it }) + fadeIn()
                           }

                           val exitTransition = if (isSettings) {
                               slideOutHorizontally(targetOffsetX = { it }) + fadeOut()
                           } else {
                               slideOutHorizontally(targetOffsetX = { -it }) + fadeOut()
                           }

                           Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                               AnimatedVisibility(
                                   visible = overlayType != null,
                                   enter = fadeIn(),
                                   exit = fadeOut(),
                                   modifier = Modifier.matchParentSize()
                               ) {
                                   Box(
                                       modifier = Modifier
                                           .fillMaxSize()
                                           .background(overlayDimColor)
                                           .clickable(enabled = false) {}
                                           .clickable { onDismiss() }
                                   )
                               }

                               AnimatedVisibility(
                                   visible = overlayType != null,
                                   enter = enterTransition,
                                   exit = exitTransition,
                                   modifier = Modifier.align(Alignment.Center)
                               ) {
                                   val overlay = overlayType ?: return@AnimatedVisibility

                                   GlassBox(
                                       modifier = Modifier
                                           .fillMaxWidth(0.9f)
                                           .wrapContentHeight()
                                           .padding(16.dp)
                                           .shadow(
                                               elevation = if (isDark) 0.dp else 16.dp,
                                               shape = RoundedCornerShape(24.dp)
                                           ),
                                       cornerRadius = 24.dp
                                   ) {
                                       Column(
                                           modifier = Modifier
                                               .background(colors.cardBackground)
                                               .padding(24.dp)
                                       ) {
                                           Row(
                                               modifier = Modifier.fillMaxWidth(),
                                               horizontalArrangement = Arrangement.SpaceBetween,
                                               verticalAlignment = Alignment.CenterVertically
                                           ) {
                                               val title = if (overlay == HomeScreen.OverlayType.HISTORY)
                                                   strings.scoresTitle
                                               else
                                                   strings.settingsTitle

                                               Text(
                                                   text = title,
                                                   style = MaterialTheme.typography.displaySmall.copy(
                                                       fontWeight = FontWeight.Black,
                                                       color = colors.textPrimary,
                                                       fontSize = 28.sp
                                                   )
                                               )
                                               IconButton(onClick = onDismiss) {
                                                   Icon(
                                                       imageVector = Icons.Default.Close,
                                                       contentDescription = strings.close,
                                                       tint = colors.textSecondary,
                                                       modifier = Modifier.size(32.dp)
                                                   )
                                               }
                                           }

                                           HorizontalDivider(
                                               color = colors.divider,
                                               modifier = Modifier.padding(vertical = 20.dp)
                                           )

                                           when (overlay) {
                                               HomeScreen.OverlayType.HISTORY -> HistoryList(
                                                   viewModel,
                                                   colors,
                                                   isDark,
                                                   strings
                                               )

                                               HomeScreen.OverlayType.SETTINGS -> SettingsList(
                                                   viewModel,
                                                   isDark,
                                                   colors
                                               )
                                           }
                                       }
                                   }
                               }
                           }
                     }
                    """.trimIndent()
            )

            //https://github.com/arifamsar/iFlix.git
            KotlinCode(
                code = """
                    //Up to Down Visibility
                    
                    AnimatedVisibility(
                        visible = showFilters,
                        enter = expandVertically() + fadeIn(),
                        exit = shrinkVertically() + fadeOut()
                        
                        //enter = expandVertically(expandFrom = Alignment.Top),
                        //exit = shrinkVertically(shrinkTowards = Alignment.Top)
                    ) { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Down to Up Visibility
                    
                    AnimatedVisibility(
                        visible = showFilters,
                        enter = slideInVertically { it },
                        exit = slideOutVertically { it }
                    ) { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    var visible by remember { mutableStateOf(connectionState != ConnectionState.Available) }

                    // Auto-hide when state is Available
                    LaunchedEffect(connectionState) {
                        if (connectionState == ConnectionState.Available) {
                            visible = true
                            delay(2000)
                            visible = false
                        } else {
                            visible = true
                        }
                    }

                    AnimatedVisibility(
                        visible = visible,
                        enter = fadeIn() + slideInVertically(initialOffsetY = { -40 }),
                        exit = fadeOut() + slideOutVertically(targetOffsetY = { -40 }),
                        modifier = modifier
                            .fillMaxWidth()
                            .padding(top = 10.dp)
                   ) { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    private fun ShowMapLoadingProgressBar(
                        visible: Boolean = false
                    ) {
                        AnimatedVisibility(
                            modifier = Modifier.fillMaxSize(),
                            visible = visible,
                            enter = EnterTransition.None,
                            exit = fadeOut(),
                        ) {
                            CircularProgressIndicator(
                                modifier = Modifier
                                    .background(MaterialTheme.colorScheme.background)
                                    .wrapContentSize()
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    AnimatedVisibility(
                        visible = visibility,
                        enter = slideInHorizontally()
                            + expandHorizontally(expandFrom = Alignment.Start)
                            + scaleIn(transformOrigin = TransformOrigin(0.5f, 0f))
                            + fadeIn(initialAlpha = 0.3f),
                    ) {
                    
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //CheckBox Animation Icon
                    
                    AnimatedVisibility(
                        isSelected,
                        enter = scaleIn(animationSpec = MaterialTheme.motionScheme.fastEffectsSpec()),
                        exit = scaleOut(animationSpec = MaterialTheme.motionScheme.fastEffectsSpec())
                    ) {
                        //Icon of Image
                    }
                    """.trimIndent()
            )

            //https://github.com/TirexSG/ExpenseTracker.git
            KotlinCode(
                code = """
                    //LazyColumn Animation Left to Right
                    
                    @Composable
                    fun GroupedExpenseItem() {
                        AnimatedVisibility(
                            visible = visible,
                            enter = slideInHorizontally(
                                initialOffsetX = { -it / 2 },
                                animationSpec = tween(300)
                            ) + fadeIn(animationSpec = tween(300)),
                            exit = slideOutHorizontally(
                                targetOffsetX = { it / 2 },
                                animationSpec = tween(240)
                            ) + fadeOut(animationSpec = tween(160))
                        ) {
                           
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/TinaT2/TimerzeerKMP.git
            KotlinCode(
                code = """
                    @Composable
                    fun SmoothSwitchTabFadeAnimatedVisibility(visible: Boolean, content: @Composable () -> Unit) =
                        AnimatedVisibility(
                            visible,
                            enter = fadeIn(
                                animationSpec = tween(durationMillis = 2000),
                                initialAlpha = 1f
                            ),
                            exit = fadeOut(
                                animationSpec = tween(durationMillis = 2000),
                                targetAlpha = 1f
                            )
                        ) {
                            content.invoke()
                        }

                    @Composable
                    fun SmoothFieldFadeAnimatedVisibility(visible: Boolean, content: @Composable () -> Unit) =
                        AnimatedVisibility(
                            visible,
                            enter = fadeIn(
                                animationSpec = tween(durationMillis = 500)
                            ),
                            exit = fadeOut(
                                animationSpec = tween(durationMillis = 500)
                            )
                        ) {
                            content.invoke()
                        }

                    @Composable
                    fun SmoothStartUpAnimation(visible: Boolean, content: @Composable () -> Unit) =
                        AnimatedVisibility(
                            visible,
                            enter = fadeIn(
                                animationSpec = tween(durationMillis = 1000),
                                initialAlpha = 0.3f
                            ),
                            exit = fadeOut(
                                animationSpec = tween(durationMillis = 1000),
                                targetAlpha = 1f
                            )
                        ) {
                            content.invoke()
                        }

                    @Composable
                    fun <S> SmoothAnimatedContent(
                        targetState: S,
                        modifier: Modifier = Modifier,
                        content: @Composable (S) -> Unit
                    ) {
                        AnimatedContent(targetState, modifier = modifier, transitionSpec = {
                            (fadeIn(animationSpec = tween(500)))
                                .togetherWith(fadeOut(animationSpec = tween(500)))
                        }) {
                            content.invoke(it)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Down to Up
                    
                    AnimatedVisibility(
                        visible = ,
                        enter = fadeIn(animationSpec = tween(800)) + slideInVertically(
                            animationSpec = tween(800),
                            initialOffsetY = { it / 2 }
                        )
                        
                        //enter = slideInVertically(initialOffsetY = { it * 2 }),
                        //exit = slideOutVertically(targetOffsetY = { it * 2 }),
                    ) { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Visible in/out Animation
                    
                    @Composable
                    fun DiaryHeaderAdvanced() {
                        var isVisible by remember { mutableStateOf(false) }

                        LaunchedEffect(Unit) {
                            delay(0) 
                            isVisible = true
                        }

                        AnimatedVisibility(
                            visible = isVisible,
                            enter = fadeIn(
                                animationSpec = tween(1000, easing = FastOutSlowInEasing)
                            ) + slideInVertically(
                                initialOffsetY = { it / 3 },
                                animationSpec = tween(1000, easing = FastOutSlowInEasing)
                            ) + scaleIn(
                                initialScale = 0.95f,
                                animationSpec = tween(1000, easing = FastOutSlowInEasing)
                            )
                        ) {
                        
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //BottomBar Animation
                    
                    AnimatedVisibility(
                        visible = visible,
                        enter =
                            slideInVertically(
                                animationSpec = tween(600),
                                initialOffsetY = { it },
                            ),
                        exit =
                            slideOutVertically(
                                animationSpec = tween(600),
                                targetOffsetY = { it },
                            ),
                    ) { }
                    """.trimIndent()
            )

            //https://github.com/Amsterdam-Team/Aflami.git
            KotlinCode(
                code = """
                    //Error Message Animation Top to Bottom
                    
                    AnimatedVisibility(
                        visible = true,
                            enter = slideInVertically(
                                animationSpec = tween(600),
                                initialOffsetY = { -it },
                            ),
                        modifier = Modifier.padding(top = 12.dp)
                    ) { }
                    
                    //Bottom To Top
                    AnimatedVisibility(
                        visible = earnedPoint,
                        enter = expandIn(
                            expandFrom = Alignment.TopCenter,
                            clip = false,
                            animationSpec = tween(1000)
                        ) + fadeIn(animationSpec = tween(1000))
                    ) { }
                    
                    //Handle return
                     if (earnedPoint == null) return@AnimatedVisibility
                     
                     //Left To Right
                     @Composable
                     fun BoxScope.AnimatedSkipText(
                         modifier: Modifier = Modifier, isVisible: Boolean, onClick: () -> Unit
                     ) {
                         val layoutDirection = LocalLayoutDirection.current
                         AnimatedVisibility(
                             modifier = Modifier
                                 .zIndex(10f)
                                 .statusBarsPadding()
                                 .padding(start = 16.dp, top = 16.dp)
                                 .clickable(
                                     indication = null,
                                     interactionSource = remember { MutableInteractionSource() },
                                     onClick = onClick
                                 )
                                 .align(Alignment.TopStart),
                             visible = isVisible,
                             enter = slideInHorizontally(
                                 tween(durationMillis = 1000), initialOffsetX = {
                                     if (layoutDirection == LayoutDirection.Rtl) it + 1000 else it - 1000
                                 }),
                             exit = slideOutHorizontally(tween(durationMillis = 1000), targetOffsetX = {
                                 if (layoutDirection == LayoutDirection.Rtl) it + 1000 else it - 1000
                             }),
                         ) {
                             Text(
                                 modifier = modifier, text = stringResource(
                                     id = R.string.skip
                                 ), style = AppTheme.textStyle.label.medium, color = AppTheme.color.primary
                             )
                         }
                     }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //AlertDialog Animation
                    @Composable
                    fun ThemeSettingsDialog(
                        isVisible: Boolean,
                        currentTheme: ThemeMode,
                        onThemeSelected: (ThemeMode) -> Unit,
                        onDismiss: () -> Unit
                    ) {
                        AnimatedVisibility(
                            visible = isVisible,
                            enter = fadeIn(
                                animationSpec = tween(
                                    durationMillis = 300,
                                    easing = FastOutSlowInEasing
                                )
                            ) + scaleIn(
                                initialScale = 0.8f,
                                animationSpec = tween(
                                    durationMillis = 300,
                                    easing = FastOutSlowInEasing
                                )
                            ),
                            exit = fadeOut(
                                animationSpec = tween(
                                    durationMillis = 250,
                                    easing = FastOutLinearInEasing
                                )
                            ) + scaleOut(
                                targetScale = 0.8f,
                                animationSpec = tween(
                                    durationMillis = 250,
                                    easing = FastOutLinearInEasing
                                )
                            )
                        ) {
                            //AlertDialog
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        AnimatedVisibility(
                            visible = itemVisible,
                            enter = slideInVertically(
                                initialOffsetY = { it },
                                animationSpec = tween(
                                    durationMillis = 600,
                                    easing = FastOutSlowInEasing
                                )
                            ) + fadeIn(
                                animationSpec = tween(
                                    durationMillis = 600,
                                    easing = FastOutSlowInEasing
                                )
                            ),
                            exit = slideOutVertically(
                                targetOffsetY = { it },
                                animationSpec = tween(300)
                            ) + fadeOut(
                                animationSpec = tween(300)
                            )
                        ) { 
                            //List of Item
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Text Down To Up
                    
                    AnimatedVisibility(
                        visible = true,
                        enter = slideInVertically(
                            animationSpec = spring(
                                dampingRatio = Spring.DampingRatioMediumBouncy,
                                stiffness = Spring.StiffnessMedium
                            ),
                            initialOffsetY = { it / 2 }
                        ) + fadeIn(
                            animationSpec = spring(
                                dampingRatio = Spring.DampingRatioMediumBouncy,
                                stiffness = Spring.StiffnessMedium
                            )
                        ) + scaleIn(
                            animationSpec = spring(
                                dampingRatio = Spring.DampingRatioLowBouncy,
                                stiffness = Spring.StiffnessMedium
                            ),
                            initialScale = 0.8f
                        )
                    ) {
                        Button(
                            onClick = {
                                Log.d("ProfileCircle", "Connect button clicked")
                                // Handle connect action
                            },
                            colors = ButtonDefaults.buttonColors(
                                containerColor = Color(0xFF2A2A2A)
                            ),
                            shape = RoundedCornerShape(24.dp),
                            modifier = Modifier
                                .fillMaxWidth(0.55f)
                                .height(52.dp)
                        ) {
                            Text(
                                text = "Connect",
                                color = Color.White,
                                fontSize = 16.sp,
                                fontWeight = FontWeight.SemiBold
                            )
                        }
                    }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun PlayScreenContent() {
                        var isContentVisible by remember { mutableStateOf(false) }

                        LaunchedEffect(Unit) {
                            isContentVisible = true
                        }

                        Column(
                            modifier =
                                Modifier
                                    .fillMaxSize()
                                    .background(MaterialTheme.colorScheme.background),
                        ) {
                            // Top Section with Welcome and Profile
                            AnimatedVisibility(
                                visible = isContentVisible,
                                enter =
                                    slideInVertically(
                                        initialOffsetY = { -it },
                                        animationSpec = tween(durationMillis = 500),
                                    ) + fadeIn(animationSpec = tween(durationMillis = 500)),
                            ) {

                            }

                            // Daily Challenge Section with scale animation
                            AnimatedVisibility(
                                visible = isContentVisible,
                                enter =
                                    scaleIn(
                                        initialScale = 0.8f,
                                        animationSpec =
                                            tween(
                                                durationMillis = 500,
                                                delayMillis = 100,
                                                easing = EaseOutBack,
                                            ),
                                    ) +
                                        fadeIn(
                                            animationSpec =
                                                tween(
                                                    durationMillis = 500,
                                                    delayMillis = 100,
                                                ),
                                        ),
                            ) {

                            }

                            // Game Modes with staggered animation
                            AnimatedVisibility(
                                visible = isContentVisible,
                                enter =
                                    slideInVertically(
                                        initialOffsetY = { it / 2 },
                                        animationSpec =
                                            tween(
                                                durationMillis = 500,
                                                delayMillis = 200,
                                                easing = EaseOutBack,
                                            ),
                                    ) +
                                        fadeIn(
                                            animationSpec =
                                                tween(
                                                    durationMillis = 500,
                                                    delayMillis = 200,
                                                ),
                                        ),
                            ) {

                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Slide Left to Right

                    @OptIn(ExperimentalFoundationApi::class)
                    @Composable
                    fun FinishButton(
                        modifier: Modifier = Modifier,
                        pagerState: PagerState,
                        count: Int,
                        onClick : () -> Unit = {}
                    ) {

                        AnimatedVisibility(
                            modifier = Modifier,
                            visible = true,
                                enter = slideInHorizontally(
                                    initialOffsetX = { -it },
                                ),
                                exit = slideOutHorizontally(
                                    targetOffsetX = { (-it * 1.2).toInt() },
                                )
                            ) {

                            }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Row Menu Animation Up/Down Scroll
                   
                    AnimatedVisibility(
                        visible = ,
                        enter = slideInVertically(initialOffsetY = { -it }) + fadeIn(),
                        exit = slideOutVertically(targetOffsetY = { -it }) + fadeOut()
                    ){ }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                   // ExpandableLayout

                   @Composable
                   fun ExpandableLayout(
                   	isExpanded: Boolean,
                   	duration: Int = 1,
                   	content: @Composable () -> Unit
                   ) {
                   	AnimatedVisibility(
                   		visible = isExpanded,
                   		enter = expandVertically(animationSpec = tween(
                   			durationMillis = duration,
                   			easing = CubicBezierEasing(0.4f, 0f, 1f, 1f)
                   		)),
                   		exit = shrinkVertically(animationSpec = tween(
                   			durationMillis = duration,
                   			easing = CubicBezierEasing(0.4f, 0f, 1f, 1f)
                   		))
                   	) {
                   		content()
                   	}
                   }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Spring Animation (Up and Down)

                    AnimatedVisibility(
                        visible =
                        enter = expandVertically(
                            animationSpec = spring(
                                dampingRatio = Spring.DampingRatioHighBouncy,
                                stiffness = Spring.StiffnessLow
                            )
                        )
                    ) { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Dropdown Selected After Composable Display
                    
                    AnimatedVisibility(
                        visible = isInfoExpanded,
                        enter = fadeIn() + expandVertically(),
                        exit = fadeOut() + shrinkVertically()
                    ) { }
                    
                    AnimatedVisibility(
                        visible = expandedTireSize,
                        enter = expandVertically(),
                        exit = shrinkVertically(),
                    ) { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    AnimatedVisibility(
                        visible = ,
                        enter = fadeIn() + slideInVertically(
                            initialOffsetY = { -it },
                            animationSpec = spring(
                                dampingRatio = Spring.DampingRatioMediumBouncy,
                                stiffness = Spring.StiffnessLow
                            )
                        )
                    ) {  }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    AnimatedVisibility(
                        visible = ,
                        enter = fadeIn() + slideInVertically(
                            initialOffsetY = { it / 2 },
                            animationSpec = tween(durationMillis = 500)
                        )
                    ) { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    AnimatedVisibility(
                        visible = ,
                        enter = fadeIn() + slideInVertically(
                            initialOffsetY = { it / 2 },
                            animationSpec = spring(stiffness = Spring.StiffnessLow)
                        )
                    ) { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    AnimatedVisibility(
                        visible = ,
                        enter = fadeIn() + slideInVertically { it },
                        exit = fadeOut() + slideOutVertically { it },
                            modifier = Modifier
                                .align(Alignment.TopCenter)
                                .padding(top = 32.dp)
                    ) { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    AnimatedVisibility(
                        visible = visible,
                        enter = fadeIn(animationSpec = tween(300)) + scaleIn(initialScale = 0.5f),
                    ) { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
            //LazyColumn Animation

            LazyColumn(
                contentPadding = PaddingValues(bottom = 16.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
                modifier = Modifier
                    .animateContentSize()
                    .heightIn(max = 500.dp),
                userScrollEnabled = true,
            ) {
                items(groups, key = { it.name }) { group ->
                   AnimatedVisibility(
                        visible = true,
                        enter = slideInVertically(
                            animationSpec = tween(durationMillis = 300),
                            initialOffsetY = { it }) + fadeIn(
                            animationSpec = tween(
                                durationMillis = 300
                            )
                        )
                    ) {

                    }
                }
            }
            """.trimIndent()
            )

            KotlinCode(
                code = """
                    AnimatedVisibility(
                        visible = ,
                        enter = fadeIn() + slideInVertically(initialOffsetY = { it / 2 }),
                        exit = fadeOut() + slideOutVertically(targetOffsetY = { -it / 2 })
                    ) {

                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        val shakeController = rememberInfiniteTransition()
                        val shake by shakeController.animateFloat(
                            initialValue = 0f,
                            targetValue = 1f,
                            animationSpec = infiniteRepeatable(
                                animation = tween(100),
                                repeatMode = RepeatMode.Reverse
                            )
                        )

                        val pulseAnim = rememberInfiniteTransition()
                        val scale by pulseAnim.animateFloat(
                            initialValue = 1f,
                            targetValue = 1.05f,
                            animationSpec = infiniteRepeatable(
                                animation = tween(1000),
                                repeatMode = RepeatMode.Reverse
                            )
                        )

                        LaunchedEffect(isShaking) {
                            if (isShaking) {
                                delay(300)
                                try {

                                } finally {
                                    isShaking = false
                                }
                            }
                        }

                        Box(
                            modifier = Modifier
                            .align(Alignment.Center)
                            .fillMaxWidth(0.9f)
                            .aspectRatio(1f)
                        ) {
                            Image(
                                painter = painterResource(Res.drawable.grapes),
                                contentDescription = "Tap to make a wish",
                                modifier = Modifier
                                    .fillMaxSize()
                                    .padding(16.dp)
                                    .scale(scale)
                                    .clickable(
                                        indication = null,
                                        interactionSource = remember { MutableInteractionSource() }
                                    ) {
                                        isShaking = true
                                        showTapHint = false
                                    }
                                    .graphicsLayer {
                                        if (isShaking) {
                                            rotationZ = shake * 5f
                                            translationX = shake * 5f
                                        }
                                    }
                            )
                        }
            """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun Modifier.hoverScaleEffect(targetScale: Float = 1.1f, duration: Int = 300): Modifier = composed {
                        val interactionSource = remember { MutableInteractionSource() }

                        val isHovered by interactionSource.collectIsHoveredAsState()

                        val scale by animateFloatAsState(
                            targetValue = if (isHovered) targetScale else 1f,
                            animationSpec = tween(durationMillis = duration, easing = FastOutSlowInEasing)
                        )

                        this.hoverable(interactionSource = interactionSource).scale(scale)
                    }
                    """.trimIndent()
            )

            //https://github.com/usarican/Clean-Architecture-Movie-Application.git
            KotlinCode(
                code = """
                    //Shake Try Again Button

                    @Composable
                    fun TryAgainButton(modifier: Modifier = Modifier, onClick: () -> Unit) {
                        val rotationZ = remember { Animatable(0f) }
                        LaunchedEffect(Unit) {
                            while (true) {
                                delay(2000)
                                repeat(3) {
                                    rotationZ.animateTo(
                                        targetValue = -2f,
                                        animationSpec = tween(durationMillis = 50, easing = LinearEasing)
                                    )
                                    rotationZ.animateTo(
                                        targetValue = 2f,
                                        animationSpec = tween(durationMillis = 50, easing = LinearEasing)
                                    )
                                }
                                rotationZ.animateTo(
                                    targetValue = 0f,
                                    animationSpec = tween(durationMillis = 50, easing = LinearEasing)
                                )
                                delay(2000)
                            }
                        }
                        Button(
                            modifier = modifier
                                .fillMaxWidth()
                                .padding(horizontal = dimensionResource(R.dimen.dp_64))
                                .graphicsLayer(rotationZ = rotationZ.value),
                            onClick = onClick,
                            colors = ButtonDefaults.buttonColors(
                                containerColor = MaterialTheme.colorScheme.errorContainer,
                                contentColor = MaterialTheme.colorScheme.onErrorContainer
                            ),
                            shape = RoundedCornerShape(dimensionResource(R.dimen.small_border))
                        ) {
                            Text(
                                modifier = Modifier,
                                text = "Try again",
                                style = MaterialTheme.typography.labelLarge.copy(
                                    color = MaterialTheme.colorScheme.onErrorContainer
                                ),
                                textAlign = TextAlign.Center
                            )
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "animateDpAsState - (CheckBox, Drawer collapsed, Tab Indicator)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/Yoke0/Gainful (Calendar, BottomBar Background Spring Animation, Tab Background Spring Animation)
            KotlinCode(
                code = """
                    //Tab Navigation Background Spring Animation
                    
                    enum class PnlPeriodType {
                        DAY,
                        WEEK,
                        MONTH,
                        YEAR,
                    }
                    
                    @Composable
                    private fun PnlPeriodTabs(
                        selectedPeriod: PnlPeriodType,
                        onPeriodSelected: (PnlPeriodType) -> Unit,
                    ) {
                        val periods =
                            listOf(
                                PnlPeriodType.DAY to stringResource(Res.string.pnl_period_day),
                                PnlPeriodType.WEEK to stringResource(Res.string.pnl_period_week),
                                PnlPeriodType.MONTH to stringResource(Res.string.pnl_period_month),
                                PnlPeriodType.YEAR to stringResource(Res.string.pnl_period_year),
                            )
                        val selectedIndex = periods.indexOfFirst { it.first == selectedPeriod }

                        var rowSize by remember { mutableStateOf(IntSize.Zero) }
                        val density = LocalDensity.current
                        val tabWidthDp = if (rowSize.width > 0) with(density) { (rowSize.width / periods.size).toDp() } else 0.dp
                        val animatedOffset by animateDpAsState(
                            targetValue = tabWidthDp * selectedIndex,
                            animationSpec = spring(dampingRatio = Spring.DampingRatioMediumBouncy, stiffness = Spring.StiffnessLow),
                        )

                        Box(
                            modifier =
                                Modifier
                                    .fillMaxWidth()
                                    .height(IntrinsicSize.Min)
                                    .clip(RoundedCornerShape(9999.dp))
                                    .background(Surface)
                                    .padding(3.dp),
                        ) {
                            if (rowSize.width > 0) {
                                Box(
                                    modifier =
                                        Modifier
                                            .fillMaxHeight()
                                            .offset(x = animatedOffset)
                                            .width(tabWidthDp)
                                            .clip(RoundedCornerShape(9999.dp))
                                            .background(Gold),
                                )
                            }

                            Row(
                                modifier =
                                    Modifier
                                        .fillMaxSize()
                                        .onGloballyPositioned { rowSize = it.size },
                            ) {
                                periods.forEach { (period, label) ->
                                    val isSelected = period == selectedPeriod
                                    Text(
                                        text = label,
                                        fontSize = 14.sp,
                                        fontWeight = FontWeight.SemiBold,
                                        color = if (isSelected) Background else TextMuted,
                                        modifier =
                                            Modifier
                                                .weight(1f)
                                                .clickable { onPeriodSelected(period) }
                                                .padding(vertical = 8.dp),
                                        textAlign = TextAlign.Center,
                                    )
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/codewithdipesh/Habitized.git
            KotlinCode(
                code = """
                    //CheckBox animateDpAsState
                    
                    val checkBoxProgress by animateDpAsState(
                        targetValue = if(todo.isCompleted) 16.dp else 0.dp,
                        animationSpec = tween(durationMillis = 150),
                        label = "checkBoxAnimation"
                    )
                        
                    Icon(
                        imageVector = Icons.Filled.Check,
                        contentDescription = "Check",
                        tint = textColor,
                        modifier = Modifier.size(checkBoxProgress)
                    )
                    """.trimIndent()
            )

            //https://github.com/Cairo-Squad/Movio.git
            KotlinCode(
                code = """
                    @Composable
                    fun TabRow(
                        tabs: List<String>,
                        selectedTabIndex: Int,
                        onTabSelected: (Int) -> Unit,
                        scrollProgress: Float,
                        tabColorWithNoScroll:Color,
                        tabColorWithScroll:Color,
                        indicatorColorWithScroll:Brush,
                        indicatorColorWithNoScroll:Brush,
                        modifier: Modifier = Modifier,

                    ) {
                        val tabPositions = remember { mutableStateMapOf<Int, Pair<Int, Int>>() }
                        var rowWidthPx by remember { mutableIntStateOf(0) }

                        val layoutDirection = LocalLayoutDirection.current
                        val density = LocalDensity.current

                        val horizontalScrollingState = rememberScrollState()
                        val horizontalScrollingValueDp = with(density) { horizontalScrollingState.value.toDp() }

                        val indicatorOffsetX by animateDpAsState(
                            targetValue = with(density) {
                                tabPositions[selectedTabIndex]?.let { (xPx, widthPx) ->
                                    if (layoutDirection == LayoutDirection.Rtl) {
                                        (rowWidthPx - xPx - widthPx).toDp()
                                    } else {
                                        xPx.toDp()
                                    }
                                } ?: 0.dp
                            },
                            animationSpec = tween(200)
                        )

                        val indicatorWidth by animateDpAsState(
                            targetValue = with(density) {
                                tabPositions[selectedTabIndex]?.second?.toDp() ?: 0.dp
                            },
                            animationSpec = tween(200)
                        )


                        val selectedTabTitle = tabs.getOrNull(selectedTabIndex)
                        val indicatorBrush = if (selectedTabTitle == stringResource(com.cairosquad.design_system.R.string.categories)) {

                            if (scrollProgress >= 0.5f) {
                                indicatorColorWithScroll
                            } else {
                                indicatorColorWithScroll
                            }
                        } else {

                            if (scrollProgress >= 0.5f) {
                                indicatorColorWithScroll
                            } else {
                                indicatorColorWithNoScroll
                            }
                        }

                        Box(modifier = modifier.fillMaxWidth()) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(36.dp)
                                    .horizontalScroll(horizontalScrollingState)
                                    .onGloballyPositioned { rowWidthPx = it.size.width },
                                horizontalArrangement = Arrangement.SpaceAround,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                tabs.forEachIndexed { index, title ->
                                    val isSelected = selectedTabIndex == index
                                    val isLightTheme = !Theme.isDark

                                    val textColor by animateColorAsState(
                                        targetValue = when {
                                            isLightTheme && isSelected && scrollProgress > 0.5f ->
                                                tabColorWithScroll
                                            isSelected ->
                                                if (selectedTabTitle == stringResource(com.cairosquad.design_system.R.string.categories))
                                                    Theme.color.brand.onPrimaryContainer
                                            else
                                                tabColorWithNoScroll
                                            else ->
                                                Theme.color.surfaces.onSurfaceVariant
                                        },
                                        animationSpec = tween(300)
                                    )

                                    val textStyle = if (isSelected)
                                        Theme.textStyle.title.mediumMedium16
                                    else
                                        Theme.textStyle.body.smallRegular16

                                    Column(
                                        modifier = Modifier
                                            .clickable(
                                                indication = null,
                                                interactionSource = remember { MutableInteractionSource() }
                                            ) { onTabSelected(index) }
                                            .padding(horizontal = 12.dp)
                                            .onGloballyPositioned { coords ->
                                                tabPositions[index] =
                                                    coords.positionInParent().x.toInt() to coords.size.width
                                            },
                                        horizontalAlignment = Alignment.CenterHorizontally,
                                        verticalArrangement = Arrangement.spacedBy(7.5.dp)
                                    ) {
                                        Text(
                                            text = title,
                                            color = textColor,
                                            textAlign = TextAlign.Center,
                                            style = textStyle
                                        )
                                    }
                                }
                            }

                            if (tabPositions.containsKey(selectedTabIndex)) {
                                Box(
                                    modifier = Modifier
                                        .offset(x = indicatorOffsetX - horizontalScrollingValueDp)
                                        .height(1.dp)
                                        .width(indicatorWidth)
                                        .align(Alignment.BottomStart)
                                        .background(brush = indicatorBrush)
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Height Animation
                    
                    val animatedHeight by animateDpAsState(
                    targetValue = if (expanded) 400.dp else 200.dp,
                    animationSpec = spring(
                        dampingRatio = Spring.DampingRatioMediumBouncy, //can set it to HighBouncy
                        stiffness = Spring.StiffnessLow     //Very low for scroll like effect
                    ),
                    label = "Height Animation")
                """.trimIndent()
            )

            //https://github.com/FabianZettl/NeiroDesktop.git
            KotlinCode(
                code = """
                      var collapsed by remember { mutableStateOf(false) }
                      
                      val sidebarWidth by animateDpAsState(
                        targetValue = if (collapsed) 56.dp else 230.dp,
                        animationSpec = tween(220),
                        label = "sidebarWidth"
                      )
                      
                      Column(
                        modifier = Modifier
                            .width(sidebarWidth)
                            .fillMaxHeight()
                      ) { }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Position Animation

                    val offSetValue by animateDpAsState(
                        targetValue = if (toggled) 150.dp else 0.dp,
                        animationSpec = tween(durationMillis = 2000, easing = LinearOutSlowInEasing)
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun ConnectivityIndicatorContainer(
                        isOnline: Boolean,
                        modifier: Modifier = Modifier,
                        content: @Composable (topPadding: Dp) -> Unit = {},
                    ) {
                        val isIndicatorVisible = remember { MutableTransitionState(!isOnline) }.apply { targetState = !isOnline }

                        val statusBarTopPadding = if (LocalInspectionMode.current) {
                            // Needed to get valid UI previews
                            24.dp
                        } else {
                            WindowInsets.statusBars.asPaddingValues().calculateTopPadding() + 6.dp
                        }
                        val target = remember(isIndicatorVisible.targetState, statusBarTopPadding) {
                            if (!isIndicatorVisible.targetState) 0.dp else statusBarTopPadding
                        }
                        val animationStateOffset by animateDpAsState(
                            targetValue = target,
                            animationSpec = spring(
                                stiffness = Spring.StiffnessMediumLow,
                                visibilityThreshold = 1.dp,
                            ),
                            label = "insets-animation",
                        )

                        content(animationStateOffset)

                        // Display the network indicator with an animation / Dynamic Field Form
                        AnimatedVisibility(
                            visibleState = isIndicatorVisible,
                            enter = fadeIn() + expandVertically(),
                            exit = fadeOut() + shrinkVertically(),
                        ) {
                            Indicator(modifier)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "𝐚𝐧𝐢𝐦𝐚𝐭𝐞𝐅𝐥𝐨𝐚𝐭𝐀𝐬𝐒𝐭𝐚𝐭𝐞",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "𝐒𝐭𝐚𝐭𝐞 𝐌𝐚𝐧𝐚𝐠𝐞𝐦𝐞𝐧𝐭: The animationStarted variable tracks whether the animation is active. It uses remember and mutableStateOf to persist its value across recompositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "𝐒𝐦𝐨𝐨𝐭𝐡 𝐀𝐧𝐢𝐦𝐚𝐭𝐢𝐨𝐧: The animateFloatAsState function animates the rotation between 0f and 180f, depending on the value of animationStarted.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "𝐓𝐰𝐞𝐞𝐧 𝐀𝐧𝐢𝐦𝐚𝐭𝐢𝐨𝐧𝐒𝐩𝐞𝐜: This defines the animation speed, making it complete in 500ms for a smooth experience.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "𝙄𝙣𝙩𝙚𝙧𝙖𝙘𝙩𝙞𝙤𝙣: With Modifier.clickable, you can toggle the animation by clicking the icon.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    Banner(
                        pics = [],
                        interval = 5000L,
                        gap = gap,
                        modifier = modifier
                    ) { pic, index, scale ->
                    	Surface(
                    		modifier = Modifier.fillMaxWidth().aspectRatio(2f).scale(scale),
                    		shape = shape,
                    		shadowElevation = ThemeValue.Shadow.Surface
                    	) {

                    	}
                    }

                    @Composable
                    private fun BannerIndicator(
                    	num: Int,
                    	current: Int,
                    	totalWidth: Dp,
                    ) {
                    	val width = totalWidth / num
                    	val offsetX by animateDpAsState(targetValue = width * current)

                    	Row(
                    		modifier = Modifier.width(totalWidth).height(width / 8f)
                    			.clip(MaterialTheme.shapes.extraLarge)
                    			.background(MaterialTheme.colorScheme.onBackground.copy(alpha = 0.2f))
                    	) {
                    		Box(
                    			modifier = Modifier.width(width).fillMaxHeight()
                    				.offset(x = offsetX).clip(MaterialTheme.shapes.extraLarge)
                    				.background(MaterialTheme.colorScheme.onBackground)
                    		)
                    	}
                    }

                    @Composable
                    fun <T> Banner(
                    	pics: List<T>,
                    	interval: Long = 0L,
                    	gap: Float = 0.2f,
                    	state: PagerState = rememberPagerState { pics.size },
                    	modifier: Modifier = Modifier,
                    	content: @Composable (pic: T, index: Int, scale: Float) -> Unit
                    ) {
                    	BoxWithConstraints(modifier = modifier) {
                    		val spacingGap = maxWidth * gap
                    		val totalWidth = maxWidth * 0.3f
                    		Column(
                    			modifier = Modifier.fillMaxWidth(),
                    			horizontalAlignment = Alignment.CenterHorizontally,
                    			verticalArrangement = Arrangement.spacedBy(ThemeValue.Padding.VerticalExtraSpace)
                    		) {
                    			HorizontalPager(
                    				state = state,
                    				beyondViewportPageCount = 1,
                    				contentPadding = PaddingValues(horizontal = spacingGap),
                    				modifier = Modifier.fillMaxWidth()
                    			) {
                    				if (it in 0 ..< pics.size) {
                    					val scale by animateFloatAsState(targetValue = if (it == state.currentPage) 1f else 0.85f)
                    					content(pics[it], it, scale)
                    				}
                    			}
                    			if (pics.isNotEmpty()) {
                    				BannerIndicator(
                    					num = pics.size,
                    					current = state.currentPage,
                    					totalWidth = totalWidth
                    				)
                    			}
                    		}
                    	}

                    	val autoplay by rememberDerivedState(interval, pics) { interval > 0L && pics.size > 1 }
                    	val isForeground = rememberOffScreenState()

                    	if (autoplay && isForeground) {
                    		LaunchedEffect(state.settledPage) {
                    			delay(interval)
                    			if (pics.isNotEmpty() && state.pageCount != 0) {
                    				state.animateScrollToPage((state.currentPage + 1) % state.pageCount)
                    			}
                    		}
                    	}
                    }

                    @Composable
                    fun <T> rememberDerivedState(key1: Any?, key2: Any?, calculation: () -> T) =
                    	remember(key1, key2) { derivedStateOf(calculation) }

                    @Composable
                    fun rememberOffScreenState(): Boolean {
                    	var value by rememberState { false }
                    	LifecycleStartEffect(Unit) {
                    		value = true
                    		onStopOrDispose {
                    			value = false
                    		}
                    	}
                    	return value
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Expand/Collapse Blur Animation

                    @Composable
                    fun AccordionVariant3(modifier: Modifier = Modifier) {
                        var expanded by remember { mutableStateOf(false) }
                        val arrowRotation by animateFloatAsState(
                            targetValue = if (expanded) 90f else 0f,
                            label = "accordion-arrow"
                        )

                        Surface(
                            color = MaterialTheme.colorScheme.tertiary,
                            modifier = modifier
                                // clip the ripple effect
                                .clip(RoundedCornerShape(12.dp))
                                .clickable { expanded = !expanded }
                        ) {
                            Column(
                                verticalArrangement = Arrangement.spacedBy(4.dp),
                                modifier = Modifier.padding(16.dp)
                            ) {
                                // accordion header
                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                                ) {
                                    Text(
                                        text = LOREM_IPSUM_SHORT,
                                        style = MaterialTheme.typography.headlineSmall,
                                        fontWeight = FontWeight.Bold,
                                        // make sure the text fill the row
                                        modifier = Modifier.weight(1f)
                                    )
                                    Icon(
                                        imageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight,
                                        contentDescription = null,
                                        // arrow rotation based on the expanded state
                                        modifier = Modifier.rotate(arrowRotation)
                                    )
                                }

                                // accordion content
                                Box(modifier = modifier) {
                                    // accordion content - collapsed item
                                    this@Column.AnimatedVisibility(
                                        visible = !expanded,
                                        // only used fadeIn and fadeOut animation for the collapsed item
                                        enter = fadeIn(animationSpec = tween(durationMillis = 0)),
                                        exit = fadeOut(animationSpec = tween())
                                    ) {
                                        Box {
                                            Text(
                                                text = LOREM_IPSUM_VERY_LONG,
                                                minLines = 2,
                                                maxLines = 2,
                                                overflow = TextOverflow.Ellipsis,
                                                style = MaterialTheme.typography.bodyMedium,
                                            )
                                            // overlay above the text
                                            Box(
                                                modifier = Modifier
                                                    .align(Alignment.BottomCenter)
                                                    .height(32.dp)
                                                    .fillMaxWidth()
                                                    .background(
                                                        // gradient color from top to bottom
                                                        brush = Brush.verticalGradient(
                                                            colors = listOf(
                                                                Color.Transparent,
                                                                MaterialTheme.colorScheme.tertiary
                                                            )
                                                        )
                                                    )
                                            )
                                        }
                                    }
                                    // accordion content - expanded item
                                    this@Column.AnimatedVisibility(
                                        visible = expanded,
                                        // animate expand vertically from the top when expanded
                                        enter = expandVertically(
                                            expandFrom = Alignment.Top,
                                            animationSpec = tween()
                                        ),
                                        // animate shrink vertically to the top when collapsed + fade out
                                        exit = shrinkVertically(
                                            shrinkTowards = Alignment.Top,
                                            animationSpec = tween()
                                        ) + fadeOut()
                                    ) {
                                        Text(
                                            text = LOREM_IPSUM_VERY_LONG,
                                            style = MaterialTheme.typography.bodyMedium
                                        )
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "animateIntOffsetAsState()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //BottomBar Menu Default Bottom to Top

                    @Composable
                    fun AddButton(
                        isExpanded: Boolean,
                        onEvent: (DashboardBottomNavigationEvent) -> Unit,
                    ) {
                        val pxToMove = with(LocalDensity.current) {
                            -40.dp.toPx().roundToInt()
                        }
                        val offset by animateIntOffsetAsState(
                            targetValue = if (isExpanded) IntOffset(0, pxToMove) else IntOffset.Zero,
                            label = "offset"
                        )
                        Row(
                            modifier = Modifier.offset { offset }.padding(bottom = 80.dp)
                                .background(color = Colors.Green50, shape = Dimens.Shape.Circle)
                                .padding(if (isExpanded) 5.dp else 0.dp),
                            horizontalArrangement = Arrangement.spacedBy(5.dp)
                        ) {

                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "aninateFloatAsState()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    var cotationYDegrees by remember { mutableStateOf(value: 0f) }

                    val rotationY by aninateFloatAsState(
                        targetValue = cotationYDegrees,
                        animationSpec = tween(durationMillis = 1000),
                        Label = "card_Y-rotation")

                    val dragGestureModifier = Modifier.pointerInput(Unit) {
                        detectDragGestures { -, dragAmount -> rotationYDegrees += (dragAmount.x * B.5f)
                    }

                    modifier•graphicslayer {
                        this.rotationY = rotationY
                        caneraDistance = 12 * density
                    }.then(dragGestureModifier)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Level Indicator

                    fun getBrushGradient(i: Int): List<Color> = listOf(
                        listOf(Colors.Green50, Colors.Green50),
                        listOf(Colors.Green50, Colors.Yellow50),
                        listOf(Colors.Yellow50, Colors.Yellow50),
                        listOf(Colors.Yellow50, Colors.Orange50),
                        listOf(Colors.Orange50, Colors.Orange50)
                    )[i]

                    @Composable
                    fun FivePackIndicator(level: Int, brushGradient: (Int) -> List<Color>) {
                        BoxWithConstraints {
                            val boxWidth = (maxWidth - 16.dp) / 5
                            Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                                for (i in 1..5) {
                                    val isSelected = i <= level
                                    val background = if (isSelected) Modifier.background(
                                        brush = Brush.linearGradient(brushGradient(i - 1)),
                                        shape = Dimens.Shape.Circle
                                    )
                                    else Modifier.background(
                                        color = Colors.Brown20, shape = Dimens.Shape.Circle
                                    )
                                    Box(
                                        modifier = Modifier.width(boxWidth).height(6.dp).then(background)
                                    )
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "rememberInfiniteTransition",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/junjange/junjange.github.io.git
            KotlinCode(
                code = """
                    //Arrow Up/Down Continues Animation
                    
                    @Composable
                    fun AnimatedArrow(
                        modifier: Modifier = Modifier,
                        onClick: () -> Unit,
                    ) {
                        val infiniteTransition = rememberInfiniteTransition()

                        val offsetY by infiniteTransition.animateFloat(
                            initialValue = 0f,
                            targetValue = 10f,
                            animationSpec =
                                infiniteRepeatable(
                                    animation = tween(durationMillis = 1000, easing = LinearEasing),
                                    repeatMode = RepeatMode.Reverse,
                                ),
                        )

                        IconButton(
                            onClick = onClick,
                            modifier = modifier,
                        ) {
                            Icon(
                                imageVector = Icons.Default.KeyboardArrowDown,
                                contentDescription = null,
                                tint = MaterialTheme.colorScheme.primary,
                                modifier =
                                    Modifier
                                        .size(64.dp)
                                        .offset(y = offsetY.dp),
                            )
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/Dev-Aditya-More/PokeVerse.git
            KotlinCode(
                code = """
                    //Background gradient left - right and right - left animation
                    
                    @Composable
                    fun PremiumBanner(
                        price: String,
                        onSubscribe: () -> Unit
                    ) {
                        val infiniteTransition = rememberInfiniteTransition(label = "")

                        val translateAnim by infiniteTransition.animateFloat(
                            initialValue = 0f,
                            targetValue = 1000f,
                            animationSpec = infiniteRepeatable(
                                animation = tween(1800, easing = LinearEasing),
                                repeatMode = RepeatMode.Reverse
                            ),
                            label = ""
                        )

                        val gradient = Brush.linearGradient(
                            colors = listOf(
                                MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.7f),
                                Color(0xFFFFD700).copy(alpha = 0.4f),
                                MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.7f),
                            ),
                            start = Offset(translateAnim - 300f, 0f),
                            end = Offset(translateAnim, 300f)
                        )

                        Card(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable { onSubscribe() },
                            shape = RoundedCornerShape(16.dp),
                            colors = CardDefaults.cardColors(containerColor = Color.Transparent)
                        ) {
                            Box(
                                modifier = Modifier
                                    .background(gradient)
                            ) {
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(16.dp),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                                ) {
                                    Icon(
                                        imageVector = Icons.Default.WorkspacePremium,
                                        contentDescription = null,
                                        tint = Color(0xFFFFD700),
                                        modifier = Modifier.size(32.dp)
                                    )

                                    Column(modifier = Modifier.weight(1f)) {
                                        Text(
                                            text = "Go Premium",
                                            style = MaterialTheme.typography.titleMedium,
                                            fontWeight = FontWeight.Bold,
                                            color = MaterialTheme.colorScheme.onPrimaryContainer
                                        )
                                        Text(
                                            text = "Unlock Hard mode and more",
                                            style = MaterialTheme.typography.bodySmall,
                                            color = MaterialTheme.colorScheme.onPrimaryContainer.copy(alpha = 0.8f)
                                        )
                                    }

                                    Text(
                                        text = price.ifEmpty { "Loading..." },
                                        style = MaterialTheme.typography.titleMedium,
                                        fontWeight = FontWeight.Bold,
                                        color = MaterialTheme.colorScheme.primary
                                    )
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/Nubet/studymood-app-public.git
            KotlinCode(
                code = """
                    @Composable
                    fun WaveText(
                        text: String,
                        modifier: Modifier = Modifier,
                        amplitude: Dp = 3.dp,
                        speedMs: Int = 1600,
                        phaseShift: Float = (PI / 6f).toFloat(),
                        fontSize: TextUnit = 16.sp,
                        color: Color = MaterialTheme.colorScheme.onSurfaceVariant,
                    ) {
                        val infinite = rememberInfiniteTransition(label = "wave")

                        val phase by
                            infinite.animateFloat(
                                initialValue = 0f,
                                targetValue = (2f * PI).toFloat(),
                                animationSpec =
                                    infiniteRepeatable(
                                        animation = tween(durationMillis = speedMs, easing = LinearEasing),
                                        repeatMode = RepeatMode.Restart,
                                    ),
                                label = "phase",
                            )

                        Row(modifier = modifier, horizontalArrangement = Arrangement.spacedBy(0.dp)) {
                            text.forEachIndexed { i, ch ->
                                val y = sin(phase + i * phaseShift)

                                val offset = amplitude.value * y

                                Box(modifier = Modifier.graphicsLayer { translationY = offset }) {
                                    Text(text = ch.toString(), fontSize = fontSize, color = color)
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/idineszam2374/kmp-luckin-coffee-clone.git
            KotlinCode(
                code = """
                    //Bounce animation Shopping Item BottomBar
                    
                    @Composable
                    private fun AnimatedFloatingCartBar(
                        cartState: CartState,
                        onCartClick: () -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        val infiniteTransition = rememberInfiniteTransition()
                        
                        //Bounce animation
                        val translateY by infiniteTransition.animateFloat(
                            initialValue = 0f,
                            targetValue = -4f,
                            animationSpec = infiniteRepeatable(
                                animation = tween(1000, easing = FastOutSlowInEasing),
                                repeatMode = RepeatMode.Reverse
                            )
                        )
                        
                        //modifier = modifier.graphicsLayer { translationY = translateY }
                    }
                    """.trimIndent()
            )

            //https://github.com/love-yuri/journal-kmp.git
            KotlinCode(
                code = """
                    //Heart Multiples UI Animation 
                    
                    data class HeartData(
                        val x: Float,
                        val y: Float,
                        val size: Float,
                        val speed: Float,
                        val delay: Int
                    )
                    
                    @Composable
                    fun FloatingHearts() {
                        var containerSize by remember { mutableStateOf(IntSize.Zero) }
                        var hearts by remember { mutableStateOf(emptyList<HeartData>()) }
                        Box(Modifier.fillMaxSize().onGloballyPositioned { coordinates ->
                            containerSize = coordinates.size
                            if (hearts.isEmpty() && containerSize.width > 0 && containerSize.height > 0) {
                                hearts = List(28) { index ->
                                    HeartData(
                                        x = Random.nextFloat() * containerSize.width,
                                        y = Random.nextFloat() * containerSize.height,
                                        size = Random.nextFloat() * 20 + 10,
                                        speed = Random.nextFloat() * 0.5f + 0.2f,
                                        delay = Random.nextInt(8) * 1000
                                    )
                                }
                            }
                        }) {
                            hearts.forEach { heart ->
                                FloatingHeart(heart)
                            }
                        }
                    }

                    @Composable
                    private fun FloatingHeart(heartData: HeartData) {
                        val infiniteTransition = rememberInfiniteTransition()

                        val animatedY by infiniteTransition.animateFloat(
                            initialValue = heartData.y,
                            targetValue = heartData.y - 100f,
                            animationSpec = infiniteRepeatable(
                                animation = tween(
                                    durationMillis = (8000 / heartData.speed).toInt(),
                                    delayMillis = heartData.delay,
                                    easing = LinearEasing
                                ),
                                repeatMode = RepeatMode.Restart
                            ),
                        )

                        val animatedX by infiniteTransition.animateFloat(
                            initialValue = heartData.x,
                            targetValue = heartData.x - 100f,
                            animationSpec = infiniteRepeatable(
                                animation = tween(
                                    durationMillis = (8000 / heartData.speed).toInt(),
                                    delayMillis = heartData.delay,
                                    easing = LinearEasing
                                ),
                                repeatMode = RepeatMode.Restart
                            ),
                        )

                        val alpha by infiniteTransition.animateFloat(
                            initialValue = 0f,
                            targetValue = 0.6f,
                            animationSpec = infiniteRepeatable(
                                animation = keyframes {
                                    durationMillis = (8000 / heartData.speed).toInt()
                                    0f at 0
                                    0.6f at 1000
                                    0.4f at 4000
                                    0f at 8000
                                },
                                repeatMode = RepeatMode.Restart
                            ),
                            label = "heartAlpha"
                        )

                        Box(
                            modifier = Modifier
                                .offset(animatedX.dp, animatedY.dp)
                                .size(heartData.size.dp)
                                .alpha(alpha)

                                .drawWithContent {
                                    drawHeart(
                                        color = Color(0xFFFF6B9D),
                                        size = size.minDimension
                                    )
                                }
                        )
                    }

                    fun DrawScope.drawHeart(color: Color, size: Float) {
                        val path = Path().apply {
                            val width = size
                            val height = size

                            moveTo(width / 2, height * 0.25f)
                            
                            cubicTo(
                                width * 0.2f, height * 0.1f,
                                -width * 0.25f, height * 0.6f,
                                width / 2, height
                            )
                            
                            cubicTo(
                                width * 1.25f, height * 0.6f,
                                width * 0.8f, height * 0.1f,
                                width / 2, height * 0.25f
                            )

                            close()
                        }

                        drawPath(path, color)
                    }
                    """.trimIndent()
            )

            //https://github.com/AminAbbasov778/AnimatedButton.git
            KotlinCode(
                code = """
                    //Animated Border
                    
                    @Composable
                    fun AnimatedBorderBox(
                        modifier: Modifier = Modifier,
                        borderColors: List<Color>,
                        borderWidth: Dp = 3.dp,
                        shape: Shape = RoundedCornerShape(12.dp),
                        animationDuration: Int = 2000,
                        content: @Composable BoxScope.() -> Unit
                    ) {
                        val infiniteTransition = rememberInfiniteTransition()
                        val angle by infiniteTransition.animateFloat(
                            0f, 360f,
                            animationSpec = infiniteRepeatable(
                                tween(animationDuration, easing = LinearEasing),
                                RepeatMode.Restart
                            )
                        )

                        Box(
                            modifier = modifier
                                .clip(shape)
                                .padding(borderWidth)
                                .drawWithContent {
                                    val brush = Brush.sweepGradient(colors = borderColors)
                                    rotate(angle) { drawCircle(brush, radius = size.width) }
                                    drawContent()
                                },
                            content = content
                        )
                    }
                    
                    @Composable
                    fun SpinningBorderButton() {
                        val isDark = isSystemInDarkTheme()

                        val trailColor = if (isDark) BorderTrailDark else BorderTrailLight

                        val borderColors = buildList {
                            repeat(12) { add(Color.Transparent) }
                            repeat(8) { add(trailColor) }
                            add(Color.Transparent)
                        }

                        Box(
                            modifier = Modifier
                                .fillMaxSize()
                                .background(Color.Black),
                            contentAlignment = Alignment.Center
                        ) {
                            AnimatedBorderBox(
                                borderColors = borderColors,
                                borderWidth = 3.dp
                            ) {
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .height(70.dp)
                                        .clip(RoundedCornerShape(11.dp))
                                        .background(
                                            Brush.linearGradient(
                                                listOf(
                                                    if (isDark) BlurLeftStartDark else BlurLeftStartLight,
                                                    if (isDark) BlurLeftEndDark else BlurLeftEndLight,
                                                    if (isDark) BlurRightStartDark else BlurRightStartLight,
                                                    if (isDark) BlurRightEndDark else BlurRightEndLight
                                                )
                                            )
                                        )
                                        .padding(horizontal = 12.dp),
                                    contentAlignment = Alignment.Center
                                ) {

                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/jasonctoms/JasonToms.git
            KotlinCode(
                code = """
                    //Background 
                    
                    @Previews
                    @Composable
                    fun WebsiteBackground() {
                        val infiniteTransition = rememberInfiniteTransition()
                        val pulseAnimation by infiniteTransition.animateFloat(
                            initialValue = 0f,
                            targetValue = 1f,
                            animationSpec = infiniteRepeatable(
                                animation = tween(
                                    durationMillis = 15000,
                                    easing = LinearEasing
                                ),
                                repeatMode = RepeatMode.Restart
                            )
                        )

                        val baseColor = MaterialTheme.colorScheme.background
                        val accentColor = MaterialTheme.colorScheme.primary.copy(alpha = 0.1f)

                        Canvas(
                            modifier = Modifier
                                .fillMaxSize()
                                .background(baseColor)
                        ) {
                            drawTexturedBackground(
                                pulseValue = pulseAnimation,
                                baseColor = baseColor,
                                accentColor = accentColor,
                            )
                        }
                    }

                    private fun DrawScope.drawTexturedBackground(
                        pulseValue: Float,
                        baseColor: Color,
                        accentColor: Color,
                    ) {
                        val width = size.width
                        val height = size.height

                        val gradientBrush = Brush.radialGradient(
                            colors = listOf(
                                accentColor.copy(alpha = 0.05f),
                                baseColor
                            ),
                            center = Offset(width * 0.5f, height * 0.3f),
                            radius = width * 0.8f
                        )

                        drawRect(
                            brush = gradientBrush,
                            size = size
                        )

                        val dotSpacing = 10.dp.toPx()
                        val baseDotRadius = 1.dp.toPx()
                        val baseDotAlpha = 0.2f

                        val cols = (width / dotSpacing).toInt() + 1
                        val rows = (height / dotSpacing).toInt() + 1

                        for (col in 0..cols) {
                            for (row in 0..rows) {
                                val x = col * dotSpacing
                                val y = row * dotSpacing

                                val wave = sin((x + y) * 0.02f + pulseValue * PI * 2f)
                                val waveFactor = (wave * 0.5f + 0.5f).toFloat()
                                val currentRadius = baseDotRadius * (1f + waveFactor * 0.5f)
                                val currentAlpha = baseDotAlpha * (1f + waveFactor)

                                drawCircle(
                                    color = accentColor.copy(alpha = currentAlpha),
                                    radius = currentRadius,
                                    center = Offset(x, y)
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Image Loading 
                    
                    @Composable
                    fun ImageLoadingIndicator(
                        modifier: Modifier = Modifier,
                        size: Dp = 48.dp,
                    ) {
                        val alpha by rememberInfiniteTransition().animateFloat(
                            initialValue = 0f,
                            targetValue = 1f,
                            animationSpec = infiniteRepeatable(
                                animation = tween(1000, easing = LinearEasing),
                                repeatMode = RepeatMode.Reverse
                            ),
                        )
                        Box(
                            modifier = modifier.fillMaxSize(),
                            contentAlignment = Alignment.Center,
                        ) {
                            Image(
                                painter = painterResource(id = R.drawable.ic_film_roll),
                                contentDescription = null,
                                modifier = modifier
                                    .size(size)
                                    .alpha(alpha),
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    // Pulse animation for peak glow
                    //1
                    val infiniteTransition = rememberInfiniteTransition()
                    val pulseSize by infiniteTransition.animateFloat(
                        initialValue = 18f,
                        targetValue = 26f,
                        animationSpec = infiniteRepeatable(
                            animation = tween(1000, easing = FastOutSlowInEasing),
                            repeatMode = RepeatMode.Reverse
                        )
                    )

                    Box(
                        modifier = Modifier
                        .align(Alignment.TopEnd)
                        .size(pulseSize.dp)
                        .background(
                            brush = Brush.radialGradient(
                            colors = listOf(Color.Yellow, Color.Transparent),
                            radius = 50f),
                        shape = CircleShape
                    )
                    
                    //2 
                    @Composable
                    fun PulsingAvatar(
                        modifier: Modifier = Modifier,
                        size: Dp = 80.dp,
                        emoji: String = "🤖"
                    ) {
                        val infiniteTransition = rememberInfiniteTransition(label = "pulse")
                        val pulse by infiniteTransition.animateFloat(
                            initialValue = 0.95f,
                            targetValue = 1.05f,
                            animationSpec = infiniteRepeatable(
                                animation = tween(1000, easing = FastOutSlowInEasing),
                                repeatMode = RepeatMode.Reverse
                            ),
                            label = "pulseAnim"
                        )

                        val glowAlpha by infiniteTransition.animateFloat(
                            initialValue = 0.25f,
                            targetValue = 0.4f,
                            animationSpec = infiniteRepeatable(
                                animation = tween(1000, easing = LinearEasing),
                                repeatMode = RepeatMode.Reverse
                            ),
                            label = "glowAlpha"
                        )

                        val floatYRaw by infiniteTransition.animateFloat(
                            initialValue = 0f,
                            targetValue = 10f,
                            animationSpec = infiniteRepeatable(
                                animation = tween(1800, easing = EaseInOutCubic),
                                repeatMode = RepeatMode.Reverse
                            ),
                            label = "floatY"
                        )

                        val floatY = with(LocalDensity.current) { floatYRaw.toDp() }

                        val density = LocalDensity.current
                        val radiusPx = with(density) { (size * 1.6f).toPx() }

                        Box(
                            modifier = modifier
                                .offset(y = floatY)
                                .size(size)
                                .graphicsLayer {
                                    scaleX = pulse
                                    scaleY = pulse
                                    shadowElevation = 12f
                                    shape = CircleShape
                                    clip = true
                                    ambientShadowColor = Color.Black.copy(alpha = 0.70f)
                                    spotShadowColor = Color.Black.copy(alpha = 0.70f)
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            Canvas(modifier = Modifier.matchParentSize()) {
                                for (i in 1..3) {
                                    drawCircle(
                                        color = Color.Cyan.copy(alpha = glowAlpha / i),
                                        radius = radiusPx * (i / 3f),
                                        center = center
                                    )
                                }
                            }

                            Text(
                                text = emoji,
                                fontSize = 32.sp
                            )
                        }
                    }
                    
            
            
            )""".trimIndent()
            )

            KotlinCode(
                code = """
                    //Bouncing Button Infinite Animation

                    @Composable
                    private fun BouncingButton(
                        onClick: () -> Unit,
                        text: String
                    ) {
                        val infiniteTransition = rememberInfiniteTransition()
                        val buttonScale by infiniteTransition.animateFloat(
                            initialValue = 1f,
                            targetValue = 1.1f,
                            animationSpec = infiniteRepeatable(
                                animation = tween(1000, easing = LinearEasing),
                                repeatMode = RepeatMode.Reverse
                            )
                        )

                        Button(
                            onClick = onClick,
                            colors = ButtonDefaults.buttonColors(
                                containerColor = Color.White,
                                contentColor = MaterialTheme.colorSchemeCustom.alwaysBlue
                            ),
                            modifier = Modifier
                                .graphicsLayer {
                                    scaleX = buttonScale
                                    scaleY = buttonScale
                                }
                                .height(48.dp)
                                .width(200.dp)
                        ) {
                            Text(
                                text = text,
                                style = MaterialTheme.typography.labelLarge,
                                fontWeight = FontWeight.Bold
                            )
                        }
                    }
                    
                    //Bouncing Button Infinite With Scale Animation
                    val buttonInteractionSource = remember { MutableInteractionSource() }
                    val isButtonPressed by buttonInteractionSource.collectIsPressedAsState()
                    val buttonScale by animateFloatAsState(
                        targetValue = if (isButtonPressed) 0.95f else 1f,
                        animationSpec = tween(200),
                        label = "buttonScale"
                    )

                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(top = 16.dp)
                            .offset(
                                y = rememberInfiniteTransition(label = "floatButton").animateFloat(
                                    initialValue = 0f,
                                    targetValue = -6f,
                                    animationSpec = infiniteRepeatable(
                                        animation = tween(durationMillis = 1400, easing = LinearEasing),
                                        repeatMode = RepeatMode.Reverse
                                    ),
                                    label = "offsetAnim"
                                ).value.dp
                            )
                            .graphicsLayer {
                                scaleX = buttonScale
                                scaleY = buttonScale
                            }
                            .clip(RoundedCornerShape(12.dp))
                            .background(
                                Brush.horizontalGradient(
                                    listOf(
                                        Color(0xFFE56C63),
                                        Color(0xFFC86A6B)
                                    )
                                )
                            )
                            .clickable(
                                interactionSource = buttonInteractionSource,
                                indication = rememberGlassyRippleIndication()
                            ) {
                                println("🔥 Button Clicked!")
                            },
                            contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = "Start Workout with AI Coach",
                            color = Color.White,
                            fontWeight = FontWeight.Bold,
                            fontSize = 16.sp
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val animationValue by rememberInfiniteTransition().animateBreathing()

                    Box(
                        modifier = modifier
                            .graphicsLayer {
                                scaleX = animationValue
                                scaleY = animationValue
                            }
                        ) { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Roated
                    
                    val infiniteTransition = rememberInfiniteTransition()
                    
                    // Creates a child animation of float type as a part of the [InfiniteTransition].
                    val rotate by infiniteTransition.animateFloat(
                        initialValue = 0f,
                        targetValue = 90f,
                        animationSpec = infiniteRepeatable(
                            // Infinitely repeating a 1000ms tween animation using default easing curve.
                            animation = tween(durationMillis = 1000, delayMillis = 5000),
                            initialStartOffset = StartOffset(5000),
                            // After each iteration of the animation (i.e. every 1000ms), the animation will
                            // start again from the [initialValue] defined above.
                            // This is the default [RepeatMode]. See [RepeatMode.Reverse] below for an
                            // alternative.
                            repeatMode = RepeatMode.Restart
                        )
                    )
            """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Animation Image Alpha
                    @Composable
                    fun SplashScreen(
                        onNavigateToHome: () -> Unit
                    ) {
                        LaunchedEffect(Unit) {
                            delay(6000)
                            onNavigateToHome()
                        }

                        SplashView()
                    }

                    @Composable
                    private fun SplashView() {
                        val infiniteTransition = rememberInfiniteTransition(label = "splash_logo")
                        val alpha by infiniteTransition.animateFloat(
                            label = "splash_logo_animated",
                            initialValue = 0f,
                            targetValue = 1f,
                            animationSpec = infiniteRepeatable(
                                animation = tween(1000, easing = LinearEasing),
                                repeatMode = RepeatMode.Reverse
                            )
                        )

                        Column(
                            verticalArrangement = Arrangement.Center,
                            horizontalAlignment = Alignment.CenterHorizontally,
                            modifier = Modifier
                                .fillMaxSize()
                                .background(MaterialTheme.colorScheme.background)
                        ) {
                            Image(
                                painter = painterResource(Res.drawable.logo),
                                contentScale = ContentScale.Crop,
                                contentDescription = null,
                                modifier = Modifier.width(140.dp).alpha(alpha)
                            )
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "AnimatedContent - Transition Between States (togetherWith, +, .togetherWith) (fadeIn - fadeOut, scaleIn - scaleOut, spring, slideInVertically - slideOutVertically, slideInHorizontally - slideOutHorizontally), Size - (SizeTransform)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To animate transitions between different Ul layouts/composables based on a state change.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When statement in AnimatedContent, passing in the new state as targetState. AnimatedContent lambda, it’s important to use targetState parameter and not the state parameter, as this will ensure the animation runs smoothly when it needs to quickly switch between different states. Optional transitionSpec parameter that can be found on the AnimatedContent composable. By default, the transitionSpec will perform a customized fade and scale of the content for the enter and exit transitions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/darkpandawarrior/Mileway
            KotlinCode(
                code = """
                    AnimatedContent(
                        targetState = ,
                        transitionSpec = {
                            if (targetState != null) {
                                (slideInHorizontally { it } + fadeIn()) togetherWith (slideOutHorizontally { -it } + fadeOut())
                            } else {
                                (slideInHorizontally { -it } + fadeIn()) togetherWith (slideOutHorizontally { it } + fadeOut())
                            }
                        },
                        label = "filterDrillDown",
                    ) { }
                    """.trimIndent()
            )

            //https://www.linkedin.com/in/hossam-el-gmmal
            //https://github.com/PocketShopiTi/pocket-shop.git
            KotlinCode(
                code = """
                    //Add to Cart Use (Add to Cart, Added)
                    
                    AnimatedContent(
                        targetState = ,
                        transitionSpec = {
                            (fadeIn() + scaleIn(initialScale = 0.8f)) togetherWith
                                (fadeOut() + scaleOut(targetScale = 0.8f))
                        },
                        label = "addToCartFeedback",
                    ) { }
                    """.trimIndent()
            )

            //https://github.com/alexiyous/ramadhan-camp-kmp.git
            KotlinCode(
                code = """
                    @Composable
                    fun Loader(
                        modifier: Modifier = Modifier,
                        isSemiTransparent: Boolean = false,
                        color: Color = accentPrimary,
                    ) {
                        Box(
                            modifier = modifier
                                .modifyIf(isSemiTransparent) {
                                    background(accentEmerald)
                                }
                                .pointerInput(Unit) {
                                    detectDragGestures { _, _ -> }
                                    detectTapGestures { }
                                },
                        ) {
                            CircularProgressIndicator(
                                modifier = Modifier
                                    .size(24.dp)
                                    .align(Alignment.Center),
                                color = color,
                                strokeWidth = 2.dp,
                            )
                        }
                    }
                    
                    private sealed interface AnimateContentState {
                        data object Loading : AnimateContentState
                        class Error(val error: ErrorEmptyState) : AnimateContentState
                        data object Success : AnimateContentState
                    }
                    
                    @Composable
                    fun HomeMainScreenContent(
                        state: HomeState,
                        action: (HomeEvent) -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        Box(
                            modifier = modifier
                                .background(RamadhanTheme.colors.bgPrimary)
                        ) {
                            AnimatedContent(
                                modifier = Modifier.fillMaxSize(),
                                targetState = when {
                                    state.isLoading -> AnimateContentState.Loading
                                    state.emptyErrorState != null -> AnimateContentState.Error(state.emptyErrorState)
                                    state.appError != null -> AnimateContentState.Error(state.appError.toErrorEmptyState())
                                    else -> AnimateContentState.Success
                                }
                            ) { targetState ->
                                when(targetState) {
                                    is AnimateContentState.Error -> HomeMainErrorContent()
                                    AnimateContentState.Loading -> Loader(
                                        modifier = Modifier.fillMaxSize()
                                    )
                                    AnimateContentState.Success -> HomeMainSuccessContent()
                                }
                            }
                        }
                    }
                    
                    data class HomeState(
                        val isLoading: Boolean = false,
                        val appError: AppError? = null,
                        val emptyErrorState: ErrorEmptyState? = null,
                        val pickedThroughPicker: Boolean = false,
                        val screenData: HomeScreenUiModel = HomeScreenUiModel(),
                        val isPermissionDenied: Boolean = false
                    )
                    
                    data class HomeScreenUiModel(
                        val city: String = "",
                        val country: String = "",
                        val currentDate: String = ""
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Content Height Animation
                    
                     AnimatedContent(
                        modifier = Modifier.align(Alignment.BottomCenter),
                        targetState = isLastPage,
                        label = "",
                        transitionSpec = {
                            (slideInVertically { height ->
                                height
                            } + fadeIn()).togetherWith(slideOutVertically { height ->
                                 -height
                            } + fadeOut()).using(SizeTransform(clip = false))
                        }) { isLastPage -> }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Change Icon Animation 
                    
                    AnimatedContent(
                        modifier = Modifier
                            .padding(16.dp)
                            .align(Alignment.TopEnd),
                        targetState = ,
                        contentAlignment = Alignment.Center,
                        transitionSpec = remember {
                            { fadeIn() + scaleIn() togetherWith scaleOut() + fadeOut() }
                        }
                    ) { copied -> }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Icon Change 
                    
                    val damping = Spring.DampingRatioMediumBouncy
                    AnimatedContent(
                        targetState = currentProfile,
                        contentAlignment = Alignment.Center,
                        transitionSpec = {
                            val contentEnter = fadeIn(spring(damping)) + slideInVertically(spring(damping)) { -it / 2 }
                            val contentExit = fadeOut(spring(damping)) + slideOutVertically(spring(damping)) { it / 2 }
                            contentEnter.togetherWith(contentExit)
                        }
                    ) { currentProfile ->
                        val icon = if (currentProfile == Profile.LIGHT) Icons.Default.DarkMode else Icons.Default.LightMode
                        Icon(imageVector = icon, contentDescription = null)
                    }
                    
                    //Spring Button Animation OnBoarding
                    AnimatedVisibility(
                        visible =,
                        enter = expandVertically(
                            animationSpec = spring(
                                dampingRatio = Spring.DampingRatioHighBouncy,
                                stiffness = Spring.StiffnessLow
                            )
                        )
                    ) { }
                    """.trimIndent()
            )

            //https://github.com/Team-Bigong/oguri-kotlin-multiplatform.git
            KotlinCode(
                code = """
                    //AnimatedContent checked isForward or not
                    //Onboarding Screen
                    
                    private const val ONBOARDING_SCREEN_ANIMATION_DURATION_MILLIS = 260
                    
                    AnimatedContent(
                        targetState = onboardingUiState.step,
                        modifier = Modifier.fillMaxWidth(),
                        transitionSpec = {
                            val isForward = targetState.ordinal > initialState.ordinal
                            (
                                slideInHorizontally(
                                    animationSpec = tween(durationMillis = ONBOARDING_SCREEN_ANIMATION_DURATION_MILLIS),
                                    initialOffsetX = { fullWidth -> if (isForward) fullWidth else -fullWidth },
                                ) + fadeIn(animationSpec = tween(durationMillis = ONBOARDING_SCREEN_ANIMATION_DURATION_MILLIS))
                            ).togetherWith(
                                slideOutHorizontally(
                                    animationSpec = tween(durationMillis = ONBOARDING_SCREEN_ANIMATION_DURATION_MILLIS),
                                    targetOffsetX = { fullWidth -> if (isForward) -fullWidth / 3 else fullWidth / 3 },
                                ) + fadeOut(animationSpec = tween(durationMillis = ONBOARDING_SCREEN_ANIMATION_DURATION_MILLIS)),
                            )
                        },
                        label = "onboarding_step_content",
                    ) { onboardingStep ->
                    
                    }
                    """.trimIndent()
            )

            //https://github.com/Nubet/studymood-app-public.git (Onboarding good using AnimatedContent)

            //https://github.com/ProjektMagma/MagmaQuiz.git
            KotlinCode(
                code = """
                        //Quiz Questions List Animation
                        
                        AnimatedContent(
                            targetState = gameState,
                            transitionSpec = {
                                slideInHorizontally { fullWidth -> fullWidth } togetherWith
                                        slideOutHorizontally { fullWidth -> -fullWidth }
                            },
                            contentKey = { state -> state.currentQuestionIndex }
                        ) { currentState -> 
                        
                        }
                    """.trimIndent()
            )

            //https://github.com/percy-g2/kmp_utxo.git
            KotlinCode(
                code = """
                    //Quiz Animation
                    
                    AnimatedContent(
                        targetState = currentTradingPair,
                        transitionSpec = {
                            val currentIndex = tradingPairList.indexOf(initialState)
                            val targetIndex = tradingPairList.indexOf(targetState)
                            val isNext = targetIndex > currentIndex

                           slideInHorizontally(
                                initialOffsetX = { fullWidth -> if (isNext) fullWidth else -fullWidth },
                                    animationSpec = tween(300)
                               ) + fadeIn(animationSpec = tween(300)) togetherWith
                                    slideOutHorizontally(
                                        targetOffsetX = { fullWidth -> if (isNext) -fullWidth else fullWidth },
                                            animationSpec = tween(300)
                                        ) + fadeOut(animationSpec = tween(300))
                               },
                               label = "Trading Pair Content Animation"
                     ) { }
                    """.trimIndent()
            )

            //https://github.com/codewithbilalahmad/PillTime.git
            KotlinCode(
                code = $$"""
                     //DatePicker Month Selection 
                     
                     AnimatedContent(targetState = "$month $visibleYear", transitionSpec = {
                        (slideInVertically(initialOffsetY = { it }) + expandVertically(initialHeight = { it }) + fadeIn()) togetherWith (slideOutVertically(
                            targetOffsetY = { -it }) + shrinkVertically(targetHeight = { -it }) + fadeOut())
                    }, label = "monthYearAnimation") { monthYear ->
                        Text(
                            text = monthYear
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                     //Dialog Animation
                     
                     Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        AnimatedContent(
                            targetState = ,
                            modifier = Modifier.background(FORM.PURE, FORM.QJYJ),
                            transitionSpec = {
                                (scaleIn(tween(100))) togetherWith (
                                    scaleOut(tween(100)))
                            },
                            contentAlignment = Alignment.Center,
                        ) {

                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @OptIn(ExperimentalAnimationApi::class)
                    @Composable
                    fun AnimateSkillText(
                        modifier: Modifier = Modifier
                    ) {
                        val messages = listOf(
                            "Kotlin Multiplatform Developer",
                            "Flutter",
                            "Jetpack Compose"
                        )

                        var index by remember { mutableStateOf(0) }

                        LaunchedEffect(Unit) {
                            while (true) {
                                delay(3000)
                                index = (index + 1) % messages.size
                            }
                        }

                        AnimatedContent(
                            targetState = messages[index],
                            transitionSpec = remember { { fadeIn().togetherWith(fadeOut()) } }
                        ) { targetMessage ->
                            Text(
                                modifier = modifier,
                                text = targetMessage,
                                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.5f),
                                style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.SemiBold)
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        AnimatedContent(
                            modifier = Modifier
                                .padding(bottom = NavigationBarHeight)
                                .fillMaxSize(),
                            targetState = creation,
                            transitionSpec = remember { { fadeIn() togetherWith fadeOut() } },
                            label = "GridItemDetailsLayoutAnimatedContent"
                        ) { sharedCreation -> 
                        
                        }
                    """.trimIndent()
            )

            //https://github.com/Kyriakos-Georgiopoulos/Navigation3.git
            KotlinCode(
                code = $$"""
                    //Fab Counter Shopping Items
                    
                    @Composable
                    internal fun HomeTabScreen(
                        state: HomeTabState,
                        onOpenDetails: (String) -> Unit,
                        onToggleFavorite: (String) -> Unit,
                        onToggleCart: (String) -> Unit,
                        onCartClick: () -> Unit,
                    ) {
                        val listState = rememberLazyListState()
                        val fabVisible = !listState.isScrollInProgress
                        val cartCount = remember(state.placeholders) { state.placeholders.count { it.isInCart } }

                        val fabAlpha by animateFloatAsState(
                            targetValue = if (fabVisible) 1f else 0f,
                            animationSpec = spring(dampingRatio = 0.9f, stiffness = Spring.StiffnessLow),
                            label = "cartFabAlpha",
                        )

                        val fabScale = remember { Animatable(0.92f) }

                        LaunchedEffect(fabVisible) {
                            if (fabVisible) {
                                fabScale.snapTo(0.86f)
                                fabScale.animateTo(
                                    targetValue = 1f,
                                    animationSpec = spring(dampingRatio = 0.55f, stiffness = Spring.StiffnessMedium),
                                )
                            } else {
                                fabScale.animateTo(
                                    targetValue = 0.92f,
                                    animationSpec = spring(dampingRatio = 0.9f, stiffness = Spring.StiffnessLow),
                                )
                            }
                        }

                        BoxWithConstraints(Modifier.fillMaxSize()) {
                            val density = LocalDensity.current

                            val fabSizeDp = 64.dp
                            val endPaddingDp = 16.dp
                            val bottomPaddingDp = 18.dp

                            val dragOffsetSaver: Saver<Offset, List<Float>> = remember {
                                Saver(
                                    save = { listOf(it.x, it.y) },
                                    restore = { Offset(it[0], it[1]) },
                                )
                            }

                            var dragOffset by rememberSaveable(stateSaver = dragOffsetSaver) {
                                mutableStateOf(Offset.Zero)
                            }

                            val maxWidthPx = with(density) { maxWidth.toPx() }
                            val maxHeightPx = with(density) { maxHeight.toPx() }
                            val fabSizePx = with(density) { fabSizeDp.toPx() }
                            val endPaddingPx = with(density) { endPaddingDp.toPx() }
                            val bottomPaddingPx = with(density) { bottomPaddingDp.toPx() }

                            val anchorX = (maxWidthPx - endPaddingPx - fabSizePx).coerceAtLeast(0f)
                            val anchorY = (maxHeightPx - bottomPaddingPx - fabSizePx).coerceAtLeast(0f)

                            val maxX = (maxWidthPx - fabSizePx).coerceAtLeast(0f)
                            val maxY = (maxHeightPx - fabSizePx).coerceAtLeast(0f)

                            val currentX = (anchorX + dragOffset.x).coerceIn(0f, maxX)
                            val currentY = (anchorY + dragOffset.y).coerceIn(0f, maxY)

                            HomePlaceholdersList(
                                placeholders = state.placeholders,
                                onPlaceholderClick = onOpenDetails,
                                onToggleFavorite = onToggleFavorite,
                                onToggleCart = onToggleCart,
                                listState = listState,
                                modifier = Modifier.fillMaxSize(),
                            )

                            Box(
                                modifier = Modifier
                                    .offset { IntOffset(currentX.roundToInt(), currentY.roundToInt()) }
                                    .pointerInput(anchorX, anchorY, maxX, maxY) {
                                        detectDragGestures { change, dragAmount ->
                                            change.consume()
                                            val proposed =
                                                Offset(dragOffset.x + dragAmount.x, dragOffset.y + dragAmount.y)
                                            val px = (anchorX + proposed.x).coerceIn(0f, maxX)
                                            val py = (anchorY + proposed.y).coerceIn(0f, maxY)
                                            dragOffset = Offset(px - anchorX, py - anchorY)
                                        }
                                    }
                            ) {
                                CartFabWithBadge(
                                    count = cartCount,
                                    fabVisible = fabVisible,
                                    fabAlpha = fabAlpha,
                                    fabScale = fabScale.value,
                                    onClick = onCartClick,
                                )
                            }
                        }
                    }

                    @Preview(name = "HomeTabScreen", showBackground = true)
                    @Composable
                    private fun HomeTabScreenPreview() {
                        HomeTabScreen(
                            state = HomeTabState(
                                placeholders = List(12) { index ->
                                    PlaceholderUi(
                                        id = "ph_$index",
                                        isFavorite = index % 3 == 0,
                                        isInCart = index % 4 == 0,
                                    )
                                }
                            ),
                            onOpenDetails = {},
                            onToggleFavorite = {},
                            onToggleCart = {},
                            onCartClick = {},
                        )
                    }

                    @Preview(name = "HomeTabScreen - Empty cart", showBackground = true)
                    @Composable
                    private fun HomeTabScreenEmptyCartPreview() {
                        HomeTabScreen(
                            state = HomeTabState(
                                placeholders = List(10) { index ->
                                    PlaceholderUi(
                                        id = "ph_$index",
                                        isFavorite = index % 2 == 0,
                                        isInCart = false,
                                    )
                                }
                            ),
                            onOpenDetails = {},
                            onToggleFavorite = {},
                            onToggleCart = {},
                            onCartClick = {},
                        )
                    }
                    
                    @Composable
                    internal fun CartFabWithBadge(
                        count: Int,
                        fabVisible: Boolean,
                        fabAlpha: Float,
                        fabScale: Float,
                        onClick: () -> Unit,
                        modifier: Modifier = Modifier,
                    ) {
                        val canShowBadge = fabVisible && count > 0

                        val badgeAppearScale = remember { Animatable(0f) }
                        var badgeAllowed by remember { mutableStateOf(false) }
                        var suppressNextOdometer by remember { mutableStateOf(false) }

                        LaunchedEffect(fabVisible) {
                            if (!fabVisible) {
                                badgeAllowed = false
                            } else {
                                badgeAllowed = false
                                delay(240)
                                badgeAllowed = true
                            }
                        }

                        LaunchedEffect(canShowBadge, badgeAllowed) {
                            val show = canShowBadge && badgeAllowed
                            if (!show) {
                                badgeAppearScale.snapTo(0f)
                            } else {
                                suppressNextOdometer = true
                                badgeAppearScale.snapTo(0f)
                                badgeAppearScale.animateTo(
                                    1f,
                                    animationSpec = spring(
                                        dampingRatio = 0.48f,
                                        stiffness = Spring.StiffnessMedium,
                                    ),
                                )
                            }
                        }

                        Box(modifier = modifier) {
                            Box(
                                modifier = Modifier
                                    .size(64.dp)
                                    .graphicsLayer {
                                        alpha = fabAlpha
                                        scaleX = fabScale
                                        scaleY = fabScale
                                    }
                                    .clip(CircleShape)
                                    .background(MaterialTheme.colorScheme.tertiaryContainer)
                                    .clickable(enabled = fabAlpha > 0.01f, onClick = onClick),
                                contentAlignment = Alignment.Center
                            ) {
                                Icon(
                                    imageVector = Icons.Rounded.ShoppingCart,
                                    contentDescription = null,
                                    tint = MaterialTheme.colorScheme.onTertiaryContainer,
                                    modifier = Modifier.size(28.dp)
                                )
                            }

                            Box(
                                modifier = Modifier
                                    .align(Alignment.TopEnd)
                                    .offset(x = 6.dp, y = (-6).dp)
                                    .graphicsLayer {
                                        alpha = fabAlpha * badgeAppearScale.value
                                        scaleX = badgeAppearScale.value
                                        scaleY = badgeAppearScale.value
                                    }
                            ) {
                                CartBadgeOdometer(
                                    count = count,
                                    suppressOdometerOnce = suppressNextOdometer,
                                    onOdometerSuppressed = { suppressNextOdometer = false },
                                )
                            }
                        }
                    }

                    @Composable
                    private fun CartBadgeOdometer(
                        count: Int,
                        suppressOdometerOnce: Boolean,
                        onOdometerSuppressed: () -> Unit,
                        modifier: Modifier = Modifier,
                    ) {
                        val dark = isSystemInDarkTheme()
                        val bg = MaterialTheme.colorScheme.errorContainer.copy(alpha = if (dark) 0.96f else 0.92f)
                        val fg = MaterialTheme.colorScheme.onErrorContainer

                        val value = count.coerceAtMost(99).toString()

                        if (suppressOdometerOnce) {
                            LaunchedEffect(value) { onOdometerSuppressed() }
                        }

                        Box(
                            modifier = modifier
                                .size(26.dp)
                                .clip(CircleShape)
                                .background(bg),
                            contentAlignment = Alignment.Center
                        ) {
                            if (suppressOdometerOnce) {
                                Text(
                                    text = value,
                                    color = fg,
                                    style = MaterialTheme.typography.labelSmall,
                                    textAlign = TextAlign.Center,
                                    maxLines = 1,
                                )
                            } else {
                                AnimatedContent(
                                    targetState = value,
                                    transitionSpec = { odometer(initialState, targetState) },
                                    label = "cartBadgeOdometer",
                                ) { v ->
                                    Text(
                                        text = v,
                                        color = fg,
                                        style = MaterialTheme.typography.labelSmall,
                                        textAlign = TextAlign.Center,
                                        maxLines = 1,
                                    )
                                }
                            }
                        }
                    }

                    private fun odometer(from: String, to: String): ContentTransform {
                        val a = from.toIntOrNull() ?: 0
                        val b = to.toIntOrNull() ?: 0
                        val increasing = b > a

                        val enter = slideInVertically(
                            animationSpec = spring(dampingRatio = 0.85f, stiffness = Spring.StiffnessMediumLow),
                            initialOffsetY = { full -> if (increasing) -full else full }
                        ) + fadeIn(animationSpec = spring(dampingRatio = 0.9f, stiffness = Spring.StiffnessLow))

                        val exit = slideOutVertically(
                            animationSpec = spring(dampingRatio = 0.9f, stiffness = Spring.StiffnessLow),
                            targetOffsetY = { full -> if (increasing) full else -full }
                        ) + fadeOut(animationSpec = spring(dampingRatio = 0.9f, stiffness = Spring.StiffnessLow))

                        return enter togetherWith exit
                    }
                    """.trimIndent()
            )

            //https://github.com/codewithbilalahmad/PillTime.git
            KotlinCode(
                code = $$"""
                        Column(
                            modifier = modifier.fillMaxWidth(),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .animateContentSize(animationSpec = MaterialTheme.motionScheme.fastEffectsSpec()),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.SpaceBetween
                            ) {
                                AnimatedContent(targetState = "$month $visibleYear", transitionSpec = {
                                    (slideInVertically(initialOffsetY = { it }) + expandVertically(initialHeight = { it }) + fadeIn()) togetherWith (slideOutVertically(
                                        targetOffsetY = { -it }) + shrinkVertically(targetHeight = { -it }) + fadeOut())
                                }, label = "monthYearAnimation") { monthYear ->
                                    Text(
                                        text = monthYear,
                                        modifier = Modifier.weight(1f),
                                        style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold)
                                    )
                                }
                            }
                        }
                    """.trimIndent()
            )

            //https://github.com/DeFerence3/broke-compose.git
            KotlinCode(
                code = """
                    //Horizontal left and right animation
                    
                    @Composable
                    fun MonthSwitcherCard(
                        currentMonth: String,
                        onPrevious: () -> Unit,
                        onNext: () -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        var monthDirection by remember { mutableIntStateOf(0) } // -1 for prev, +1 for next

                        Surface(
                            modifier = modifier
                                .fillMaxWidth()
                                .padding(vertical = 6.dp),
                            tonalElevation = 2.dp,
                            shape = RoundedCornerShape(12.dp),
                            color = MaterialTheme.colorScheme.surfaceContainerHighest
                        ) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(horizontal = 12.dp, vertical = 8.dp),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                IconButton(onClick = {
                                    monthDirection = -1
                                    onPrevious()
                                }) {
                                    Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Previous Month")
                                }

                                AnimatedContent(
                                    targetState = currentMonth,
                                    transitionSpec = {
                                        if (monthDirection > 0) {
                                            slideInHorizontally(initialOffsetX = { it }) + fadeIn() togetherWith
                                                    slideOutHorizontally(targetOffsetX = { -it }) + fadeOut()
                                        } else {
                                            slideInHorizontally(initialOffsetX = { -it }) + fadeIn() togetherWith
                                                    slideOutHorizontally(targetOffsetX = { it }) + fadeOut()
                                        }
                                    },
                                    label = "Month Switch Animation"
                                ) { targetMonth ->
                                    Text(
                                        text = targetMonth,
                                        style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Medium)
                                    )
                                }

                                IconButton(onClick = {
                                    monthDirection = 1
                                    onNext()
                                }) {
                                    Icon(Icons.AutoMirrored.Filled.ArrowForward, contentDescription = "Next Month")
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun AnimatedIntText(
                        modifier: Modifier = Modifier,
                        value: Int,
                        label: String,
                        animationDurationMs: Int = 300,
                        style: TextStyle = LocalTextStyle.current,
                    ) {
                        AnimatedContent(
                            modifier = modifier,
                            targetState = value,
                            transitionSpec = {
                                getProgressAnimation(animationDurationMs)
                            },
                            label = label,
                        ) { count ->
                            Text(
                                text = count.toString(),
                                style = style,
                            )
                        }
                    }

                    private fun getProgressAnimation(durationMs: Int): ContentTransform = (
                        fadeIn(animationSpec = tween(durationMs)) + slideInVertically(
                            animationSpec = tween(durationMs),
                            initialOffsetY = { it },
                        )
                    ) togetherWith (
                        fadeOut(animationSpec = tween(durationMs)) + slideOutVertically(
                            animationSpec = tween(durationMs),
                            targetOffsetY = { -it },
                        )
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    AnimatedContent(
                        targetState = "$visibleMonthName $visibleYear",
                        transitionSpec = {
                            slideInVertically { it } + fadeIn() togetherWith
                                    slideOutVertically { -it } + fadeOut()
                        },
                        label = "monthTitle", modifier = Modifier.weight(1f)
                    ) { title ->
                        Text(
                            text = title,
                            style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold)
                        )
                    }
                    """.trimIndent()
            )

            //https://github.com/RamanshSharma07/KMP-E-Commerce.git
            KotlinCode(
                code = """
                    IconButton(
                    onClick = {
                       
                    },
                    modifier = Modifier
                    .padding(8.dp)
                    .clip(CircleShape)
                    .size(32.dp)
                    .background(purple)
                    ) {
                        AnimatedContent(
                            targetState = justAdded,
                            transitionSpec = {
                                (slideInVertically { height -> height } + fadeIn())
                                    .togetherWith(slideOutVertically { height -> -height } + fadeOut())
                            },
                            label = "Add to Cart Icon Animation"
                        ) {
                            if (justAdded) {
                               
                            } else {
                                
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/KenilBalar/Animation-Hub
            KotlinCode(
                code = $$"""
                    //Counter Animation 
                    
                    @OptIn(ExperimentalAnimationApi::class)
                    @Composable
                    fun AnimatedContent() {
                        var count by remember { mutableIntStateOf(0) }
                        var lastCount by remember { mutableIntStateOf(0) }

                        Column(modifier = Modifier.fillMaxSize(), horizontalAlignment = Alignment.CenterHorizontally) {
                            Spacer(Modifier.height(20.dp))
                            Card(shape = RoundedCornerShape(30)) {
                                Row(
                                    Modifier
                                        .padding(16.dp), verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Text("-", fontSize = 36.sp, textAlign = TextAlign.Center, modifier = Modifier
                                        .size(44.dp)
                                        .clip(shape = RoundedCornerShape(50))
                                        .clickable {
                                            lastCount = count; count--
                                        })

                                    AnimatedContent(
                                        targetState = count,
                                        transitionSpec = {
                                            (slideInVertically { if (count > lastCount) it else -it } + fadeIn()).togetherWith(
                                                slideOutVertically { if (count > lastCount) -it else it } + fadeOut()
                                            )
                                        }, label = "counter"
                                    ) { value ->
                                        Box(modifier = Modifier.size(150.dp), contentAlignment = Alignment.Center) {
                                            Text("$value", fontSize = 128.sp, fontWeight = FontWeight.Bold)
                                        }
                                    }

                                    Text("+", fontSize = 36.sp, textAlign = TextAlign.Center, modifier = Modifier
                                        .size(44.dp)
                                        .clip(shape = RoundedCornerShape(50))
                                        .clickable {
                                            lastCount = count; count++
                                        })
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Bookmark Animation
                    
                    @Composable
                    fun SaveButton(
                        isSaved: Boolean,
                        onClick: () -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        Box(
                            modifier = modifier
                                .clip(CircleShape)
                                .size(40.dp)
                                .background(Theme.color.surfaces.onSurfaceAt3)
                                .clickable(onClick = onClick),
                            contentAlignment = Alignment.Center
                        ) {
                            AnimatedContent(
                                targetState = isSaved,
                                transitionSpec = {
                                    scaleIn(animationSpec = tween(300)).togetherWith(scaleOut(animationSpec = tween(300)))
                                }
                            ) {
                                Icon(
                                    painter = painterResource(
                                        if (it) {
                                            Res.drawable.ic_bookmark_big_filled
                                        } else {
                                            Res.drawable.ic_bookmark_big
                                        }
                                    ),
                                    contentDescription = stringResource(Res.string.save),
                                    tint = Theme.color.surfaces.textColor,
                                    modifier = Modifier
                                        .size(20.dp)
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Counter animation

                    
                    @OptIn(ExperimentalAnimationApi::class)
                    @Composable
                    fun AnimatedContentExample(modifier: Modifier = Modifier) {
                        var count by remember { mutableIntStateOf(0) }

                        Column(
                            verticalArrangement = Arrangement.Center,
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Button(onClick = { count-- }) {
                                Text(text = "Decrease count")
                            }
                            
                            //1
                            AnimatedContent(
                                targetState = count,
                                transitionSpec = {
                                    if (targetState > initialState) {
                                        slideInVertically { fullHeight -> fullHeight } + fadeIn() with slideOutVertically { fullHeight ->
                                            -fullHeight
                                        }
                                    } else {
                                        slideInVertically { fullHeight -> -fullHeight } + fadeIn() with slideOutVertically { fullHeight ->
                                            fullHeight
                                        }
                                    }.using(SizeTransform(clip = true))
                                }
                                
                            //2
                            AnimatedContent(
                                targetState = count,
                                transitionSpec = {
                                    if (targetState > initialState) {
                                        (slideInVertically { height -> height } + fadeIn()) togetherWith
                                                (slideOutVertically { height -> -height } + fadeOut())
                                    } else {
                                        (slideInVertically { height -> -height } + fadeIn()) togetherWith
                                                (slideOutVertically { height -> height } + fadeOut())
                                    }.using(SizeTransform(clip = false) )
                                }
                            ) {}
                                
                            ) {
                                Box(modifier = Modifier.size(180.dp), contentAlignment = Alignment.Center) {
                                    Text(text = count, style = MaterialTheme.typography.headlineLarge)
                                }
                            }

                            Button(onClick = { count++ }) {
                                Text(text = "Increase count")
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Icon animation

                    IconButton(onClick = {  }) {
                        AnimatedContent(
                            targetState = true,
                            transitionSpec = {
                                scaleIn(
                                    transformOrigin = TransformOrigin(1f, .5f)
                                ) togetherWith scaleOut(
                                    transformOrigin = TransformOrigin(0f, .5f)
                                ) using SizeTransform(clip = false)
                            },
                            label = ""
                        ) {
                            Icon(
                                painter = painterResource(id = if (it) R.drawable.ic_dark_mode else R.drawable.ic_light_mode),
                                contentDescription = "dark mode"
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun AnimatedText() {
                        val messages = listOf("Hello World!", "¡Hola Mundo!", "Hallo Welt!", "Ciao Mondo!","हालो!")
                        val messageColors = listOf(Color(0xFFfa8231), Color(0xFFc678dd), Color(0xFFa9b0bd), Color.White,Color(0xFFfa8231))
                        var currentMessageIndex by remember { mutableIntStateOf(0) }
                        val currentMessage = messages[currentMessageIndex]
                        val currentColor = messageColors[currentMessageIndex]

                        LaunchedEffect(Unit) {
                            while (true) {
                                delay(3000)
                                currentMessageIndex = (currentMessageIndex + 1) % messages.size
                            }
                        }

                        Box(
                            modifier = Modifier.fillMaxSize(),
                            contentAlignment = Alignment.Center
                        ) {
                            Row {
                                Text(
                                    text = "println(",
                                    color = Color(0xFF61afef),
                                    fontStyle = FontStyle.Normal,
                                    fontFamily = FontFamily.Monospace,
                                    fontSize = 24.sp
                                )
                                Text(
                                    text = "\"",
                                    color = Color(0xFFE3BB68),
                                    fontSize = 24.sp
                                )
                                AnimatedContent(
                                    targetState = currentMessage,
                                    transitionSpec = {
                                        slideInVertically { height -> height } + fadeIn() togetherWith
                                                slideOutVertically { height -> -height } + fadeOut()
                                    }, label = "currentMessageLabel"
                                ) { targetMessage ->
                                    Text(
                                        text = targetMessage,
                                        fontSize = 24.sp,
                                        color = currentColor
                                    )
                                }
                                Text(
                                    text = "\"",
                                    color = Color(0xFFE3BB68),
                                    fontSize = 24.sp
                                )
                                Text(
                                    text = ")",
                                    color = Color(0xFF61afef),
                                    fontSize = 24.sp
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                     //Vertical On/Off Animation
                    @Composable
                    fun FavAnimationView(name: String, modifier: Modifier = Modifier) {

                    var switchLabel by remember { mutableStateOf("Off") }
                    var fanTarget by remember { mutableFloatStateOf(0f) }

                    Column(
                        modifier = Modifier.fillMaxSize(),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.SpaceAround,
                    ){
                        AnimatedContent(
                            targetState = switchLabel,
                            transitionSpec = {
                            slideInVertically { height -> -height } togetherWith slideOutVertically { height -> height }
                        },
                            label = ""
                        ) { targetLabel ->
                            Text(
                                text = targetLabel,
                                fontSize = 50.sp,
                                modifier = Modifier
                                    .clickable(
                                        indication = null,
                                        interactionSource = remember {
                                            MutableInteractionSource()
                                        }
                                    ) {
                                        switchLabel = if (switchLabel == "Off") {
                                            fanTarget = 360f
                                            "On"
                                        }else{
                                            fanTarget = 0f
                                            "Off"
                                        }
                                    }
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    AnimatedContent(state) { targetState →
                        when (targetState) {
                            Loaded - /* your composable */ 
                            Loading -> /* your composable */
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun AnimatedContent() {
                        Column(
                            modifier = Modifier.fillMaxSize().background(Color(0xFF03122A))
                                .padding(top = 50.dp, bottom = 30.dp),
                            verticalArrangement = Arrangement.SpaceAround,
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {

                            val textList = listOf(
                                "Hello Developers! 🎉",
                                "Did you loved Kotlin Multiplatform? ❤️",
                                "Give a star on GitHub! ⭐",
                                "Follow me on GitHub! 🥳",
                                "Happy Coding! 🚀"
                            )

                            var currentTextIndex by remember { mutableIntStateOf(0) }

                            // Auto-advance text every 2 seconds
                            LaunchedEffect(textList.size) {
                                if (textList.isEmpty()) return@LaunchedEffect
                                
                                while (true) {
                                    delay(2000) // 2 seconds delay
                                    currentTextIndex = (currentTextIndex + 1) % textList.size
                                }
                            }

                            // Animated text content
                            Box(
                                modifier = Modifier.fillMaxSize(),
                                contentAlignment = Alignment.Center
                            ) {
                                AnimatedContent(
                                    targetState = currentTextIndex,
                                    transitionSpec = {
                                        ContentTransform(
                                            targetContentEnter = slideInVertically(
                                                animationSpec = tween(500),
                                                initialOffsetY = { it }
                                            ),
                                            initialContentExit = slideOutVertically(
                                                animationSpec = tween(500),
                                                targetOffsetY = { -it }
                                            )
                                        )
                                    },
                                    label = "text_animation"
                                ) { index ->
                                    Text(
                                        text = textList[index],
                                        fontSize = 18.sp,
                                        fontWeight = FontWeight.Medium,
                                        color = Color.White,
                                        textAlign = TextAlign.Center,
                                        modifier = Modifier.padding(horizontal = 16.dp)
                                    )
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/MustafaKoceerr/JustRelax.git
            KotlinCode(
                code = """
                    //Bottom to Up Animation (Player control)
                    
                    AnimatedContent(
                        targetState = isPlaying,
                        transitionSpec = {
                            (fadeIn(animationSpec = tween(ANIM_CONTENT_ENTER_DURATION, delayMillis = ANIM_CONTENT_DELAY)) +
                                scaleIn(initialScale = 0.92f, animationSpec = tween(ANIM_CONTENT_ENTER_DURATION, delayMillis = ANIM_CONTENT_DELAY)))
                            .togetherWith(fadeOut(animationSpec = tween(ANIM_CONTENT_EXIT_DURATION)))
                        },
                        label = "BottomContent"
                    ) { }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //Counter Animation
                    
                    //1
                    @Composable
                    fun AnimatedContentExample() {
                        var count by remember { mutableStateOf(0) }

                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Button(onClick = { count++ }) {
                                Text("Increase")
                            }

                            AnimatedContent(targetState = count) { targetCount ->
                                Text("Count: $targetCount")
                            }
                        }
                    }
                    
                    //2 
                    @Composable
                    fun AnimatedContentExample() {
                        var count by remember { mutableIntStateOf(0) }
                        
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            modifier = Modifier.padding(16.dp)
                        ) {
                            Text("4. AnimatedContent", style = MaterialTheme.typography.titleMedium)
                            Spacer(Modifier.height(16.dp))
                            
                            AnimatedContent(
                                targetState = count,
                                transitionSpec = {
                                    // Slide up + fade in for new content
                                    // Slide down + fade out for old content
                                    slideInVertically { height -> height } + fadeIn() togetherWith
                                            slideOutVertically { height -> -height } + fadeOut()
                                },
                                label = "count"
                            ) { targetCount ->
                                Text(
                                    text = "$targetCount"
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    AnimatedContent(
                        targetState =
                        transitionSpec = {
                            slideInVertically { height -> height } + fadeIn() togetherWith 
                            slideOutVertically { height -> height } + fadeOut()
                        }, label = ""
                    ) { isSignUp -> Column{} }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    //1
                    AnimatedVisibility(
                        visible =
                        enter = fadeIn() + expandVertically(),
                        exit = fadeOut() + shrinkVertically()
                    ) { }
                    
                    //2
                    enter = fadeIn(
                        animationSpec = tween (300)
                    ) + expandVertically(
                        animationSpec = tween (300)
                    )
                    
                    exit = fadeOut/
                        animationSpec = tween (200)
                    ) + shrinkVertically(
                        animationSpec = tween (200)
                    )    
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    AnimatedContent(
                        targetState =
                        transitionSpec = { fadeIn() togetherWith fadeOut() },
                        label = ""
                    ) { }
                    
                    AnimatedContent(
                        targetState = isLoading,
                        transitionSpec = {
                            fadeIn(tween(300)) togetherWith fadeOut(tween(300))
                        },
                        label = "BalanceTransition"
                    ) { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    AnimatedContent(
                        targetState = "",
                            transitionSpec = {
                                fadeIn(tween(220)).togetherWith(fadeOut(tween(220)))
                            }
                    ) { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    AnimatedContent(
                        targetState = paymentState,
                            transitionSpec = {
                                fadeIn(animationSpec = spring(stiffness = Spring.StiffnessLow)) togetherWith
                                fadeOut(animationSpec = spring(stiffness = Spring.StiffnessLow))
                            },
                            label = "Payment State Animation"
                    ) { }
                    """.trimIndent()
            )

            SpanText(
                text = "Crossfade",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When we want to switch one content to the other, if we just change the content.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun FavoriteIcon(
                        isFavorite: Boolean = false,
                        onClick: () -> Unit = {},
                        modifier: Modifier = Modifier
                    ) {
                        Box(
                            modifier = modifier
                                .size(32.dp)
                                .background(
                                    color = Theme.colorScheme.primary.primary,
                                    shape = CircleShape
                                )
                                .clickable(
                                    interactionSource = null,
                                    indication = null
                                ) { onClick() },
                            contentAlignment = Alignment.Center
                        ) {
                            Crossfade(targetState = isFavorite) { isFavorite ->
                                val favoriteIcon = if (isFavorite) Res.drawable.ic_favorite_filled
                                else Res.drawable.ic_favorite

                                Icon(
                                    painter = painterResource(favoriteIcon),
                                    contentDescription = stringResource(Res.string.favorites),
                                    modifier = Modifier.size(20.dp),
                                    tint = Theme.colorScheme.primary.onPrimary
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    /*@Composable
                    @Preview
                    fun App() {
                        var showSplash by remember { mutableStateOf(true) }

                        LaunchedEffect(Unit) {
                            delay(3000) // 3 seconds delay
                            showSplash = false
                        }

                        MaterialTheme {
                            ProfessionalScreenTransition(
                                showFirst = showSplash,
                                firstScreen = { SplashScreen() },
                                secondScreen = { LoginSignupScreen() },
                                animationDurationMs = 1000
                            )
                        }
                    }*/

                    @Composable
                    fun CrossFadeTransition(
                        showFirst: Boolean,
                        firstScreen: @Composable () -> Unit,
                        secondScreen: @Composable () -> Unit,
                        modifier: Modifier = Modifier,
                        animationDurationMs: Int = 600
                    ) {
                        Crossfade(
                            targetState = showFirst,
                            modifier = modifier,
                            animationSpec = tween(
                                durationMillis = animationDurationMs,
                                easing = FastOutSlowInEasing
                            )
                        ) { isFirst ->
                            if (isFirst) {
                                firstScreen()
                            } else {
                                secondScreen()
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/Knthcame/MyHealthKMP.git
            KotlinCode(
                code = """
                    @Composable
                    private fun HeartRateCard(
                        modifier: Modifier = Modifier,
                        heartRateUiState: HeartRateUiState,
                    ) {
                        val crossFadeAnimationDuration = 600

                        Card(modifier = modifier.fillMaxWidth()) {
                            Column(
                                modifier = Modifier.padding(16.dp),
                                verticalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                HeartRateCardHeader(
                                    heartRateUiState = heartRateUiState,
                                    crossFadeAnimationSpec = tween(crossFadeAnimationDuration),
                                )

                                Row(modifier = Modifier.height(IntrinsicSize.Min)) {
                                    HeartRateCardValue(
                                        modifier = Modifier.weight(0.5f),
                                        heartRateUiState = heartRateUiState,
                                        crossFadeAnimationSpec = tween(crossFadeAnimationDuration),
                                    )

                                    AnimatedVisibility(
                                        visible = heartRateUiState is HeartRateUiState.Available,
                                        enter = fadeIn(),
                                        exit = fadeOut(),
                                        modifier = Modifier.weight(0.5f)
                                    ) {
                                        HeartRateCardGraph(
                                            modifier = Modifier.fillMaxSize(),
                                            values = (heartRateUiState as HeartRateUiState.Available).graphValues,
                                        )
                                    }
                                }
                            }
                        }
                    }

                    @Composable
                    private fun HeartRateCardHeader(
                        modifier: Modifier = Modifier,
                        heartRateUiState: HeartRateUiState,
                        crossFadeAnimationSpec: FiniteAnimationSpec<Float> = tween(),
                    ) {
                        Row(
                            modifier = modifier,
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            val pulseTransition = rememberInfiniteTransition()

                            val pulseScale by pulseTransition.animateFloat(
                                initialValue = 1f,
                                targetValue = 0.8f,
                                animationSpec = infiniteRepeatable(
                                    animation = tween(durationMillis = 1000, easing = FastOutLinearInEasing),
                                    repeatMode = RepeatMode.Reverse,
                                )
                            )

                            Icon(
                                painter = painterResource(Res.drawable.heart_rate),
                                contentDescription = "Heart rate icon",
                                tint = Color.Red,
                                modifier = Modifier.size(20.dp).graphicsLayer {
                                    scaleX = pulseScale
                                    scaleY = pulseScale
                                },
                            )
                            Text(
                                text = stringResource(Res.string.dashboard_heart_rate_card_title),
                                style = MaterialTheme.typography.titleMedium,
                            )
                            Spacer(Modifier.weight(1f))

                            AnimatedVisibility(visible = heartRateUiState is HeartRateUiState.Available) {
                                Crossfade(
                                    targetState = (heartRateUiState as HeartRateUiState.Available).timeStamp,
                                    animationSpec = crossFadeAnimationSpec,
                                ) { targetState ->
                                    Text(
                                        text = targetState,
                                        style = MaterialTheme.typography.bodyMedium,
                                    )
                                }
                            }
                        }
                    }

                    @Composable
                    private fun HeartRateCardValue(
                        modifier: Modifier = Modifier,
                        heartRateUiState: HeartRateUiState,
                        crossFadeAnimationSpec: FiniteAnimationSpec<Float> = tween(),
                    ) {
                        val bpmText: String = when (heartRateUiState) {
                            is HeartRateUiState.Available -> heartRateUiState.value.toString()
                            HeartRateUiState.Missing -> stringResource(Res.string.placeholder)
                        }
                        Crossfade(
                            targetState = bpmText,
                            animationSpec = crossFadeAnimationSpec,
                            modifier = modifier,
                        ) { targetState ->
                            Text(buildAnnotatedString {
                                withStyle(
                                    SpanStyle(
                                        fontSize = 32.sp,
                                        fontWeight = FontWeight.ExtraBold,
                                    )
                                ) {
                                    append(targetState)
                                }
                                append(" ")
                                withStyle(SpanStyle(fontWeight = FontWeight.ExtraBold)) {
                                    append(stringResource(Res.string.unit_beats_per_minute))
                                }
                            })
                        }
                    }

                    @Composable
                    fun HeartRateCardGraph(modifier: Modifier, values: List<HeartRate>) {
                        val minInstant = remember(values) { values.minOf { item -> item.timeStamp } }
                        val maxInstant = remember(values) { values.maxOf { item -> item.timeStamp } }
                        val maxValue = 130
                        val minValue = 60

                        Canvas(modifier = modifier, onDraw = {
                            /** Calculates the horizontal offset from the start. */
                            fun calculateX(timeStamp: Instant): Float {
                                val totalWidth = (maxInstant - minInstant).inWholeMicroseconds
                                val localWidth = (timeStamp - minInstant).inWholeMicroseconds
                                val percentage = localWidth.toFloat() / totalWidth

                                return size.width * percentage
                            }

                            /** Calculates the vertical offset from the top. */
                            fun calculateY(bpm: Double): Float {
                                val totalHeight = maxValue - minValue
                                val localHeight = maxValue - bpm
                                val percentage = localHeight.toFloat() / totalHeight

                                return size.height * percentage
                            }

                            val path = Path()
                            val firstValue = values[0]
                            path.moveTo(x = calculateX(firstValue.timeStamp), y = calculateY(firstValue.bpm))

                            for (i in 1..values.lastIndex) {
                                val value = values[i]
                                path.lineTo(x = calculateX(value.timeStamp), y = calculateY(value.bpm))
                            }

                            drawPath(
                                path = path,
                                brush = SolidColor(Color.Red),
                                style = Stroke(
                                    width = 2.dp.toPx(),
                                    pathEffect = PathEffect.cornerPathEffect(10.dp.toPx())
                                ),
                            )
                        })
                    }
                    
                    sealed class HeartRateUiState {
                        data object Missing : HeartRateUiState()
                        data class Available(
                            val value: Double,
                            val timeStamp: String,
                            val graphValues: List<HeartRate>,
                        ) : HeartRateUiState()
                    }
                    
                    import kotlin.time.Instant

                    /**
                     * Contains the beats per minute ([bpm]) and the [timeStamp] when it was measured
                     *
                     * @property bpm The measured heart beats per minute.
                     * @property timeStamp The date & time when the measurement was done.
                     */
                    data class HeartRate(
                        val bpm: Double,
                        val timeStamp: Instant,
                    )
                    """.trimIndent()
            )

            //https://github.com/stoyan-vuchev/squircle-shape.git
            KotlinCode(
                code = """
                    //Side Menu List
                    
                    @Composable
                    fun NavigationListItem(
                        modifier: Modifier = Modifier,
                        label: String,
                        icon: () -> Any? = { null },
                        selected: () -> Boolean,
                        onSelected: () -> Unit
                    ) = Row(
                        modifier = Modifier
                            .then(modifier)
                            .pointerHoverIcon(icon = PointerIcon.Hand)
                            .defaultMinSize(minWidth = 88.dp, minHeight = 56.dp)
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = rememberRipple(),
                                onClick = onSelected,
                                role = Role.Tab
                            )
                            .padding(horizontal = 16.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.Start),
                        content = {

                            val color by animateColorAsState(
                                targetValue = if (selected()) Theme.colorScheme.accent
                                else Theme.colorScheme.onSurface,
                                animationSpec = spring()
                            )

                            icon()?.let {
                                AsyncIcon(
                                    icon = icon,
                                    tint = color
                                )
                            }

                            Text(
                                text = label,
                                style = Theme.typography.label,
                                color = color
                            )

                        }
                    )
                    
                    @Composable
                    fun NavigationList(
                        modifier: Modifier = Modifier,
                        selectedItemIndex: Int,
                        itemsCount: Int,
                        items: @Composable NavigationListScope.() -> Unit
                    ) {

                        Box(
                            modifier = Modifier
                                .then(modifier)
                                .floatingComponentHazeEffect(Theme.shapes.medium)
                        ) {

                            var maxHeight by remember { mutableIntStateOf(0) }
                            var maxWidth by remember { mutableIntStateOf(0) }
                            val targetItem by remember(itemsCount, selectedItemIndex) {
                                derivedStateOf {
                                    (maxHeight / itemsCount) * selectedItemIndex
                                }
                            }

                            val layoutHeight = remember(itemsCount) { 56.dp * itemsCount }
                            val offsetY by animateIntAsState(
                                targetValue = targetItem,
                                animationSpec = spring(
                                    dampingRatio = Spring.DampingRatioLowBouncy,
                                    stiffness = Spring.StiffnessMediumLow
                                )
                            )

                            Box(
                                modifier = Modifier
                                    .graphicsLayer { this.translationY = offsetY.toFloat() }
                                    .height(56.dp)
                                    .width(with(LocalDensity.current) { maxWidth.toDp() })
                                    .padding(4.dp)
                                    .clip(shape = Theme.shapes.small)
                                    .background(color = Theme.colorScheme.accent.copy(alpha = .16f))
                                    .innerShadow(
                                        shape = Theme.shapes.small,
                                        shadow = Shadow(
                                            radius = 16.dp,
                                            brush = Brush.verticalGradient(
                                                colors = listOf(
                                                    Theme.colorScheme.accent.copy(.0f),
                                                    Theme.colorScheme.accent.copy(.1f)
                                                )
                                            ),
                                            offset = DpOffset(x = 0.dp, y = (-2).dp)
                                        )
                                    )
                                    .border(
                                        width = 1.dp,
                                        brush = Brush.verticalGradient(
                                            colors = listOf(
                                                Theme.colorScheme.accent.copy(.1f),
                                                Theme.colorScheme.accent.copy(.0f)
                                            )
                                        ),
                                        shape = Theme.shapes.small
                                    )
                            )

                            Column(
                                modifier = Modifier
                                    .height(layoutHeight)
                                    .onSizeChanged {
                                        maxHeight = it.height
                                        maxWidth = it.width
                                    },
                                horizontalAlignment = Alignment.Start,
                                content = items
                            )

                        }

                    }

                    typealias NavigationListScope = ColumnScope
                    
                    @Composable
                    fun DemoAppSideRail(
                        selectedItem: () -> DemoAppNavigation,
                        onSelected: (DemoAppNavigation) -> Unit,
                    ) {

                        val selectedItemIndex by rememberUpdatedState(
                            when (selectedItem()) {
                                is DemoAppNavigation.Demo -> 0
                                else -> 1
                            }
                        )

                        SideRail(
                            selectedItemIndex = selectedItemIndex,
                            itemsCount = 2,
                            navList = {

                                val selectedListItemIndex by rememberUpdatedState(
                                    when (selectedItem()) {
                                        is DemoAppNavigation.Demo -> 0
                                        is DemoAppNavigation.Setup -> 0
                                        is DemoAppNavigation.Usage -> 1
                                        is DemoAppNavigation.FAQ -> 2
                                        is DemoAppNavigation.About -> 3
                                        is DemoAppNavigation.Osl -> 3
                                    }
                                )

                                val isListVisible by rememberUpdatedState(
                                    selectedItemIndex > 0
                                )

                                AnimatedReveal(
                                    visible = isListVisible
                                ) {

                                    NavigationList(
                                        selectedItemIndex = selectedListItemIndex,
                                        itemsCount = 4
                                    ) {

                                        NavigationListItem(
                                            label = "Setup",
                                            icon = { Res.drawable.settings_outlined },
                                            selected = { selectedItem() == DemoAppNavigation.Setup },
                                            onSelected = remember { { onSelected(DemoAppNavigation.Setup) } }
                                        )

                                        NavigationListItem(
                                            label = "Usage",
                                            icon = { Res.drawable.explore_outlined },
                                            selected = { selectedItem() == DemoAppNavigation.Usage },
                                            onSelected = remember { { onSelected(DemoAppNavigation.Usage) } }
                                        )

                                        NavigationListItem(
                                            label = "FAQ",
                                            icon = { Res.drawable.faq_outlined },
                                            selected = { selectedItem() == DemoAppNavigation.FAQ },
                                            onSelected = remember { { onSelected(DemoAppNavigation.FAQ) } }
                                        )

                                        NavigationListItem(
                                            label = "About",
                                            icon = { Res.drawable.info_outlined },
                                            selected = { selectedListItemIndex == 3 },
                                            onSelected = remember { { onSelected(DemoAppNavigation.About) } }
                                        )

                                    }
                                }

                            }
                        ) {
                            SideRailItem(
                                icon = { Res.drawable.squircle_icon_outlined },
                                label = "Demo",
                                selected = { selectedItem() == DemoAppNavigation.Demo },
                                onSelected = remember { { onSelected(DemoAppNavigation.Demo) } }
                            )

                            SideRailItem(
                                icon = { Res.drawable.documentation },
                                label = "Docs",
                                selected = { selectedItem() != DemoAppNavigation.Demo },
                                onSelected = remember { { onSelected(DemoAppNavigation.Setup) } }
                            )
                        }
                    }
                    
                    @Composable
                    fun AnimatedReveal(
                        visible: Boolean,
                        modifier: Modifier = Modifier,
                        content: @Composable () -> Unit
                    ) {

                        val progress = remember { Animatable(if (visible) 1f else 0f) }
                        var isComposed by remember { mutableStateOf(visible) }

                        LaunchedEffect(visible) {
                            if (visible) {
                                isComposed = true
                                withContext(Dispatchers.Default) {
                                    progress.animateTo(
                                        1f,
                                        spring(
                                            dampingRatio = Spring.DampingRatioLowBouncy,
                                            stiffness = Spring.StiffnessMediumLow
                                        )
                                    )
                                }
                            } else {
                                withContext(Dispatchers.Default) { progress.animateTo(0f) }
                                isComposed = false
                            }
                        }

                        if (isComposed) {

                            Layout(
                                content = content,
                                modifier = Modifier
                                    .graphicsLayer {
                                        alpha = progress.value
                                        scaleX = progress.value
                                        scaleY = progress.value
                                    }
                                    .then(modifier)
                            ) { measurables, constraints ->

                                val placeable = measurables.first().measure(constraints)
                                val animatedHeight = (placeable.height * progress.value).toInt()
                                val animatedWidth = (placeable.width * progress.value).toInt()

                                layout(
                                    width = animatedWidth,
                                    height = animatedHeight
                                ) { placeable.place(0, 0) }

                            }
                        }
                    }
                    
                    @Composable
                    fun HorizontalSpacer(width: Dp) = Spacer(modifier = Modifier.width(width))

                    @Composable
                    fun VerticalSpacer(height: Dp) = Spacer(modifier = Modifier.height(height))
                    
                    val LocalHazeState = compositionLocalOf { HazeState() }

                    fun Modifier.floatingComponentHazeEffect(
                        shape: CornerBasedShape
                    ): Modifier = composed {
                        this
                            .dropShadow(
                                shape = shape,
                                shadow = Shadow(
                                    radius = 24.dp,
                                    color = Theme.colorScheme.shadow,
                                    offset = DpOffset(x = 0.dp, y = 4.dp)
                                )
                            )
                            .clip(shape)
                            .hazeEffect(
                                state = LocalHazeState.current,
                                style = HazeStyle(
                                    backgroundColor = Theme.colorScheme.surface,
                                    tint = HazeTint(
                                        color = Theme.colorScheme.surfaceElevationMedium.copy(.75f)
                                    ),
                                    blurRadius = 12.dp
                                )
                            )
                            .innerShadow(
                                shape = shape,
                                shadow = Shadow(
                                    radius = 16.dp,
                                    brush = Brush.verticalGradient(
                                        colors = listOf(
                                            Theme.colorScheme.surface.copy(.5f),
                                            Theme.colorScheme.surface.copy(0f)
                                        )
                                    ),
                                    offset = DpOffset(x = 0.dp, y = 2.dp)
                                )
                            )
                            .innerShadow(
                                shape = shape,
                                shadow = Shadow(
                                    radius = 16.dp,
                                    brush = Brush.verticalGradient(
                                        colors = listOf(
                                            Theme.colorScheme.surfaceElevationHigh.copy(0f),
                                            Theme.colorScheme.surfaceElevationHigh
                                        )
                                    ),
                                    offset = DpOffset(x = 0.dp, y = (-2).dp)
                                )
                            )
                            .border(
                                width = 1.dp,
                                brush = Brush.verticalGradient(
                                    colors = listOf(
                                        Theme.colorScheme.outline,
                                        Theme.colorScheme.outline.copy(0f)
                                    )
                                ),
                                shape = shape
                            )
                    }
                    
                    
                    @Composable
                    fun SideRail(
                        modifier: Modifier = Modifier,
                        selectedItemIndex: Int,
                        itemsCount: Int,
                        action: @Composable (ColumnScope.() -> Unit)? = null,
                        navList: @Composable (ColumnScope.() -> Unit)? = null,
                        items: @Composable ColumnScope.() -> Unit
                    ) {

                        val windowState = LocalWindowState.current
                        val horizontalPadding = remember(windowState) {
                            when {
                                windowState.isLargeWidth -> 64.dp
                                windowState.isMediumWidth -> 20.dp
                                else -> 0.dp
                            }
                        }

                        Column(
                            modifier = Modifier
                                .verticalScroll(state = rememberScrollState())
                                .padding(start = horizontalPadding)
                                .padding(vertical = 24.dp)
                                .then(modifier),
                            verticalArrangement = Arrangement.Center,
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {

                            Box(
                                modifier = Modifier.floatingComponentHazeEffect(shape = Theme.shapes.medium)
                            ) {

                                val density = LocalDensity.current
                                var maxHeight by remember { mutableIntStateOf(0) }
                                var maxWidth by remember { mutableIntStateOf(0) }
                                val targetItem by remember(itemsCount, selectedItemIndex) {
                                    derivedStateOf {
                                        (maxHeight / itemsCount) * selectedItemIndex
                                    }
                                }

                                val railHeight by remember {
                                    derivedStateOf { 56.dp * itemsCount }
                                }

                                val indicatorSize by remember {
                                    derivedStateOf {
                                        DpSize(
                                            width = with(density) { maxWidth.toDp() },
                                            height = 56.dp
                                        )
                                    }
                                }

                                val offsetY by animateIntAsState(
                                    targetValue = targetItem,
                                    animationSpec = spring(
                                        dampingRatio = Spring.DampingRatioLowBouncy,
                                        stiffness = Spring.StiffnessMediumLow
                                    )
                                )

                                val backgroundColor by rememberUpdatedState(
                                    Theme.colorScheme.primary.copy(alpha = .16f)
                                )

                                val borderBrush by rememberUpdatedState(
                                    Brush.verticalGradient(
                                        colors = listOf(
                                            Theme.colorScheme.primary.copy(.1f),
                                            Theme.colorScheme.accent.copy(.0f)
                                        )
                                    )
                                )

                                Box(
                                    modifier = Modifier
                                        .graphicsLayer { this.translationY = offsetY.toFloat() }
                                        .size(indicatorSize)
                                        .padding(4.dp)
                                        .clip(shape = Theme.shapes.small)
                                        .background(color = backgroundColor)
                                        .innerShadow(
                                            shape = Theme.shapes.small,
                                            shadow = Shadow(
                                                radius = 16.dp,
                                                brush = Brush.verticalGradient(
                                                    colors = listOf(
                                                        Theme.colorScheme.primary.copy(.0f),
                                                        Theme.colorScheme.primary.copy(.1f)
                                                    )
                                                ),
                                                offset = DpOffset(x = 0.dp, y = (-2).dp)
                                            )
                                        )
                                        .border(
                                            width = 1.dp,
                                            brush = borderBrush,
                                            shape = Theme.shapes.small
                                        )
                                )

                                Column(
                                    modifier = Modifier
                                        .onSizeChanged {
                                            maxHeight = it.height
                                            maxWidth = it.width
                                        }
                                        .height(railHeight),
                                    content = items
                                )

                            }

                            navList?.let {
                                VerticalSpacer(height = 16.dp)
                                navList()
                            }

                            action?.let {
                                VerticalSpacer(height = 16.dp)
                                action()
                            }
                        }
                    }
                    """.trimIndent()
            )

        }
    }
}

//https://github.com/surajpsp/CustomList.git (Section List Animation)
//https://github.com/UmairKhanDeveloper/Expense-Tracker-PRO (onBoarding Pager)
//https://github.com/Nubet/studymood-app-public.git (Onboarding good using AnimatedContent)
//https://github.com/mrAyush1804/PoultryMandi.git (Select Date Flip Animation)
//https://github.com/stoyan-vuchev/squircle-shape.git (Good Menu List (Side Menu Left Side, Hide/Show))
//https://github.com/alexiyous/ramadhan-camp-kmp.git (MVI API Calling, Common ViewModel With Parameter Pass)
//https://github.com/Syedwaheed/CineVault.git (LocalNavAnimatedContentScope, LocalAnimatedVisibilityScope, LocalSharedTransitionScope, BottomNavigation Width Scroll Manage)
//https://github.com/PocketShopiTi/pocket-shop.git (Good OnBoarding, SplashScreen Animation, Error Shake Animation, Card Added Animation Text, 2 Option Selection Good)
//https://github.com/Yoke0/Gainful (Calendar, BottomBar Background Spring Animation, Tab Background Spring Animation)
//https://github.com/Shahidzbi4213/WallpaperApp-Cmp.git (Number Pagination Animation)
//https://github.com/MehdiSekoba (OTP Animation)
//https://gist.github.com/ardakazanci/cdb80476dea0c5d3cfaacdb9a1a111b2 (Add to Cart Counter Animation)

//https://proandroiddev.com/the-case-of-the-invisible-highlight-in-jetpack-compose-452a9874ea02
//https://abifarhan.medium.com/the-20-jetpack-compose-interview-questions-that-separate-seniors-from-everyone-else-2026-edition-a53a3d82265b

//Purchase
//https://hotmart.com/es/marketplace/productos/compose-animations-masterclass-55-ejemplos-practicos/D104844614K